import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Scale,
  RefreshCw, Save, X, Sparkles, AlertTriangle, CheckCircle, Info,
  ChevronRight, ChevronLeft, ChevronDown, Edit2, Copy, ArrowDown, ArrowLeft, Lightbulb
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useClient } from '../context/ClientContext';
import { goalOptions, allergiesOptions, limitationsOptions, regions as regionOptions } from '../constants/profileOptions';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import {
  fetchClientMealPlanConfig,
  fetchClientDailyTargets,
  fetchClientMealPlanStructure,
  fetchClientRecommendations,
  fetchClientMealPlans,
  fetchWeightLogs,
  fetchMealPlanTemplates,
  saveMealPlanTemplate,
  saveClientMealPlan,
  updateMealPlan,
  updateClientSettings,
  activateMealPlan,
} from '../services/clientApi';
import { translateMenu } from '../services/translateService';
import API_BASE_URL, { CREATE_MEAL_PLAN_ENDPOINT } from '../config/api';
import AddIngredientModal from '../components/AddIngredientModal';
import IngredientPortionModal from '../components/IngredientPortionModal';
import { friendlyPlanNote } from '../utils/friendlyPlanNote';
import IngredientTable from '../components/mealplan/IngredientTable';
import MealStickerTile from '../components/mealplan/MealStickerTile';
import OverflowMenu from '../components/mealplan/OverflowMenu';
import { pickMealStickers, stickersFromText, matchIngredient, mealIcon } from '../utils/foodStickers';
import { validateMealPlanForSave } from '../utils/mealPlanPayloadValidation';
import {
  reconcile, DAY_TOLERANCE, MEAL_TOLERANCE, parseGrams,
  hasSavedDailyTargets, readSlots, writeSlot, slotMacros, defaultSlotNames, resolveTargets,
} from '../utils/mealPlanTargets';
import { toast } from 'sonner';
import {
  roundMacro,
  isWithinAiMealMacroMargin,
  getAiMealMacroRange,
  sumIngredientNutrition,
  normalizeAiDish,
  normalizeMealNameForSave,
  formatDishForMealPlanDb,
  countAlternativeDishes,
  normalizeMealAlternativeSlots,
  getAltDishFromMeal,
  writeAltDishToMeal,
  removeAltDishFromMeal,
} from '../utils/mealPlanAlternatives';

// A list field from the client record: an array, a JSON array string, or comma/line separated text. "None" means empty.
const toList = (value) => {
  if (value == null || value === '') return [];
  let v = value;
  if (typeof v === 'string' && v.trim().startsWith('[')) { try { v = JSON.parse(v); } catch { /* plain text */ } }
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  return String(v).split(/[,\n،;]/).map((x) => x.trim()).filter((x) => x && !/^(none|no|null|ללא|אין)$/i.test(x));
};

// The client brief as the page edits it, from the raw record fields (see fetchClientDailyTargets).
const briefFrom = (raw) => {
  let pref = raw?.clientPreference;
  if (typeof pref === 'string') { try { pref = JSON.parse(pref); } catch { pref = null; } }
  const p = pref && typeof pref === 'object' && !Array.isArray(pref) ? pref : {};
  return {
    allergies: toList(raw?.foodAllergies),
    limitations: toList(raw?.foodLimitations),
    dietPrefs: toList(p.dietary_preferences),
    dietStyle: String(p.diet_style || ''),
    medical: String(raw?.medicalConditions || ''),
    goal: String(raw?.goal || ''),
    region: String(raw?.region || ''),
    notes: Array.isArray(raw?.recommendations) ? raw.recommendations.join('\n') : String(raw?.recommendations || ''),
  };
};
const EMPTY_BRIEF = briefFrom(null);

// An option chip's value or either of its labels (older records stored labels).
const optionMatches = (opt, item) => [opt.value, opt.labelEn, opt.labelHe].some((x) => String(x).toLowerCase() === String(item).toLowerCase());

// A copy of a prescription { day, meals, slots } that can be edited without touching the original.
const cloneTargets = (v) => (v ? {
  day: { ...v.day },
  meals: v.meals,
  slots: v.slots.map((s) => ({ ...s, targetMacros: s.targetMacros ? { ...s.targetMacros } : null })),
} : null);

// Suggested slots for a client with none saved: the default names for their meal count, the day's calories split
// evenly. Shown and editable; written to the client only when the coach saves or starts a plan.
const suggestSlots = (count, dayCalories) => {
  const names = defaultSlotNames(count);
  const kcal = Math.max(0, Math.round(Number(dayCalories) || 0));
  const each = names.length ? Math.floor(kcal / names.length) : 0;
  return names.map((mealSlot, i) => ({
    mealSlot,
    mealName: '',
    targetCalories: i === names.length - 1 ? kcal - each * (names.length - 1) : each,
    targetMacros: null,
    format: 'legacy',
  }));
};

// The coach's starting working copy: the saved prescription, with suggested slots if none are saved.
const workFrom = (saved) => {
  const meals = Number(saved.meals) || 4;
  const copy = cloneTargets({ ...saved, meals });
  return saved.slots.length ? copy : { ...copy, slots: suggestSlots(meals, saved.day.calories) };
};

const scaleIngredientToGrams = (ing, grams) => {
  const nextGrams = Math.max(0, Number(grams) || 0);
  const oldGrams = Number(ing['portionSI(gram)']) || 0;
  if (oldGrams <= 0 || nextGrams <= 0) return ing;
  const factor = nextGrams / oldGrams;
  const round1 = (n) => Math.round((Number(n) || 0) * factor * 10) / 10;
  return {
    ...ing,
    'portionSI(gram)': Math.round(nextGrams),
    calories: Math.round((Number(ing.calories) || 0) * factor),
    protein: round1(ing.protein),
    carbs: round1(ing.carbs),
    fat: round1(ing.fat),
  };
};

// Hit the meal calorie target without cutting the highest-protein food first.
const rebalanceIngredientList = (ingredients, targetKcal) => {
  const list = Array.isArray(ingredients) ? ingredients : [];
  if (!list.length || !(Number(targetKcal) > 0)) return list;
  const kcal = (ing) => Number(ing.calories) || 0;
  const current = list.reduce((sum, ing) => sum + kcal(ing), 0);
  if (current <= 0) return list;
  const over = current - Number(targetKcal);
  if (Math.abs(over) < 8) return list;
  let proteinIdx = 0;
  list.forEach((ing, i) => {
    const score = kcal(ing) > 0 ? (Number(ing.protein) || 0) / kcal(ing) : 0;
    const bestKcal = kcal(list[proteinIdx]);
    const best = bestKcal > 0 ? (Number(list[proteinIdx].protein) || 0) / bestKcal : 0;
    if (score > best) proteinIdx = i;
  });
  const othersKcal = list.reduce((sum, ing, i) => (i === proteinIdx ? sum : sum + kcal(ing)), 0);
  return list.map((ing, i) => {
    const grams = Number(ing['portionSI(gram)']) || 100;
    if (over > 0 && i !== proteinIdx && othersKcal > 0) {
      const cut = Math.min(over, othersKcal * 0.85);
      return scaleIngredientToGrams(ing, grams * ((othersKcal - cut) / othersKcal));
    }
    if (over > 0 && i === proteinIdx) {
      const leftover = over - Math.min(over, othersKcal * 0.85);
      if (leftover < 8) return ing;
      const factor = Math.max(0.4, (kcal(ing) - leftover) / Math.max(kcal(ing), 1));
      return scaleIngredientToGrams(ing, grams * factor);
    }
    if (over < 0 && i === proteinIdx) {
      return scaleIngredientToGrams(ing, grams + grams * ((-over) / Math.max(kcal(ing), 1)));
    }
    return ing;
  });
};

const computeMainTotalsFromPlan = (planData) => {
  if (!planData?.meals) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  }
  return planData.meals.reduce(
    (acc, meal) => {
      if (meal.main) {
        const nutrition = meal.main.nutrition || {};
        acc.calories += nutrition.calories || meal.main.calories || 0;
        acc.protein += nutrition.protein || meal.main.protein || 0;
        acc.carbs += nutrition.carbs || meal.main.carbs || 0;
        acc.fat += nutrition.fat || meal.main.fat || 0;
      }
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );
};

const buildMealPlanPayload = (planData, mealPlanNote, translations) => {
  const englishTranslations = translations.english || {};
  const hebrewTranslations = translations.hebrew || {};
  const mainTotals = computeMainTotalsFromPlan(planData);

  return {
    note: mealPlanNote || planData.note || '',
    meals: (planData.meals || []).map((meal) => {
      const normalizedMeal = normalizeMealAlternativeSlots(meal);
      const englishMealName = normalizeMealNameForSave(normalizedMeal.meal, englishTranslations, hebrewTranslations);
      const mealObj = { meal: englishMealName };

      if (meal.main) {
        mealObj.main = {
          meal_name: englishMealName,
          meal_title: meal.main.meal_title || meal.main.meal_name || meal.main.title || englishMealName,
          nutrition: meal.main.nutrition || {
            calories: meal.main.calories || 0,
            protein: meal.main.protein || 0,
            carbs: meal.main.carbs || 0,
            fat: meal.main.fat || 0,
          },
          ingredients: meal.main.ingredients || [],
          main_protein_source: meal.main.main_protein_source || '',
        };
      }

      if (normalizedMeal.alternative) {
        mealObj.alternative = {
          meal_name: englishMealName,
          meal_title: normalizedMeal.alternative.meal_title || normalizedMeal.alternative.meal_name || normalizedMeal.alternative.title || englishMealName,
          nutrition: normalizedMeal.alternative.nutrition || {
            calories: normalizedMeal.alternative.calories || 0,
            protein: normalizedMeal.alternative.protein || 0,
            carbs: normalizedMeal.alternative.carbs || 0,
            fat: normalizedMeal.alternative.fat || 0,
          },
          ingredients: normalizedMeal.alternative.ingredients || [],
          main_protein_source: normalizedMeal.alternative.main_protein_source || '',
        };
      }

      mealObj.alternatives = (normalizedMeal.alternatives || []).map((alt) =>
        formatDishForMealPlanDb(alt, englishMealName)
      );

      return mealObj;
    }),
    totals: planData.totals
      ? {
          fat: planData.totals.fat || planData.totals.fats || 0,
          carbs: planData.totals.carbs || 0,
          protein: planData.totals.protein || 0,
          calories: planData.totals.calories || 0,
        }
      : {
          fat: mainTotals.fat || 0,
          carbs: mainTotals.carbs || 0,
          protein: mainTotals.protein || 0,
          calories: mainTotals.calories || 0,
        },
  };
};

function CreateMealPlanPage() {
  const navigate = useNavigate();
  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();
  const { selectedClient } = useClient();

  // --- PREMIUM DESIGN TOKENS ---
  const themeClasses = {
    ...baseThemeClasses,
    inputField: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 ${isDarkMode ? 'bg-canvas/50 border-line text-ink placeholder-ink-muted' : 'bg-canvas border-line text-ink placeholder-ink-muted'}`,
    select: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 appearance-none bg-no-repeat pr-10 ${isDarkMode ? 'bg-canvas/50 border-line text-ink [&_option]:bg-surface' : 'bg-canvas border-line text-ink'}`,
    btnPrimary: `px-5 py-2.5 rounded-lg font-medium bg-forest-ondark text-white dark:text-canvas-dark hover:bg-forest-ondark/90   active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-all duration-200`,
    btnSecondary: `px-5 py-2.5 rounded-lg font-medium border border-line dark:border-line bg-surface dark:bg-surface text-ink dark:text-ink hover:bg-canvas dark:hover:bg-moss/50  focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200`,
    btnMutedPill: `px-4 py-2.5 rounded-lg font-medium text-ink-muted dark:text-ink-muted hover:bg-moss dark:hover:bg-surface/50 hover:text-ink dark:hover:text-ink focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas active:scale-[0.98] flex items-center justify-center gap-2 transition-all duration-200`,
  };

  const cardBase = `${themeClasses.bgCard}  rounded-lg border border-line dark:border-line   transition-shadow duration-300`;
  const notesTextareaClass = `${themeClasses.inputField} !rounded-lg resize-none min-h-[100px] bg-surface dark:bg-canvas/80 !border-line dark:!border-line `;

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';
  const isRTL = direction === 'rtl';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- STATE ---
  // The client's prescription as saved on their record (set at onboarding, maybe changed by a coach since).
  // Nothing on this page rescales it.
  const [dayTargets, setDayTargets] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
  const [targetsSaved, setTargetsSaved] = useState(false);
  // Saved meal slots (meal_plan_structure), exactly as stored. See readSlot.
  const [slots, setSlots] = useState([]);
  // Meals per day the client asked for at onboarding (number_of_meals).
  const [mealCount, setMealCount] = useState(0);
  // The coach's working copy of the prescription, edited in place: { day, meals, slots }. Saved only on Save.
  const [work, setWork] = useState(null);
  // The prescription as onboarding set it: the server's copy once a coach changed it, otherwise what was loaded.
  const [onboardingValues, setOnboardingValues] = useState(null);
  const [isSavingTargets, setIsSavingTargets] = useState(false);
  const [targetsError, setTargetsError] = useState('');
  // Profile fields for "Estimate from profile" (clients who never onboarded).
  const [clientProfile, setClientProfile] = useState(null);
  const [estimatedFromProfile, setEstimatedFromProfile] = useState(false);
  // Builder: whether the structure strip is expanded.
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [addingMeal, setAddingMeal] = useState(false);
  const [newMealPick, setNewMealPick] = useState('');
  // True while the working slots are the suggested ones (none saved): they follow the meal count and calories.
  const [autoSlots, setAutoSlots] = useState(false);
  // Step 1's choice: 'ai' | 'manual' | 'copy'; null until the coach picks one.
  const [startMethod, setStartMethod] = useState(null);
  // Structure editor: grams typed by hand instead of a preset, and grams set per meal instead of following the day.
  const [customMacros, setCustomMacros] = useState(false);
  const [advancedSplit, setAdvancedSplit] = useState(false);
  // The client brief (allergies, limits, diet, goal, region, notes for the AI): always beside the steps, never a gate.
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [briefSaved, setBriefSaved] = useState(EMPTY_BRIEF);
  const [isSavingBrief, setIsSavingBrief] = useState(false);
  const [briefError, setBriefError] = useState('');
  const [weightLogs, setWeightLogs] = useState(null);
  // Copy a plan: the picker, this client's plans (loaded when it opens), and the one picked.
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyPlans, setCopyPlans] = useState(null);
  const [copyError, setCopyError] = useState('');
  const [copyPickId, setCopyPickId] = useState(null);
  const [copyNote, setCopyNote] = useState('');
  const [copyTab, setCopyTab] = useState('client');
  const [templates, setTemplates] = useState(null);
  const [templatesError, setTemplatesError] = useState('');
  const [templatePickId, setTemplatePickId] = useState(null);
  const [templateName, setTemplateName] = useState(null); // null = form closed
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const [isSavingMealPlan, setIsSavingMealPlan] = useState(false);
  const [savedMealPlanId, setSavedMealPlanId] = useState(null);
  const [savedMealPlanName, setSavedMealPlanName] = useState(null);
  const savedMealPlanIdRef = useRef(null);
  const [isApplyingAiDraft] = useState({});
  const [mealPlanNote, setMealPlanNote] = useState('');
  const [generatedMealPlan, setGeneratedMealPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiDiscoverPreviews, setAiDiscoverPreviews] = useState({});
  const [aiDiscoverHistory, setAiDiscoverHistory] = useState({});
  const [isDiscoverLoading, setIsDiscoverLoading] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The AI's own note is offered, never written over the coach's notes.
  const [aiNote, setAiNote] = useState('');
  // Snapshot of the plan + note at last save, to tell "Saved" from "Unsaved changes".
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  // Set when a drafted plan has fully arrived; cleared once it's on screen and announced.
  const [revealPlan, setRevealPlan] = useState(null);
  // One meal at a time: which meal is open, and which tab.
  const [activeMealIndex, setActiveMealIndex] = useState(0);
  // The alternative opened for editing in the meal panel ("<dishType>-<altIndex>"), one at a time.
  const [openAlt, setOpenAlt] = useState(null);
  // Structure rows whose "idea for AI" field was opened (it stays open while it has text).
  const [ideaRows, setIdeaRows] = useState({});
  const [publishOpen, setPublishOpen] = useState(false);
  const [planNameDraft, setPlanNameDraft] = useState('');
  const [sendFrom, setSendFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [sendUntil, setSendUntil] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() + 28);
    return end.toISOString().slice(0, 10);
  });
  const [sendDays, setSendDays] = useState([0, 1, 2, 3, 4]);
  const [isSending, setIsSending] = useState(false);
  const [draftPhase, setDraftPhase] = useState(null);
  const [redraftConfirm, setRedraftConfirm] = useState(false);
  const [sentToClient, setSentToClient] = useState(false);
  const streamTimers = useRef([]);

  // Ingredient modal states
  const [isAddIngredientModalVisible, setIsAddIngredientModalVisible] = useState(false);
  const [selectedMealIndex, setSelectedMealIndex] = useState(null);
  const [isEditPortionModalVisible, setIsEditPortionModalVisible] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);
  const [isReplacingIngredient, setIsReplacingIngredient] = useState(false);
  const [replacingIngredientIndex, setReplacingIngredientIndex] = useState(null);

  // Alternative meal editing state
  const [selectedAlternativeMealIndex, setSelectedAlternativeMealIndex] = useState(null);
  const [selectedAlternativeType, setSelectedAlternativeType] = useState(null);
  const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState(null);
  const [isAddAlternativeIngredientModalVisible, setIsAddAlternativeIngredientModalVisible] = useState(false);
  const [isEditAlternativePortionModalVisible, setIsEditAlternativePortionModalVisible] = useState(false);

  // AI draft editing (pre-apply workspace)
  const [selectedAiDraftMealIndex, setSelectedAiDraftMealIndex] = useState(null);
  const [isAddAiDraftIngredientModalVisible, setIsAddAiDraftIngredientModalVisible] = useState(false);
  const [isEditAiDraftPortionModalVisible, setIsEditAiDraftPortionModalVisible] = useState(false);

  const [editingMealTitle, setEditingMealTitle] = useState(null);
  const generatedPlanRef = useRef(null);
  const generatedMealPlanRef = useRef(null);

  // --- TRANSLATIONS ---
  const mealTranslations = {
    english: {
      'meal.preMorning': 'Pre-Morning Snack',
      'meal.breakfast': 'Breakfast',
      'meal.morningSnack': 'Morning Snack',
      'meal.lunch': 'Lunch',
      'meal.afternoonSnack': 'Afternoon Snack',
      'meal.preWorkout': 'Pre-Workout Meal',
      'meal.postWorkout': 'Post-Workout Meal',
      'meal.dinner': 'Dinner',
      'meal.eveningSnack': 'Evening Snack',
      'meal.nightSnack': 'Night Snack',
      'page.newPlan': 'New meal plan',
      'page.stepMeals': 'Meals',
      'page.selectClientFirst': 'Please select a client first',
      'page.editTargets': 'Edit targets',
      'page.cancel': 'Cancel',
      'page.saving': 'Saving...',
      'page.calories': 'Calories',
      'page.protein': 'Protein',
      'page.carbs': 'Carbohydrates',
      'page.fats': 'Fat',
      'page.generateMealPlan': 'Generate with AI',
      'page.notesForMealPlanPlaceholder': 'Add notes that will be saved with the meal plan...',
      'page.forClient': 'For',
      'page.statusDraft': 'Draft — not saved',
      'page.statusUnsaved': 'Unsaved changes',
      'page.statusSaved': 'Saved',
      'page.loadErrorTitle': 'Couldn\'t load this client\'s targets',
      'page.retry': 'Retry',
      'page.targetsSaved': 'Targets saved',
      'page.generateFailed': 'Couldn\'t draft the plan',
      'page.replaceUnsavedConfirm': 'Replace the current unsaved plan with a new draft?',
      'page.onTarget': 'On target',
      'page.overBy': 'Over by {n}',
      'page.underBy': 'Under by {n}',
      'page.mealsWithin': '{n} of {m} meals within ±10% of target',
      'page.aiNoteTitle': 'AI note',
      'page.addToNotes': 'Add to notes',
      'page.dismiss': 'Dismiss',
      'page.notesSeedHint': 'Starts from the client\'s recommendations. Saved with the plan.',
      'page.savingMealPlan': 'Saving...',
      'page.mealPlanName': 'Meal Plan Name',
      'page.mealPlanSaved': 'Meal plan saved successfully!',
      'page.mealPlanSaveError': 'Failed to save meal plan',
      'page.ingredients': 'Ingredients',
      'page.items': 'Items',
      'page.target': 'Target',
      'page.deleteAlternative': 'Delete alternative',
      'page.cannotDeleteLastAlternative': 'At least one alternative meal must remain. You cannot delete the last one.',
      'page.generateSuggestion': 'Generate',
      'page.generateAnother': 'Generate Another',
      'page.generatingSuggestion': 'Generating…',
      'page.aiSuggestion': 'AI Suggestion',
      'page.aiDraftBadge': 'AI',
      'page.mainProtein': 'Main protein',
      'page.addToPlan': 'Add as alternative',
      'page.discardDraft': 'Discard draft',
      'page.macroDiffFromTarget': 'vs meal target',
      'page.macroDiffOk': 'Within target range',
      'page.macroDiffWarn': 'Outside target range',
      'page.draftNeedsTitle': 'Please set a dish title before adding to the plan.',
      'page.draftNeedsIngredients': 'Add at least one ingredient before adding to the plan.',
      'page.addToPlanSaving': 'Saving to plan…',
      'page.addToPlanSaveError': 'Failed to save alternative to plan',
      'page.planReadyToast': 'Meal plan drafted',
      'page.planReadySummary': '{meals} meals · {kcal} of {target} kcal · {within} within meal targets. Review below, then save.',
      'page.draftNotSentHint': 'The client sees nothing until you save.',
      'page.cannotSaveYet': 'Cannot save yet',
      'page.issueNoIngredients': 'add at least one ingredient',
      'page.issueIngredientName': 'an ingredient has no name',
      'page.issueUnknownSlot': 'unknown meal name',
      'page.issueNoMain': 'main dish is missing',
      'page.redraft': 'Redraft with AI',
      'page.replaceDraft': 'Replace',
      'page.offlineDraft': 'You\'re offline. Reconnect, then draft again.',
      'page.serviceUnreachable': 'Couldn\'t reach the meal-plan service. Try again in a moment.',
      'page.translateFailed': 'Couldn\'t translate this draft. Names are still in English — edit them before you send.',
      'page.undo': 'Undo',
      'page.ingredientRemoved': 'Ingredient removed',
      'page.alternativeRemoved': 'Alternative removed',
      'page.alreadyBalanced': 'Already within 8 kcal of this meal\'s target.',
      'page.defaultPlanName': 'Meal Plan - {date}',
      'page.endBeforeStart': 'End date is before the start date.',
      'page.dayRail': 'Meals of the day',
      'page.emptyMeal': 'Empty',
      'page.tabMain': 'Main dish',
      'page.tabAlternatives': 'Alternatives',
      'page.tabAi': 'AI suggestion',
      'page.addIngredient': 'Add ingredient',
      'page.noAlternatives': 'No alternatives yet. Ask AI for one, or add one later.',
      'page.untitledDish': 'Untitled dish',
      'page.prevMeal': 'Previous meal',
      'page.nextMeal': 'Next meal',
      'page.planNotes': 'Plan notes',
      'page.aiSuggestHelper': 'AI drafts an option that fits this meal\'s target ({kcal} kcal). You edit it before adding it.',
      'page.draftingDay': 'Drafting the day. Meals appear as they are ready.',
      'page.writingMeal': 'Writing {meal}',
      'page.writingNow': 'Writing',
      'page.waiting': 'Waiting',
      'page.writingHint': 'Stay on this page. Each meal fills in when it is ready. This usually takes under a minute.',
      'page.rebalance': 'Rebalance',
      'page.reviewSend': 'Review and send',
      'page.sendTo': 'Send to {name}',
      'page.sentTo': 'Sent to {name}',
      'page.sendFailed': 'Couldn\'t send the plan',
      'page.saveDraft': 'Save as draft',
      'page.sendHint': 'The client sees the plan on the days you pick. A draft stays off their app.',
      'page.nothingToSend': 'Draft the day, or add ingredients, then send.',
      'page.startDate': 'Start date',
      'page.endDate': 'End date',
      'page.activeDays': 'Days',
      'page.needDates': 'Pick a start date, an end date, and at least one day.',
      'page.closeReview': 'Close',
      'page.slotsDiffer': 'The slots add up to {sum} kcal. The day target is {day} kcal.',
      'page.noStructureFailed': 'The meal-plan service needs saved meal slots for this client. Add them in Edit targets, then draft again.',
      'page.targetsMissingIntro': '{name} hasn\'t finished onboarding, so there are no daily targets yet. Set them here; they\'re saved to the client and used for this plan.',
      'page.saveTargets': 'Save goals',
      'page.targetsFormIncomplete': 'Enter calories, protein, carbs and fat.',
      'page.slot': 'Meal',
      'page.dishHint': 'Idea for AI (optional)',
      'page.addSlot': 'Add slot',
      'page.removeSlot': 'Remove slot',
      'page.rebalanceNoChange': 'Rebalance changed nothing. Adjust the grams by hand.',
      'page.fromOnboarding': 'From onboarding',
      'page.changedByCoach': 'Changed by coach',
      'page.noTargets': 'No targets',
      'page.mealsPerDay': 'Meals per day',
      'page.onboardingWas': 'Onboarding: {n}',
      'page.countMismatch': 'Onboarding asked for {n} meals; {m} slots are listed.',
      'page.noSlotsSaved': 'No meal slots saved yet. Building it yourself starts with {n} empty meals; drafting with AI needs slots, so add them here.',
      'page.estimateNote': '≈ Onboarding set calories per meal only, so protein, carbs and fat per meal are estimated from each meal\'s share of the day.',
      'page.estimated': 'estimated',
      'page.unsavedChanges': 'Unsaved changes.',
      'page.undoChanges': 'Cancel',
      'page.resetOnboarding': 'Reset to onboarding',
      'page.targetsSaveFailedKept': 'Couldn\'t save targets. Your changes are still here; try again.',
      'page.mealsRange': 'Meals per day must be 1 to 8.',
      'page.buildManually': 'Add meals myself',
      'page.draftNeedsSlots': 'Drafting with AI needs meal slots. Add slots, or build it yourself.',
      'page.setTargetsToStart': 'Set calories, protein, carbs and fat to start.',
      'page.estimateFromProfile': 'Estimate from profile',
      'page.estimateNeedsProfile': 'Needs weight, height and age in the profile.',
      'page.estimatedNote': 'Estimated from the profile. Check before saving.',
      'page.editSlot': 'Edit name and hint',
      'page.doneEditing': 'Done',
      'page.actions': 'Actions',
      'page.targetsLabel': 'Targets',
      'page.nMeals': '{n} meals',
      'page.hideTargets': 'Hide targets',
      'page.daySoFar': 'Day so far',
      'page.addMeal': 'Add meal',
      'page.chooseMeal': 'Choose a meal',
      'page.removeMealFromPlan': 'Remove {meal} from this plan',
      'page.mealRemovedFromPlan': '{meal} removed',
      'page.addedForPlan': 'Added for this plan, no target',
      'page.editTarget': 'Edit target',
      'page.noFoodYet': 'No food in {meal} yet.',
      'page.addAFood': 'Add a food',
      'page.orAskAi': 'or ask AI for a suggestion',
      'page.setByCoach': 'Set by coach',
      'page.suggestedSlots': 'No meal slots were saved, so these {n} are suggested: the day split evenly. Change them as you like; they\'re saved when you save or start the plan.',
      'page.goals': 'Goals',
      'page.planEmptyTitle': 'No meals yet',
      'page.planEmptyBody': 'Add the meals yourself and pick the foods, or let AI build the day from these goals. You can change everything afterwards.',
      'page.editGoals': 'Edit goals',
      'page.adjustSplit': 'Adjust meal split',
      'page.saveUpdatesClient': 'Saving also updates {name}\'s goals in their app.',
      'page.close': 'Close',
      'page.dayMacrosOff': 'Protein, carbs and fat add up to {sum} kcal; the day\'s calories are {kcal}.',
      'page.slotMacrosOff': '{meal}: protein, carbs and fat add up to {sum} kcal; its target is {kcal} kcal.',
      'page.copyPlan': 'Copy a plan',
      'page.copyTitle': 'Start from a saved plan',
      'page.copyHint': 'The copy is a new draft; the original doesn\'t change.',
      'page.copyNone': 'This client has no earlier plans to copy yet.',
      'page.copyLoadFailed': 'Couldn\'t load this client\'s plans.',
      'page.copyOf': '{name} (copy)',
      'page.copyDiffers': 'The copied plan has {meals} meals and {kcal} kcal; the goals are {goalMeals} meals and {goal} kcal. Adjust the meals to fit.',
      'page.useThisPlan': 'Use this plan',
      'page.preview': 'Preview',
      'page.statusActive': 'Active',
      'page.tabClientPlans': 'This client\'s plans',
      'page.tabTemplates': 'Templates',
      'page.templatesNone': 'No templates yet. Save a plan as a template from Review and send, and your whole team can start from it.',
      'page.templatesLoadFailed': 'Couldn\'t load templates.',
      'page.byYou': 'By you',
      'page.byTeam': 'By your team',
      'page.saveAsTemplate': 'Save as template',
      'page.templateName': 'Template name',
      'page.saveTemplate': 'Save template',
      'page.templateHint': 'Everyone in your company can start from it. Plan notes aren\'t included; make sure the name doesn\'t identify the client.',
      'page.templateSaved': 'Saved "{name}" as a template',
      'page.templateSaveFailed': 'Couldn\'t save the template',
      'page.foodMoved': '{food} moved to {meal}',
      'page.duplicateMeal': 'Duplicate meal',
      'page.mealDuplicated': '{meal} duplicated. The copy is right after it.',
      'page.saveGoalsFirst': 'Save or cancel your goal changes first.',
      'page.startSavesGoals': 'Starting saves these goals to {name}\'s profile, so their app shows them too.',
      'page.kcal': 'kcal',
      'page.useAsMain': 'Use as main dish',
      'page.makeMain': 'Make main dish',
      'page.mainReplaced': '"{dish}" is now the main dish. The previous main dish is kept as an alternative.',
      'page.mainSet': '"{dish}" is now the main dish.',
      'page.clientBrief': 'Client brief',
      'page.briefNeedsReview': 'Needs review',
      'page.briefConfirmed': 'Confirmed',
      'page.briefIntro': 'This is what {name} told us. Read it before building the plan, and add your own notes for the AI below.',
      'page.allergies': 'Allergies',
      'page.limitsAndDiet': 'Limits and diet',
      'page.medicalConditions': 'Medical conditions',
      'page.goalAndRegion': 'Goal and region',
      'page.notesForAi': 'Notes for the AI',
      'page.notesForAiHint': 'e.g. Works night shifts, no cooking at lunch, prefers quick breakfasts',
      'page.noneRecorded': 'None recorded',
      'page.currentPlan': 'Current plan',
      'page.noActivePlan': 'No active plan',
      'page.weightTrend': 'Weight',
      'page.noWeighIns': 'No weigh-ins yet',
      'page.since': 'since {date}',
      'page.loadingShort': 'Loading…',
      'page.confirmBrief': 'I\'ve read it, continue',
      'page.briefSaved': 'Notes for the AI saved',
      'page.briefSaveFailed': 'Couldn\'t save your notes. They\'re still here; try again.',
      'page.confirmBriefFirst': 'Confirm the client brief first.',
      'page.editNotes': 'Edit notes',
      'page.notesSaveNote': 'Your notes are saved to {name}\'s profile; the AI follows them.',
      'page.briefReadNote': 'Confirm once you\'ve read it.',
      'page.caloriesPerDay': 'Calories per day',
      'page.caloriesFollowMacros': 'Calories follow the macros (protein and carbs 4 kcal/g, fat 9). Moving calories scales all three and keeps their balance.',
      'page.mealsAddUp': 'Meals add up to {sum} of {kcal} kcal',
      'page.kcalLeft': '{n} kcal left',
      'page.kcalOver': '{n} kcal over',
      'page.ofDay': '{pct}% of the day',
      'page.estimatedShort': '≈ estimated from the day',
      'page.stepsNav': 'Steps',
      'page.stepStart': 'Starting point',
      'page.stepStructure': 'Structure',
      'page.stepSend': 'Review & send',
      'page.startTitle': 'How do you want to build {name}\'s plan?',
      'page.startBody': 'Pick a starting point. Whichever you choose, you can change every meal, and {name} sees nothing until you send it.',
      'page.startAiTitle': 'Build with AI',
      'page.startAiBody': 'You set the structure (calories, macro balance, meals). AI fills every meal to its target and keeps to the client\'s allergies and your notes.',
      'page.startManualTitle': 'Build it myself',
      'page.startManualBody': 'Opens empty meals right away, each with its target from {name}\'s goals. You add foods from the database and adjust the grams.',
      'page.nextAddFoods': 'Next: add foods',
      'page.startCopyTitle': 'Start from a saved plan',
      'page.startCopyBody': 'Reuse one of {name}\'s earlier plans or a team template. It opens as a new draft; the original stays as it is.',
      'page.nextStructure': 'Next: set the structure',
      'page.nextPickPlan': 'Next: pick a plan',
      'page.structureTitle': 'Set the structure the plan is built on',
      'page.structureBodyAi': 'This is the blueprint the AI follows: calories per day, the protein / carbs / fat balance, and how much of the day each meal gets.',
      'page.back': 'Back',
      'page.change': 'Change',
      'page.methodAi': 'Building with AI',
      'page.dailyCaloriesHint': 'Changing it keeps the meal split and the macro balance.',
      'page.macroStyle': 'Macro balance',
      'page.presetBalanced': 'Balanced',
      'page.presetHighProtein': 'High protein',
      'page.presetLowCarb': 'Low carb',
      'page.presetCustom': 'Custom',
      'page.customMacrosHint': 'Enter grams. Calories follow them: protein and carbs 4 kcal/g, fat 9 kcal/g.',
      'page.mealSplit': 'Meals and their share of the day',
      'page.mealSplitHint': 'Give each meal a percentage of the daily calories. Its protein, carbs and fat follow the same balance as the day.',
      'page.mealShare': 'share of the day',
      'page.splitOk': 'Meals add up to the full day',
      'page.splitUnder': '{n} kcal not given to any meal',
      'page.splitOver': 'Meals are {n} kcal over the day',
      'page.fitToDay': 'Fit to the day',
      'page.advancedGrams': 'Set grams per meal',
      'page.advancedGramsHint': 'Set protein, carbs and fat for each meal yourself. The meal\'s calories follow them.',
      'page.dishHintShort': 'Idea for AI (optional), e.g. omelette',
      'page.aiWillBuild': 'What the AI will build',
      'page.aiWillBuildLine': '{n} meals · {kcal} kcal a day',
      'page.aiAvoids': 'Keeps out',
      'page.aiFollowsNotes': 'Follows your notes',
      'page.aiReviewAfter': 'Usually under a minute. Nothing reaches {name} until you review and send.',
      'page.generatePlan': 'Generate the plan',
      'page.createMeals': 'Create {n} empty meals',
      'page.mealsTitle': 'Build and check each meal',
      'page.mealsBody': 'Open a meal from the list. Each one shows its target from the structure; adjust foods and grams until it\'s on target.',
      'page.mealsBodyGenerating': 'AI is building {n} meals on the structure you set. Each one fills in when it\'s ready.',
      'page.nextReview': 'Next: review & send',
      'page.backToMeals': 'Back to meals',
      'page.sendTitle': 'Review and send to {name}',
      'page.structureLabel': 'Structure',
      'page.editStructure': 'Edit structure',
      'page.hideStructure': 'Hide structure',
      'page.dayStructure': 'Day structure',
      'page.aboutClient': 'About {name}',
      'page.mustAvoid': 'Must avoid',
      'page.saveNotes': 'Save notes',
      'page.notesAiHelp': 'The AI reads these when it builds the plan. Saved to {name}\'s profile.',
      'page.avoid': 'Avoid',
      'page.dayOverview': 'Day',
      'page.stepOf': 'Step {n} of {total}',
      'page.targetsButton': 'Targets',
      'page.targetsDrawerHint': 'Daily calories, macro balance and each meal\'s share. Saving updates {name}\'s goals.',
      'page.moreActions': 'More actions',
      'page.fixGrams': 'Fix grams',
      'page.renameDish': 'Rename dish',
      'page.removeMeal': 'Remove meal',
      'page.suggestWithAi': 'Suggest with AI',
      'page.alternativesHint': 'Other dishes the client can swap in for this meal.',
      'page.mealCol': 'Meal',
      'page.shareCol': 'Share',
      'page.useShares': 'Use % per meal',
      'page.addIdea': 'Add an idea for AI',
    },
    hebrew: {
      'meal.preMorning': 'נשנוש בוקר מוקדם',
      'meal.breakfast': 'ארוחת בוקר',
      'meal.morningSnack': 'ביניים בוקר',
      'meal.lunch': 'ארוחת צהריים',
      'meal.afternoonSnack': 'ביניים אחר הצהריים',
      'meal.preWorkout': 'לפני אימון',
      'meal.postWorkout': 'אחרי אימון',
      'meal.dinner': 'ארוחת ערב',
      'meal.eveningSnack': 'ביניים ערב',
      'meal.nightSnack': 'ארוחת לילה',
      'page.newPlan': 'תוכנית חדשה',
      'page.stepMeals': 'ארוחות',
      'page.selectClientFirst': 'אנא בחר לקוח תחילה',
      'page.editTargets': 'ערוך יעדים',
      'page.cancel': 'ביטול',
      'page.saving': 'שומר...',
      'page.calories': 'קלוריות',
      'page.protein': 'חלבון',
      'page.carbs': 'פחמימות',
      'page.fats': 'שומן',
      'page.generateMealPlan': 'יצירה עם AI',
      'page.notesForMealPlanPlaceholder': 'הוסף הערות שיישמרו עם תוכנית הארוחות...',

      'page.savingMealPlan': 'שומר...',
      'page.mealPlanName': 'שם תוכנית ארוחות',
      'page.mealPlanSaved': 'תוכנית הארוחות נשמרה בהצלחה!',
      'page.mealPlanSaveError': 'שגיאה בשמירת תוכנית הארוחות',
      'page.ingredients': 'מרכיבים',
      'page.items': 'פריטים',
      'page.target': 'יעד',
      'page.deleteAlternative': 'מחק חלופה',
      'page.cannotDeleteLastAlternative': 'חייבת להישאר לפחות מנת חלופה אחת. לא ניתן למחוק את האחרונה.',
      'page.generateSuggestion': 'צור הצעה',
      'page.generateAnother': 'צור הצעה נוספת',
      'page.generatingSuggestion': 'יוצר…',
      'page.aiSuggestion': 'הצעת AI',
      'page.aiDraftBadge': 'AI',
      'page.mainProtein': 'חלבון עיקרי',
      'page.addToPlan': 'הוספה כחלופה',
      'page.discardDraft': 'בטל טיוטה',
      'page.macroDiffFromTarget': 'מול יעד הארוחה',
      'page.macroDiffOk': 'בטווח היעד',
      'page.macroDiffWarn': 'מחוץ לטווח היעד',
      'page.draftNeedsTitle': 'הגדר שם מנה לפני הוספה לתוכנית.',
      'page.draftNeedsIngredients': 'הוסף לפחות מרכיב אחד לפני הוספה לתוכנית.',
      'page.addToPlanSaving': 'שומר לתוכנית…',
      'page.addToPlanSaveError': 'שמירת החלופה לתוכנית נכשלה',
      'page.forClient': 'עבור',
      'page.statusDraft': 'טיוטה — לא נשמרה',
      'page.statusUnsaved': 'שינויים שלא נשמרו',
      'page.statusSaved': 'נשמר',
      'page.loadErrorTitle': 'לא הצלחנו לטעון את יעדי הלקוח',
      'page.retry': 'נסו שוב',
      'page.targetsSaved': 'היעדים נשמרו',
      'page.generateFailed': 'יצירת הטיוטה נכשלה',
      'page.replaceUnsavedConfirm': 'להחליף את התוכנית שלא נשמרה בטיוטה חדשה?',
      'page.onTarget': 'ביעד',
      'page.overBy': 'מעל ב-{n}',
      'page.underBy': 'מתחת ב-{n}',
      'page.mealsWithin': '{n} מתוך {m} ארוחות בטווח ±10% מהיעד',
      'page.aiNoteTitle': 'הערת AI',
      'page.addToNotes': 'הוסף להערות',
      'page.dismiss': 'סגור',
      'page.notesSeedHint': 'מתחיל מההמלצות של הלקוח. נשמר עם התוכנית.',
      'page.planReadyToast': 'טיוטת התפריט מוכנה',
      'page.planReadySummary': '{meals} ארוחות · {kcal} מתוך {target} קק"ל · {within} בטווח יעד הארוחה. בדקו למטה ושמרו.',
      'page.draftNotSentHint': 'הלקוח לא רואה כלום עד שתשמרו.',
      'page.cannotSaveYet': 'עדיין אי אפשר לשמור',
      'page.issueNoIngredients': 'הוסיפו לפחות מרכיב אחד',
      'page.issueIngredientName': 'למרכיב חסר שם',
      'page.issueUnknownSlot': 'שם ארוחה לא מוכר',
      'page.issueNoMain': 'חסרה מנה עיקרית',
      'page.redraft': 'טיוטה חדשה עם AI',
      'page.replaceDraft': 'החלפה',
      'page.offlineDraft': 'אין חיבור. התחברו מחדש, ואז נסחו שוב.',
      'page.serviceUnreachable': 'לא הצלחנו להגיע לשירות תוכנית התזונה. נסו שוב בעוד רגע.',
      'page.translateFailed': 'לא ניתן לתרגם את הטיוטה. השמות נשארו באנגלית — ערכו אותם לפני השליחה.',
      'page.undo': 'בטל',
      'page.ingredientRemoved': 'המרכיב הוסר',
      'page.alternativeRemoved': 'החלופה הוסרה',
      'page.alreadyBalanced': 'הארוחה כבר בטווח של 8 קק״ל מהיעד.',
      'page.defaultPlanName': 'תוכנית ארוחות - {date}',
      'page.endBeforeStart': 'תאריך הסיום לפני תאריך ההתחלה.',
      'page.dayRail': 'ארוחות היום',
      'page.emptyMeal': 'ריק',
      'page.tabMain': 'מנה עיקרית',
      'page.tabAlternatives': 'חלופות',
      'page.tabAi': 'הצעת AI',
      'page.addIngredient': 'הוסף מרכיב',
      'page.noAlternatives': 'אין חלופות עדיין. בקשו הצעה מה-AI, או הוסיפו מאוחר יותר.',
      'page.untitledDish': 'מנה ללא שם',
      'page.prevMeal': 'הארוחה הקודמת',
      'page.nextMeal': 'הארוחה הבאה',
      'page.planNotes': 'הערות לתוכנית',
      'page.aiSuggestHelper': 'ה-AI מציע מנה שמתאימה ליעד הארוחה ({kcal} קק"ל). אתם עורכים אותה לפני ההוספה.',
      'page.draftingDay': 'היום נכתב. הארוחות מופיעות כשהן מוכנות.',
      'page.writingMeal': 'נכתב: {meal}',
      'page.writingNow': 'נכתב',
      'page.waiting': 'ממתין',
      'page.writingHint': 'הישארו בעמוד. כל ארוחה מופיעה כשהיא מוכנה. זה בדרך כלל לוקח פחות מדקה.',
      'page.rebalance': 'איזון מנות',
      'page.reviewSend': 'בדיקה ושליחה',
      'page.sendTo': 'שליחה אל {name}',
      'page.sentTo': 'נשלח אל {name}',
      'page.sendFailed': 'שליחת התוכנית נכשלה',
      'page.saveDraft': 'שמירה כטיוטה',
      'page.sendHint': 'הלקוח רואה את התוכנית בימים שבחרתם. טיוטה לא מופיעה אצלו.',
      'page.nothingToSend': 'נסחו את היום, או הוסיפו מרכיבים, ואז שלחו.',
      'page.startDate': 'תאריך התחלה',
      'page.endDate': 'תאריך סיום',
      'page.activeDays': 'ימים',
      'page.needDates': 'בחרו תאריך התחלה, תאריך סיום, ולפחות יום אחד.',
      'page.closeReview': 'סגירה',
      'page.slotsDiffer': 'הארוחות מסתכמות ב-{sum} קק״ל. יעד היום הוא {day} קק״ל.',
      'page.noStructureFailed': 'שירות תוכנית התזונה צריך ארוחות שמורות ללקוח. הוסיפו אותן בעריכת היעדים ונסחו שוב.',
      'page.targetsMissingIntro': 'ל{name} עדיין אין יעדים יומיים, כי האונבורדינג לא הושלם. הגדירו אותם כאן; הם יישמרו ללקוח וישמשו לתוכנית הזו.',
      'page.saveTargets': 'שמירת יעדים',
      'page.targetsFormIncomplete': 'הזינו קלוריות, חלבון, פחמימות ושומן.',
      'page.slot': 'ארוחה',
      'page.dishHint': 'רעיון ל-AI (לא חובה)',
      'page.addSlot': 'הוספת ארוחה ליעדים',
      'page.removeSlot': 'הסרת ארוחה',
      'page.rebalanceNoChange': 'האיזון לא שינה דבר. שנו את הגרמים ידנית.',
      'page.fromOnboarding': 'מהאונבורדינג',
      'page.changedByCoach': 'שונה על ידי המאמן',
      'page.noTargets': 'אין יעדים',
      'page.mealsPerDay': 'ארוחות ביום',
      'page.onboardingWas': 'באונבורדינג: {n}',
      'page.countMismatch': 'באונבורדינג נבחרו {n} ארוחות; ברשימה יש {m}.',
      'page.noSlotsSaved': 'עדיין לא נשמרו ארוחות. בנייה ידנית מתחילה עם {n} ארוחות ריקות; טיוטה עם AI צריכה ארוחות, אז הוסיפו אותן כאן.',
      'page.estimateNote': '≈ באונבורדינג נקבעו רק קלוריות לכל ארוחה, לכן החלבון, הפחמימות והשומן לכל ארוחה מוערכים לפי החלק של הארוחה ביום.',
      'page.estimated': 'הערכה',
      'page.unsavedChanges': 'יש שינויים שלא נשמרו.',
      'page.undoChanges': 'ביטול',
      'page.resetOnboarding': 'חזרה ליעדי האונבורדינג',
      'page.targetsSaveFailedKept': 'שמירת היעדים נכשלה. השינויים שלכם עדיין כאן; נסו שוב.',
      'page.mealsRange': 'מספר הארוחות ביום חייב להיות בין 1 ל-8.',
      'page.buildManually': 'הוספת ארוחות בעצמי',
      'page.draftNeedsSlots': 'טיוטה עם AI צריכה ארוחות. הוסיפו ארוחות, או בנו את התוכנית בעצמכם.',
      'page.setTargetsToStart': 'הגדירו קלוריות, חלבון, פחמימות ושומן כדי להתחיל.',
      'page.estimateFromProfile': 'הערכה לפי הפרופיל',
      'page.estimateNeedsProfile': 'צריך משקל, גובה וגיל בפרופיל.',
      'page.estimatedNote': 'הערכה לפי הפרופיל. בדקו לפני השמירה.',
      'page.editSlot': 'עריכת שם ורעיון',
      'page.doneEditing': 'סיום',
      'page.actions': 'פעולות',
      'page.targetsLabel': 'יעדים',
      'page.nMeals': '{n} ארוחות',
      'page.hideTargets': 'הסתרת היעדים',
      'page.daySoFar': 'היום עד כה',
      'page.addMeal': 'הוספת ארוחה',
      'page.chooseMeal': 'בחירת ארוחה',
      'page.removeMealFromPlan': 'הסרת {meal} מהתוכנית',
      'page.mealRemovedFromPlan': '{meal} הוסרה',
      'page.addedForPlan': 'נוספה לתוכנית הזו, ללא יעד',
      'page.editTarget': 'עריכת היעד',
      'page.noFoodYet': 'אין עדיין אוכל ב{meal}.',
      'page.addAFood': 'הוספת מזון',
      'page.orAskAi': 'או לבקש הצעה מה-AI',
      'page.setByCoach': 'נקבע על ידי המאמן',
      'page.suggestedSlots': 'לא נשמרו ארוחות, לכן אלה {n} ארוחות מוצעות: היום מחולק שווה בשווה. שנו אותן כרצונכם; הן נשמרות כשתשמרו או כשתתחילו את התוכנית.',
      'page.goals': 'יעדים',
      'page.planEmptyTitle': 'אין עדיין ארוחות',
      'page.planEmptyBody': 'הוסיפו את הארוחות בעצמכם ובחרו מזונות, או תנו ל-AI לבנות את היום לפי היעדים האלה. אפשר לשנות הכול אחר כך.',
      'page.editGoals': 'עריכת יעדים',
      'page.adjustSplit': 'התאמת החלוקה לארוחות',
      'page.saveUpdatesClient': 'השמירה מעדכנת גם את היעדים של {name} באפליקציה.',
      'page.close': 'סגירה',
      'page.dayMacrosOff': 'החלבון, הפחמימות והשומן מסתכמים ב-{sum} קק״ל; הקלוריות היומיות הן {kcal}.',
      'page.slotMacrosOff': '{meal}: החלבון, הפחמימות והשומן מסתכמים ב-{sum} קק״ל; היעד הוא {kcal} קק״ל.',
      'page.copyPlan': 'העתקת תוכנית',
      'page.copyTitle': 'התחלה מתוכנית שמורה',
      'page.copyHint': 'ההעתק הוא טיוטה חדשה; המקור לא משתנה.',
      'page.copyNone': 'ללקוח עדיין אין תוכניות קודמות להעתקה.',
      'page.copyLoadFailed': 'טעינת התוכניות של הלקוח נכשלה.',
      'page.copyOf': '{name} (העתק)',
      'page.copyDiffers': 'בתוכנית שהועתקה יש {meals} ארוחות ו-{kcal} קק״ל; היעדים הם {goalMeals} ארוחות ו-{goal} קק״ל. התאימו את הארוחות.',
      'page.useThisPlan': 'שימוש בתוכנית הזו',
      'page.preview': 'תצוגה מקדימה',
      'page.statusActive': 'פעילה',
      'page.tabClientPlans': 'התוכניות של הלקוח',
      'page.tabTemplates': 'תבניות',
      'page.templatesNone': 'עדיין אין תבניות. שמרו תוכנית כתבנית מ\'בדיקה ושליחה\', וכל הצוות יוכל להתחיל ממנה.',
      'page.templatesLoadFailed': 'טעינת התבניות נכשלה.',
      'page.byYou': 'שלכם',
      'page.byTeam': 'של הצוות',
      'page.saveAsTemplate': 'שמירה כתבנית',
      'page.templateName': 'שם התבנית',
      'page.saveTemplate': 'שמירת התבנית',
      'page.templateHint': 'כל מי שבחברה שלכם יוכל להתחיל ממנה. הערות התוכנית לא נכללות; ודאו שהשם לא מזהה את הלקוח.',
      'page.templateSaved': '"{name}" נשמרה כתבנית',
      'page.templateSaveFailed': 'שמירת התבנית נכשלה',
      'page.foodMoved': '{food} הועבר אל {meal}',
      'page.duplicateMeal': 'שכפול ארוחה',
      'page.mealDuplicated': '{meal} שוכפלה. ההעתק מופיע מיד אחריה.',
      'page.saveGoalsFirst': 'קודם שמרו או בטלו את השינויים ביעדים.',
      'page.startSavesGoals': 'ההתחלה שומרת את היעדים האלה בפרופיל של {name}, כך שהם יופיעו גם באפליקציה.',
      'page.kcal': 'קק״ל',
      'page.useAsMain': 'שימוש כמנה עיקרית',
      'page.makeMain': 'הפיכה למנה עיקרית',
      'page.mainReplaced': '"{dish}" היא עכשיו המנה העיקרית. המנה העיקרית הקודמת נשמרה כחלופה.',
      'page.mainSet': '"{dish}" היא עכשיו המנה העיקרית.',
      'page.clientBrief': 'תקציר הלקוח',
      'page.briefNeedsReview': 'דורש בדיקה',
      'page.briefConfirmed': 'אושר',
      'page.briefIntro': 'זה מה ש{name} סיפר לנו. קראו לפני בניית התוכנית, והוסיפו למטה הערות משלכם ל-AI.',
      'page.allergies': 'אלרגיות',
      'page.limitsAndDiet': 'הגבלות ותזונה',
      'page.medicalConditions': 'מצבים רפואיים',
      'page.goalAndRegion': 'מטרה ואזור',
      'page.notesForAi': 'הערות ל-AI',
      'page.notesForAiHint': 'לדוגמה: עובד במשמרות לילה, בלי בישול בצהריים, מעדיף ארוחות בוקר מהירות',
      'page.noneRecorded': 'לא נרשם',
      'page.currentPlan': 'התוכנית הנוכחית',
      'page.noActivePlan': 'אין תוכנית פעילה',
      'page.weightTrend': 'משקל',
      'page.noWeighIns': 'עדיין אין שקילות',
      'page.since': 'מאז {date}',
      'page.loadingShort': 'טוען…',
      'page.confirmBrief': 'קראתי, המשך',
      'page.briefSaved': 'ההערות ל-AI נשמרו',
      'page.briefSaveFailed': 'שמירת ההערות נכשלה. הן עדיין כאן; נסו שוב.',
      'page.confirmBriefFirst': 'קודם אשרו את תקציר הלקוח.',
      'page.editNotes': 'עריכת ההערות',
      'page.notesSaveNote': 'ההערות שלכם נשמרות בפרופיל של {name}; ה-AI פועל לפיהן.',
      'page.briefReadNote': 'אשרו אחרי שקראתם.',
      'page.caloriesPerDay': 'קלוריות ליום',
      'page.caloriesFollowMacros': 'הקלוריות נגזרות מהמאקרו (חלבון ופחמימה 4 קק"ל לגרם, שומן 9). הזזת הקלוריות משנה את שלושתם ושומרת על היחס ביניהם.',
      'page.mealsAddUp': 'הארוחות מסתכמות ב-{sum} מתוך {kcal} קק"ל',
      'page.kcalLeft': 'נותרו {n} קק"ל',
      'page.kcalOver': '{n} קק"ל מעל',
      'page.ofDay': '{pct}% מהיום',
      'page.estimatedShort': '≈ הערכה לפי היום',
      'page.stepsNav': 'שלבים',
      'page.stepStart': 'נקודת התחלה',
      'page.stepStructure': 'מבנה',
      'page.stepSend': 'בדיקה ושליחה',
      'page.startTitle': 'איך תרצו לבנות את התוכנית של {name}?',
      'page.startBody': 'בחרו נקודת התחלה. בכל דרך אפשר לשנות כל ארוחה, ו{name} לא רואה כלום עד שתשלחו.',
      'page.startAiTitle': 'בנייה עם AI',
      'page.startAiBody': 'אתם קובעים את המבנה (קלוריות, איזון מאקרו, ארוחות). ה-AI ממלא כל ארוחה לפי היעד שלה ושומר על האלרגיות של הלקוח ועל ההערות שלכם.',
      'page.startManualTitle': 'בנייה ידנית',
      'page.startManualBody': 'נפתחות מיד ארוחות ריקות, לכל אחת היעד שלה לפי היעדים של {name}. מוסיפים מזונות מהמאגר ומכוונים גרמים.',
      'page.nextAddFoods': 'הבא: הוספת מזונות',
      'page.startCopyTitle': 'התחלה מתוכנית שמורה',
      'page.startCopyBody': 'שימוש חוזר באחת התוכניות הקודמות של {name} או בתבנית של הצוות. היא נפתחת כטיוטה חדשה; המקור לא משתנה.',
      'page.nextStructure': 'הבא: קביעת המבנה',
      'page.nextPickPlan': 'הבא: בחירת תוכנית',
      'page.structureTitle': 'קבעו את המבנה שעליו נבנית התוכנית',
      'page.structureBodyAi': 'זו התבנית שה-AI עובד לפיה: קלוריות ליום, האיזון בין חלבון, פחמימה ושומן, וכמה מהיום מקבלת כל ארוחה.',
      'page.back': 'חזרה',
      'page.change': 'שינוי',
      'page.methodAi': 'בנייה עם AI',
      'page.dailyCaloriesHint': 'שינוי הערך שומר על החלוקה בין הארוחות ועל איזון המאקרו.',
      'page.macroStyle': 'איזון מאקרו',
      'page.presetBalanced': 'מאוזן',
      'page.presetHighProtein': 'עתיר חלבון',
      'page.presetLowCarb': 'דל פחמימה',
      'page.presetCustom': 'מותאם אישית',
      'page.customMacrosHint': 'הזינו גרמים. הקלוריות מחושבות מהם: חלבון ופחמימה 4 קק״ל לגרם, שומן 9.',
      'page.mealSplit': 'הארוחות וחלקן ביום',
      'page.mealSplitHint': 'תנו לכל ארוחה אחוז מהקלוריות היומיות. החלבון, הפחמימה והשומן שלה שומרים על אותו איזון כמו היום.',
      'page.mealShare': 'חלק מהיום',
      'page.splitOk': 'הארוחות מסתכמות ליום המלא',
      'page.splitUnder': '{n} קק״ל לא שויכו לאף ארוחה',
      'page.splitOver': 'הארוחות חורגות ב-{n} קק״ל מהיום',
      'page.fitToDay': 'התאמה ליום',
      'page.advancedGrams': 'גרמים לכל ארוחה',
      'page.advancedGramsHint': 'קבעו בעצמכם חלבון, פחמימה ושומן לכל ארוחה. הקלוריות של הארוחה נגזרות מהם.',
      'page.dishHintShort': 'רעיון ל-AI (לא חובה), למשל חביתה',
      'page.aiWillBuild': 'מה ה-AI יבנה',
      'page.aiWillBuildLine': '{n} ארוחות · {kcal} קק״ל ביום',
      'page.aiAvoids': 'בלי',
      'page.aiFollowsNotes': 'לפי ההערות שלכם',
      'page.aiReviewAfter': 'בדרך כלל פחות מדקה. שום דבר לא מגיע ל{name} עד שתבדקו ותשלחו.',
      'page.generatePlan': 'יצירת התוכנית',
      'page.createMeals': 'יצירת {n} ארוחות ריקות',
      'page.mealsTitle': 'בנו ובדקו כל ארוחה',
      'page.mealsBody': 'פתחו ארוחה מהרשימה. כל ארוחה מציגה את היעד שלה מהמבנה; כוונו מזונות וגרמים עד שהיא ביעד.',
      'page.mealsBodyGenerating': 'ה-AI בונה {n} ארוחות לפי המבנה שקבעתם. כל ארוחה מתמלאת כשהיא מוכנה.',
      'page.nextReview': 'הבא: בדיקה ושליחה',
      'page.backToMeals': 'חזרה לארוחות',
      'page.sendTitle': 'בדיקה ושליחה ל{name}',
      'page.structureLabel': 'מבנה',
      'page.editStructure': 'עריכת המבנה',
      'page.hideStructure': 'הסתרת המבנה',
      'page.dayStructure': 'מבנה היום',
      'page.aboutClient': 'על {name}',
      'page.mustAvoid': 'חובה להימנע',
      'page.saveNotes': 'שמירת ההערות',
      'page.notesAiHelp': 'ה-AI קורא אותן כשהוא בונה את התוכנית. נשמרות בפרופיל של {name}.',
      'page.avoid': 'להימנע',
      'page.dayOverview': 'יום',
      'page.stepOf': 'שלב {n} מתוך {total}',
      'page.targetsButton': 'יעדים',
      'page.targetsDrawerHint': 'קלוריות ליום, איזון מאקרו וחלק כל ארוחה. השמירה מעדכנת את היעדים של {name}.',
      'page.moreActions': 'פעולות נוספות',
      'page.fixGrams': 'תיקון גרמים',
      'page.renameDish': 'שינוי שם המנה',
      'page.removeMeal': 'הסרת הארוחה',
      'page.suggestWithAi': 'הצעה עם AI',
      'page.alternativesHint': 'מנות אחרות שהלקוח יכול להחליף בהן את הארוחה.',
      'page.mealCol': 'ארוחה',
      'page.shareCol': 'חלק מהיום',
      'page.useShares': 'אחוז לכל ארוחה',
      'page.addIdea': 'הוספת רעיון ל-AI',
    }
  };

  const t = (key) => {
    const translations = mealTranslations[language] || mealTranslations.english;
    return translations[key] || key;
  };

  // --- Draft model: everything is a local draft until "Save meal plan" ---
  const snapshotOf = (plan, note) => JSON.stringify({ plan, note });
  const isPlanDirty = !!generatedMealPlan && snapshotOf(generatedMealPlan, mealPlanNote) !== savedSnapshot;
  const planStatus = !generatedMealPlan ? null : !savedMealPlanId ? 'draft' : isPlanDirty ? 'unsaved' : 'saved';

  useEffect(() => {
    if (!isPlanDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isPlanDirty]);

  // When a drafted plan has fully arrived: bring it into view, focus it, and say what arrived.
  useEffect(() => {
    if (!revealPlan || isGenerating || !generatedMealPlan?.meals?.length) return;
    const frame = requestAnimationFrame(() => {
      const el = generatedPlanRef.current;
      if (!el) return;
      setActiveMealIndex(0);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
      const meals = generatedMealPlan.meals;
      const kcal = Math.round(meals.reduce((sum, m) => sum + (m.main?.nutrition?.calories || m.main?.calories || 0), 0));
      const within = meals.filter((m, i) => {
        const slot = slotTargetFor(m, i);
        return slot && reconcile(m.main?.nutrition?.calories || m.main?.calories || 0, slot.calories, MEAL_TOLERANCE).status === 'on';
      }).length;
      toast.success(t('page.planReadyToast'), {
        description: t('page.planReadySummary')
          .replace('{meals}', meals.length)
          .replace('{kcal}', kcal.toLocaleString())
          .replace('{target}', Number(dayTargets.calories || 0).toLocaleString())
          .replace('{within}', within),
      });
      setRevealPlan(null);
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealPlan, isGenerating, generatedMealPlan]);

  // Slot name → translation key: English first, then Hebrew. No match → null, and the slot keeps its own name.
  const slotNameKey = (name) => {
    const n = String(name || '').trim().toLowerCase();
    if (!n) return null;
    if (n.startsWith('meal.') && mealTranslations.english[n]) return n;
    const find = (dict) => Object.keys(dict).find((k) => k.startsWith('meal.') && String(dict[k]).toLowerCase() === n);
    return find(mealTranslations.english) || find(mealTranslations.hebrew) || null;
  };
  const slotLabel = (name) => {
    const key = slotNameKey(name);
    return key ? t(key) : String(name || '');
  };

  // The saved target for a drafted meal: the slot at the same position if it is the same slot, else the slot with that name.
  // A meal added for this plan only has no target. Without saved per-meal macros, they're the slot's share of the day (≈).
  const slotTargetFor = (meal, index) => {
    if (meal?.planOnly) return null;
    const key = slotNameKey(meal?.meal);
    const raw = String(meal?.meal || '').trim().toLowerCase();
    const sameSlot = (s) => !!s && ((key && slotNameKey(s.mealSlot) === key) || (raw && s.mealSlot.toLowerCase() === raw));
    const s = sameSlot(slots[index]) ? slots[index] : (slots.find(sameSlot) || slots[index]);
    if (!s) return null;
    const m = slotMacros(s, dayTargets);
    return { calories: Number(s.targetCalories) || 0, protein: m.protein, carbs: m.carbs, fat: m.fats, estimated: m.estimated };
  };

  const translateMealNameForDisplay = (mealName) => {
    if (!mealName) return mealName;
    let nameKey = null;
    const englishTranslations = mealTranslations.english;

    if (mealName.startsWith('meal.')) {
      nameKey = mealName;
    } else {
      for (const [key, value] of Object.entries(englishTranslations)) {
        if (value === mealName) {
          nameKey = key;
          break;
        }
      }
      if (!nameKey) {
        const hebrewTranslations = mealTranslations.hebrew;
        for (const [key, value] of Object.entries(hebrewTranslations)) {
          if (value === mealName) {
            nameKey = key;
            break;
          }
        }
      }
    }

    if (nameKey) {
      const currentTranslations = mealTranslations[language] || mealTranslations.english;
      return currentTranslations[nameKey] || mealName;
    }
    return mealName;
  };

  // --- LOAD: the client's saved targets and slots, as stored ---
  useEffect(() => {
    if (!selectedClient?.id) return undefined;
    let cancelled = false;

    const loadData = async () => {
      setIsLoadingData(true);
      setLoadError(null);
      // New client → start clean. Never carry another client's plan (or its saved id) across.
      streamTimers.current.forEach(clearTimeout);
      streamTimers.current = [];
      setGeneratedMealPlan(null);
      setIsGenerating(false);
      setDraftPhase(null);
      setRedraftConfirm(false);
      setSavedMealPlanId(null);
      setSavedMealPlanName(null);
      savedMealPlanIdRef.current = null;
      setSavedSnapshot(null);
      setSentToClient(false);
      setPublishOpen(false);
      setAiDiscoverPreviews({});
      setAiDiscoverHistory({});
      setAiNote('');
      setTargetsOpen(false);
      setStartMethod(null);
      setCustomMacros(false);
      setAdvancedSplit(false);
      setBriefError('');
      setWeightLogs(null);
      setCopyOpen(false);
      setCopyPlans(null);
      setCopyPickId(null);
      setCopyTab('client');
      setCopyNote('');
      setTemplateName(null);
      setTargetsError('');
      setAddingMeal(false);
      setEstimatedFromProfile(false);
      setActiveMealIndex(0);
      try {
        const [config, targets, structure, recs] = await Promise.all([
          fetchClientMealPlanConfig(selectedClient.id),
          fetchClientDailyTargets(selectedClient.id),
          fetchClientMealPlanStructure(selectedClient.id),
          fetchClientRecommendations(selectedClient.id),
        ]);
        if (cancelled) return;
        const day = {
          calories: parseGrams(targets?.calories),
          protein: parseGrams(targets?.protein),
          carbs: parseGrams(targets?.carbs),
          fats: parseGrams(targets?.fats),
        };
        const savedSlots = readSlots(structure?.structure, day.calories);
        const meals = Number(targets?.numberOfMeals ?? config?.numberOfMeals ?? structure?.numberOfMeals) || savedSlots.length || 0;
        const saved = { day, meals, slots: savedSlots };
        const hasTargets = hasSavedDailyTargets(targets || {});
        setDayTargets(day);
        setTargetsSaved(hasTargets);
        setSlots(savedSlots);
        setMealCount(meals);
        setWork(workFrom(saved));
        setAutoSlots(!savedSlots.length);
        setClientProfile(targets?.profile || null);
        const loadedBrief = briefFrom(targets?.brief);
        setBrief(loadedBrief);
        setBriefSaved(loadedBrief);
        fetchWeightLogs(selectedClient.user_code, selectedClient.id)
          .then((res) => { if (!cancelled) setWeightLogs(res?.data || []); })
          .catch(() => { if (!cancelled) setWeightLogs([]); });
        // The server keeps onboarding's values the first time a coach changes them; until then the record is onboarding's.
        // A client with no targets never had onboarding values: whatever the coach saves is the coach's.
        const snap = targets?.onboardingTargets;
        if (!hasTargets) {
          setOnboardingValues(null);
        } else if (snap) {
          let m = snap.macros || {};
          if (typeof m === 'string') { try { m = JSON.parse(m); } catch { m = {}; } }
          const snapDay = {
            calories: parseGrams(snap.calories),
            protein: parseGrams(m.protein ?? m.protein_g),
            carbs: parseGrams(m.carbs ?? m.carbohydrates),
            fats: parseGrams(m.fats ?? m.fat),
          };
          setOnboardingValues({ day: snapDay, meals: Number(snap.number_of_meals) || 0, slots: readSlots(snap.meal_plan_structure, snapDay.calories) });
        } else {
          setOnboardingValues(cloneTargets(saved));
        }
        setMealPlanNote(recs?.recommendations ?? '');
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading data', err);
        setLoadError(err?.message || 'load-failed');
      } finally {
        if (!cancelled) setIsLoadingData(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, reloadKey]);

  useEffect(() => () => {
    streamTimers.current.forEach(clearTimeout);
  }, []);

  // Recent foods come from this client's earlier plans; load them quietly once a plan is open.
  useEffect(() => {
    if (copyPlans || !selectedClient || isLoadingData) return;
    let cancelled = false;
    fetchClientMealPlans(selectedClient.user_code || selectedClient.id)
      .then((plans) => { if (!cancelled) setCopyPlans((Array.isArray(plans) ? plans : []).filter((p) => p?.meal_plan?.meals?.length)); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, isLoadingData]);

  const macrosForClient = (day) => ({
    protein: `${day.protein}g`,
    carbs: `${day.carbs}g`,
    fat: `${day.fats}g`,
  });

  // --- CLIENT BRIEF: what the client told us (read-only) plus the coach's notes for the AI; read and confirmed first ---
  const notesDirty = brief.notes.trim() !== briefSaved.notes.trim();
  const optionLabel = (options, item) => {
    const opt = options.find((o) => optionMatches(o, item));
    return opt ? (language === 'hebrew' ? opt.labelHe : opt.labelEn) : item;
  };
  const setNotes = (text) => setBrief((b) => ({ ...b, notes: text }));

  // Save the coach's notes for the AI (the AI reads them from the client record). True when saved or unchanged.
  const saveNotes = async () => {
    setBriefError('');
    if (!notesDirty) return true;
    setIsSavingBrief(true);
    try {
      await updateClientSettings(selectedClient.id, { recommendations: brief.notes.trim() });
      setBriefSaved((b) => ({ ...b, notes: brief.notes.trim() }));
      toast.success(t('page.briefSaved'));
      return true;
    } catch (e) {
      setBriefError(t('page.briefSaveFailed'));
      return false;
    } finally {
      setIsSavingBrief(false);
    }
  };
  const cancelNoteEdits = () => { setBrief((b) => ({ ...b, notes: briefSaved.notes })); setBriefError(''); };

  // Progress for the brief: the latest weigh-in and the change over about a month.
  const weightTrend = (() => {
    const logs = (weightLogs || [])
      .filter((l) => Number(l?.weight_kg) > 0 && l?.measurement_date)
      .sort((a, b) => new Date(b.measurement_date) - new Date(a.measurement_date));
    if (!logs.length) return null;
    const latest = logs[0];
    const earlier = logs.find((l) => new Date(latest.measurement_date) - new Date(l.measurement_date) >= 28 * 864e5) || logs[logs.length - 1];
    return {
      kg: Number(latest.weight_kg),
      date: latest.measurement_date,
      change: earlier === latest ? null : Math.round((Number(latest.weight_kg) - Number(earlier.weight_kg)) * 10) / 10,
      since: earlier.measurement_date,
    };
  })();

  // --- PRESCRIPTION: edited in place (first screen and the builder's targets strip) ---
  const slotKeys = Object.keys(mealTranslations.english).filter((k) => k.startsWith('meal.'));
  const workDay = {
    calories: parseGrams(work?.day.calories),
    protein: parseGrams(work?.day.protein),
    carbs: parseGrams(work?.day.carbs),
    fats: parseGrams(work?.day.fats),
  };
  const workMeals = Number(work?.meals) || 0;
  // Compare prescriptions by their numbers and slot details only.
  const targetsKey = (v) => (v ? JSON.stringify({
    d: ['calories', 'protein', 'carbs', 'fats'].map((k) => parseGrams(v.day[k])),
    m: Number(v.meals) || 0,
    s: v.slots.map((x) => [x.mealSlot, x.mealName, parseGrams(x.targetCalories), x.targetMacros && ['protein', 'carbs', 'fats'].map((k) => parseGrams(x.targetMacros[k]))]),
  }) : '');
  const savedTargets = { day: dayTargets, meals: mealCount, slots };
  const targetsDirty = !!work && targetsKey(work) !== targetsKey(savedTargets);
  const changedByCoach = !!onboardingValues && targetsSaved && targetsKey(savedTargets) !== targetsKey(onboardingValues);
  const canResetToOnboarding = !!onboardingValues && targetsSaved && targetsKey(work) !== targetsKey(onboardingValues);
  const targetsComplete = workDay.calories > 0 && workDay.protein > 0 && workDay.carbs > 0 && workDay.fats > 0;
  // The listed meals are the meal count; the number alone only counts while no meals are listed.
  const plannedMeals = work?.slots.length || workMeals;
  const mealsValid = plannedMeals >= 1 && plannedMeals <= 8;

  // A meal count picked while the meals are only suggested: that many default meals, the day split evenly.
  // They stay suggested, so they keep following the day's calories until the coach changes one.
  const setSuggestedMealCount = (n) => {
    setAutoSlots(true);
    setWork((w) => ({ ...w, meals: n, slots: suggestSlots(n, w.day.calories) }));
  };

  // One slot, one field. The other slots never move. Per-meal macros exist only in the structured format.
  const setWorkSlot = (index, key, value) => { setAutoSlots(false); setWork((w) => ({
    ...w,
    slots: w.slots.map((s, i) => {
      if (i !== index) return s;
      if (key === 'mealSlot' || key === 'mealName' || key === 'targetCalories') return { ...s, [key]: value };
      const m = s.targetMacros || { protein: '', carbs: '', fats: '' };
      return { ...s, format: 'structured', targetMacros: { ...m, [key]: value } };
    }),
  })); };

  // --- LINKED SLIDERS: calories follow the macros (P×4 + C×4 + F×9); moving calories scales P/C/F keeping their ratio ---
  // The ratio is remembered while only calories move, so dragging down and back up never drifts from rounding.
  const ratioRef = useRef({});
  const macroKcalOf = (m) => Math.round((parseGrams(m.protein) || 0) * 4 + (parseGrams(m.carbs) || 0) * 4 + (parseGrams(m.fats) || 0) * 9);
  const scaleMacros = (refKey, macros, kcal) => {
    const now = ['protein', 'carbs', 'fats'].map((k) => parseGrams(macros[k]) || 0);
    let held = ratioRef.current[refKey];
    if (!held || held.produced !== now.join('|')) {
      const from = now[0] * 4 + now[1] * 4 + now[2] * 9;
      if (!(from > 0)) return null;
      held = { share: now.map((g, i) => (g * (i === 2 ? 9 : 4)) / from) };
    }
    const next = held.share.map((s, i) => Math.round((s * kcal) / (i === 2 ? 9 : 4)));
    ratioRef.current[refKey] = { share: held.share, produced: next.join('|') };
    return { protein: next[0], carbs: next[1], fats: next[2] };
  };

  // The meals keep their share of the day when the day's calories move. Shares are remembered the same way as the
  // macro ratio, so typing 1 → 18 → 1800 lands back on the same split.
  const splitRef = useRef(null);
  const scaleSlotsToDay = (list, kcal, oldDay) => {
    const now = list.map((s) => parseGrams(s.targetCalories) || 0);
    let held = splitRef.current;
    if (!held || held.produced !== now.join('|') || held.share.length !== now.length) {
      const from = now.reduce((a, b) => a + b, 0);
      if (!(from > 0)) return list;
      const day = parseGrams(oldDay) || from;
      held = { share: now.map((k) => k / day) };
    }
    const next = held.share.map((s) => Math.round(s * kcal));
    splitRef.current = { share: held.share, produced: next.join('|') };
    return list.map((s, i) => {
      const scaled = s.targetMacros ? scaleMacros(`slot${i}`, s.targetMacros, next[i]) : null;
      return { ...s, targetCalories: next[i], ...(scaled ? { targetMacros: scaled } : {}) };
    });
  };

  const setDayCalories = (value) => {
    setEstimatedFromProfile(false);
    setWork((w) => {
      const kcal = value === '' ? '' : Number(value);
      const scaled = kcal > 0 ? (scaleMacros('day', w.day, kcal) || gramsFor(kcal, MACRO_PRESETS[0].share)) : null;
      return {
        ...w,
        day: { ...w.day, ...(scaled || {}), calories: kcal },
        slots: autoSlots ? suggestSlots(Number(w.meals) || 4, kcal) : (kcal > 0 ? scaleSlotsToDay(w.slots, kcal, w.day.calories) : w.slots),
      };
    });
  };

  // Macro balance presets, as shares of the day's calories (protein, carbs, fat).
  const MACRO_PRESETS = [
    { id: 'balanced', label: 'page.presetBalanced', share: [0.3, 0.4, 0.3] },
    { id: 'highProtein', label: 'page.presetHighProtein', share: [0.4, 0.35, 0.25] },
    { id: 'lowCarb', label: 'page.presetLowCarb', share: [0.35, 0.25, 0.4] },
  ];
  const gramsFor = (kcal, share) => ({
    protein: Math.round((kcal * share[0]) / 4),
    carbs: Math.round((kcal * share[1]) / 4),
    fats: Math.round((kcal * share[2]) / 9),
  });
  // The preset the day's grams match (within 1.5 points of each share), or null.
  const matchingPreset = (() => {
    const total = workDay.protein * 4 + workDay.carbs * 4 + workDay.fats * 9;
    if (!(total > 0)) return null;
    const now = [workDay.protein * 4 / total, workDay.carbs * 4 / total, workDay.fats * 9 / total];
    return MACRO_PRESETS.find((p) => p.share.every((s, i) => Math.abs(s - now[i]) <= 0.015))?.id || null;
  })();
  // A preset sets the day's grams, and every meal with its own grams follows the same balance.
  const applyPreset = (preset) => {
    setCustomMacros(false);
    setEstimatedFromProfile(false);
    setWork((w) => {
      const kcal = parseGrams(w.day.calories);
      if (!(kcal > 0)) return w;
      return {
        ...w,
        day: { ...gramsFor(kcal, preset.share), calories: kcal },
        slots: w.slots.map((s) => (s.targetMacros ? { ...s, targetMacros: gramsFor(parseGrams(s.targetCalories) || 0, preset.share) } : s)),
      };
    });
  };

  // A meal's share of the day, in whole percent; its calories (and grams, if it has its own) follow.
  const setSlotShare = (index, pct) => {
    if (pct === '') { setSlotCalories(index, ''); return; }
    setSlotCalories(index, Math.round((workDay.calories * Math.max(0, Number(pct) || 0)) / 100));
  };
  // Scale every meal so together they make the whole day; the last meal takes the rounding.
  const fitSlotsToDay = () => {
    setAutoSlots(false);
    setWork((w) => {
      const day = parseGrams(w.day.calories);
      const now = w.slots.map((s) => parseGrams(s.targetCalories) || 0);
      const sum = now.reduce((a, b) => a + b, 0);
      if (!(day > 0)) return w;
      const next = sum > 0 ? now.map((k) => Math.round((k * day) / sum)) : now.map(() => Math.floor(day / now.length));
      next[next.length - 1] += day - next.reduce((a, b) => a + b, 0);
      return {
        ...w,
        slots: w.slots.map((s, i) => {
          const scaled = s.targetMacros ? scaleMacros(`slot${i}`, s.targetMacros, next[i]) : null;
          return { ...s, targetCalories: next[i], ...(scaled ? { targetMacros: scaled } : {}) };
        }),
      };
    });
  };
  const setDayMacro = (key, value) => {
    setEstimatedFromProfile(false);
    setWork((w) => {
      const day = { ...w.day, [key]: value };
      const kcal = macroKcalOf(day);
      const slotsNext = autoSlots ? suggestSlots(Number(w.meals) || 4, kcal) : (kcal > 0 ? scaleSlotsToDay(w.slots, kcal, w.day.calories) : w.slots);
      return { ...w, day: { ...day, calories: kcal }, slots: slotsNext };
    });
  };

  // One meal moves; the others never do. A meal with only calories (older format) keeps only calories until a macro moves.
  const setSlotCalories = (index, value) => { setAutoSlots(false); setWork((w) => ({
    ...w,
    slots: w.slots.map((s, i) => {
      if (i !== index) return s;
      const kcal = value === '' ? '' : Number(value);
      const scaled = s.targetMacros && kcal > 0 ? scaleMacros(`slot${i}`, s.targetMacros, kcal) : null;
      return { ...s, targetCalories: kcal, ...(scaled ? { targetMacros: scaled } : {}) };
    }),
  })); };
  const setSlotMacro = (index, key, value) => { setAutoSlots(false); setWork((w) => {
    const day = { calories: parseGrams(w.day.calories), protein: parseGrams(w.day.protein), carbs: parseGrams(w.day.carbs), fats: parseGrams(w.day.fats) };
    return {
      ...w,
      slots: w.slots.map((s, i) => {
        if (i !== index) return s;
        // Converting an older slot: start from its estimate so the other two macros don't drop to zero.
        const est = slotMacros({ ...s, targetMacros: null }, day);
        const base = { protein: s.targetMacros?.protein ?? est.protein, carbs: s.targetMacros?.carbs ?? est.carbs, fats: s.targetMacros?.fats ?? est.fats };
        ['protein', 'carbs', 'fats'].forEach((k) => { if (base[k] === '' || base[k] == null) base[k] = est[k] || 0; });
        const targetMacros = { ...base, [key]: value };
        return { ...s, format: 'structured', targetMacros, targetCalories: macroKcalOf(targetMacros) };
      }),
    };
  }); };

  const addWorkSlot = () => {
    setAutoSlots(false);
    setWork((w) => {
    const used = new Set(w.slots.map((s) => slotNameKey(s.mealSlot)));
    const key = slotKeys.find((k) => !used.has(k)) || 'meal.morningSnack';
    // A new slot follows the client's existing format; a client with no slots gets onboarding's format.
    const format = w.slots.some((s) => s.format === 'structured') ? 'structured' : 'legacy';
    // A new meal starts as a snack-sized tenth of the day; the total line offers to fit the split back to the day.
    const kcal = Math.round((parseGrams(w.day.calories) || 0) * 0.1);
    const next = [...w.slots, { mealSlot: mealTranslations.english[key], mealName: '', targetCalories: kcal || '', targetMacros: null, format }];
    return { ...w, meals: next.length, slots: next };
    });
  };

  const removeWorkSlot = (index) => {
    setAutoSlots(false);
    setWork((w) => {
      const next = w.slots.filter((_, i) => i !== index);
      return { ...w, meals: next.length || w.meals, slots: next };
    });
  };

  const undoTargetChanges = () => { setTargetsError(''); setEstimatedFromProfile(false); setCustomMacros(false); setWork(workFrom(savedTargets)); setAutoSlots(!slots.length); };
  const resetToOnboarding = () => { setTargetsError(''); setCustomMacros(false); setWork(workFrom(onboardingValues)); setAutoSlots(!onboardingValues.slots.length); };

  // Clients who never onboarded: prefill from their profile (Mifflin/Harris-style estimate, 30/40/30 split).
  const profileEstimate = (() => {
    if (!clientProfile) return null;
    const r = resolveTargets({ targets: {}, client: clientProfile, weightLogs: [] });
    if (!r.calories.value) return null;
    const kcal = r.calories.value;
    return { calories: kcal, protein: Math.round((kcal * 0.3) / 4), carbs: Math.round((kcal * 0.4) / 4), fats: Math.round((kcal * 0.3) / 9) };
  })();
  const applyProfileEstimate = () => {
    if (!profileEstimate) return;
    setWork((w) => ({ ...w, day: { ...profileEstimate }, meals: w.meals || 4, slots: autoSlots ? suggestSlots(w.meals || 4, profileEstimate.calories) : w.slots }));
    setEstimatedFromProfile(true);
  };

  // Saves the working copy to the client. Returns what was saved (so a start can use it right away), or null.
  const saveTargets = async () => {
    if (!selectedClient?.id || !work) return null;
    if (!targetsComplete) { setTargetsError(t('page.targetsFormIncomplete')); return null; }
    if (!mealsValid) { setTargetsError(t('page.mealsRange')); return null; }
    const day = workDay;
    const meals = plannedMeals;
    const named = work.slots.filter((s) => String(s.mealSlot || '').trim());
    // Once any meal has its own macros, the client's whole split moves to the format that stores them.
    const structured = named.some((s) => s.format === 'structured');
    const nextSlots = named.map((s) => {
      if (!structured) return s;
      if (!s.targetMacros) return { ...s, format: 'structured' };
      const est = slotMacros({ ...s, targetMacros: null }, day);
      const fill = (k) => (String(s.targetMacros[k] ?? '').trim() === '' ? est[k] : s.targetMacros[k]);
      return { ...s, format: 'structured', targetMacros: { protein: fill('protein'), carbs: fill('carbs'), fats: fill('fats') } };
    });
    const structure = nextSlots.map((s) => writeSlot(s, day.calories));
    const body = { calories: day.calories, macros: macrosForClient(day), numberOfMeals: meals };
    if (structure.length || slots.length) body.mealPlanStructure = structure;
    setIsSavingTargets(true);
    setTargetsError('');
    try {
      await updateClientSettings(selectedClient.id, body);
      const saved = { day, meals, slots: readSlots(structure, day.calories) };
      setDayTargets(day);
      setTargetsSaved(true);
      setSlots(saved.slots);
      setMealCount(meals);
      setWork(cloneTargets(saved));
      setEstimatedFromProfile(false);
      setAutoSlots(false);
      toast.success(t('page.targetsSaved'));
      return saved;
    } catch (error) {
      setTargetsError(t('page.targetsSaveFailedKept'));
      return null;
    } finally {
      setIsSavingTargets(false);
    }
  };

  // The two ways to start. Unsaved notes and target changes are saved first, so the AI and the client's record agree.
  const startWith = async (kind) => {
    if (!(await saveNotes())) return;
    let current = savedTargets;
    if (!targetsSaved || targetsDirty) {
      current = await saveTargets();
      if (!current) return;
    }
    if (kind === 'ai') requestDraft(current.slots);
    else startManual(current);
  };

  // Build it myself: one empty meal per slot (or per meal the client asked for), then food search and grams.
  const startManual = (prescription = savedTargets) => {
    const names = prescription.slots.length
      ? prescription.slots.map((s) => s.mealSlot)
      : defaultSlotNames(prescription.meals || 3);
    const zero = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    streamTimers.current.forEach(clearTimeout);
    streamTimers.current = [];
    setGeneratedMealPlan({
      meals: names.map((name) => ({ meal: name, main: { meal_title: '', ingredients: [], nutrition: { ...zero } }, alternatives: [] })),
      totals: { ...zero },
      note: '',
      origin: 'manual',
    });
    setSavedSnapshot(null);
    setSavedMealPlanId(null);
    setSavedMealPlanName(null);
    savedMealPlanIdRef.current = null;
    setSentToClient(false);
    setAiNote('');
    setActiveMealIndex(0);
    setTargetsOpen(false);
    requestAnimationFrame(() => document.getElementById('meal-detail-title')?.focus());
  };

  // --- RECENT FOODS: offered first in food search ---
  const recentFoods = (() => {
    const tally = new Map();
    const add = (ing) => {
      const item = String(ing?.item || ing?.name || '').trim();
      const grams = Number(ing?.['portionSI(gram)']) || 0;
      if (!item || !(grams > 0)) return;
      const cur = tally.get(item.toLowerCase()) || { count: 0, ing: { item, grams, calories: ing.calories, protein: ing.protein, carbs: ing.carbs, fat: ing.fat, brand: ing['brand of pruduct'] || '', UPC: ing.UPC || null } };
      cur.count += 1;
      tally.set(item.toLowerCase(), cur);
    };
    (copyPlans || []).forEach((p) => (p.meal_plan?.meals || []).forEach((m) => (m.main?.ingredients || []).forEach(add)));
    (generatedMealPlan?.meals || []).forEach((m) => (m.main?.ingredients || []).forEach(add));
    return [...tally.values()]
      .filter((v) => !matchIngredient(v.ing.item)?.skip)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((v) => v.ing);
  })();

  // --- COPY A PLAN: start from one of this client's earlier plans ---
  const planKcal = (plan) => {
    const meals = plan?.meal_plan?.meals || [];
    const fromMeals = meals.reduce((sum, m) => sum + (Number(m?.main?.nutrition?.calories ?? m?.main?.calories) || 0), 0);
    return Math.round(Number(plan?.daily_total_calories) || fromMeals);
  };

  const openCopyPicker = async () => {
    setCopyOpen(true);
    setCopyError('');
    if (copyPlans) return;
    try {
      const plans = await fetchClientMealPlans(selectedClient?.user_code || selectedClient?.id);
      const usable = (Array.isArray(plans) ? plans : []).filter((p) => p?.meal_plan?.meals?.length);
      setCopyPlans(usable);
      setCopyPickId(usable[0]?.id ?? null);
    } catch (e) {
      setCopyError(t('page.copyLoadFailed'));
      setCopyPlans(null);
    }
  };

  const loadTemplates = async () => {
    setTemplatesError('');
    try {
      const list = (await fetchMealPlanTemplates()).filter((p) => p?.meal_plan?.meals?.length);
      setTemplates(list);
      setTemplatePickId((id) => id ?? list[0]?.id ?? null);
    } catch (e) {
      setTemplates(null);
      setTemplatesError(t('page.templatesLoadFailed'));
    }
  };
  const showCopyTab = (tab) => {
    setCopyTab(tab);
    if (tab === 'templates' && !templates) loadTemplates();
  };

  // Save the plan on screen as a template the whole company can start from.
  const saveAsTemplate = async () => {
    const name = String(templateName || '').trim();
    if (!name || !generatedMealPlan?.meals?.length) return;
    setIsSavingTemplate(true);
    try {
      // Plan notes start from this client's recommendations, so a shared template leaves them out.
      const payload = { ...buildMealPlanPayload(generatedMealPlan, '', mealTranslations), note: '' };
      await saveMealPlanTemplate({
        name,
        mealPlan: payload,
        dailyTotalCalories: Math.round(computeMainTotalsFromPlan(generatedMealPlan).calories),
        macrosTarget: { protein: `${dayTargets.protein}g`, carbs: `${dayTargets.carbs}g`, fat: `${dayTargets.fats}g` },
      });
      setTemplateName(null);
      setTemplates(null); // the list reloads with the new one next time
      toast.success(t('page.templateSaved').replace('{name}', name));
    } catch (e) {
      toast.error(t('page.templateSaveFailed'), { description: e?.message });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // The copy is a new, unsaved draft: the earlier plan is never changed.
  const applyCopiedPlan = (plan, { fromTemplate = false } = {}) => {
    if (!plan?.meal_plan?.meals?.length) return;
    const meals = plan.meal_plan.meals.map((m) => normalizeMealAlternativeSlots({
      ...m,
      alternatives: Array.isArray(m.alternatives) ? m.alternatives : [],
    }));
    const totals = meals.reduce((acc, m) => {
      const n = m.main?.nutrition || {};
      return { calories: acc.calories + (n.calories || 0), protein: acc.protein + (n.protein || 0), carbs: acc.carbs + (n.carbs || 0), fat: acc.fat + (n.fat || 0) };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
    streamTimers.current.forEach(clearTimeout);
    streamTimers.current = [];
    setGeneratedMealPlan({ meals, totals, note: plan.meal_plan.note || '', origin: 'copy' });
    if (plan.meal_plan.note) setMealPlanNote(plan.meal_plan.note);
    setPlanNameDraft(fromTemplate ? (plan.meal_plan_name || '') : t('page.copyOf').replace('{name}', plan.meal_plan_name || t('page.newPlan')));
    setSavedSnapshot(null);
    setSavedMealPlanId(null);
    setSavedMealPlanName(null);
    savedMealPlanIdRef.current = null;
    setSentToClient(false);
    setAiNote('');
    setActiveMealIndex(0);
    setCopyOpen(false);
    // Say so if the copy doesn't match today's goals (calories beyond ±5%, or a different number of meals).
    const kcal = planKcal(plan);
    const goal = Number(dayTargets.calories) || 0;
    const differs = (goal > 0 && kcal > 0 && Math.abs(kcal - goal) > goal * DAY_TOLERANCE) || (mealCount > 0 && meals.length !== mealCount);
    setCopyNote(!differs ? '' : t('page.copyDiffers').replace('{kcal}', kcal.toLocaleString()).replace('{meals}', meals.length).replace('{goal}', goal.toLocaleString()).replace('{goalMeals}', mealCount || meals.length));
    requestAnimationFrame(() => document.getElementById('meal-detail-title')?.focus());
  };

  // Builder day list: meals added or removed here change this plan only, never the client's saved slots.
  const addPlanMeal = (englishName) => {
    if (!englishName) return;
    const zero = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    setActiveMealIndex(generatedMealPlan?.meals?.length || 0);
    setGeneratedMealPlan((prev) => (prev?.meals
      ? { ...prev, meals: [...prev.meals, { meal: englishName, main: { meal_title: '', ingredients: [], nutrition: { ...zero } }, alternatives: [], planOnly: true }] }
      : prev));
    setAddingMeal(false);
  };

  const removePlanMeal = (index) => {
    const removed = generatedMealPlan?.meals?.[index];
    const name = translateMealNameForDisplay(removed?.meal);
    setGeneratedMealPlan((prev) => (prev?.meals?.length > 1 ? { ...prev, meals: prev.meals.filter((_, i) => i !== index) } : prev));
    setActiveMealIndex((i) => Math.max(0, i >= index ? i - 1 : i));
    toast(t('page.mealRemovedFromPlan').replace('{meal}', name), {
      action: { label: t('page.undo'), onClick: () => { setGeneratedMealPlan((p) => (p?.meals ? { ...p, meals: [...p.meals.slice(0, index), removed, ...p.meals.slice(index)] } : p)); setActiveMealIndex(index); } },
    });
  };

  // Draft the day with AI. The service reads the client record: with saved slots it keeps their split
  // (preserve_structure); with none, it is asked to split the saved day across the client's meals.
  const handleGenerateMealPlan = async (slotsNow = slots) => {
    const userCode = selectedClient?.user_code || selectedClient?.id;
    if (!userCode) {
      toast.error(t('page.selectClientFirst'));
      return;
    }
    if (!CREATE_MEAL_PLAN_ENDPOINT) {
      toast.error(t('page.generateFailed'));
      return;
    }

    // If the request fails, the screen goes back to exactly what it was.
    const previousPlan = generatedMealPlan;
    const zero = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    const emptyMeal = (name) => ({
      meal: name,
      main: { meal_title: '', ingredients: [], nutrition: { ...zero } },
      alternatives: [],
      arriving: true,
    });
    const waitingNames = slotsNow.length ? slotsNow.map((s) => s.mealSlot) : defaultSlotNames(mealCount || 3);

    streamTimers.current.forEach(clearTimeout);
    streamTimers.current = [];
    setIsGenerating(true);
    setDraftPhase('request');
    setRedraftConfirm(false);
    setRevealPlan(null);
    setPublishOpen(false);
    setActiveMealIndex(0);
    setGeneratedMealPlan({ meals: waitingNames.map(emptyMeal), totals: { ...zero }, note: '', origin: 'pending' });

    try {
      const menuRes = await fetch(CREATE_MEAL_PLAN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_code: userCode, preserve_structure: slotsNow.length > 0, fresh: true }),
      });
      const menuData = await menuRes.json().catch(() => ({}));
      if (!menuRes.ok || !Array.isArray(menuData?.meals) || !menuData.meals.length) {
        const err = new Error(menuData?.error || menuRes.statusText || 'Meal plan generation failed');
        err.code = menuData?.error_code;
        throw err;
      }

      const noteFromApi = menuData.note || '';
      let meals = menuData.meals;
      if (language === 'hebrew') {
        try {
          const translatedMenu = await translateMenu({ meals, note: noteFromApi }, 'he');
          if (translatedMenu?.meals?.length) meals = translatedMenu.meals;
        } catch (translateError) {
          console.error('Translation error:', translateError);
          toast.error(t('page.translateFailed'));
        }
      }

      const arrived = meals.map((m) => normalizeMealAlternativeSlots({
        ...m,
        alternatives: Array.isArray(m.alternatives) ? m.alternatives : [],
      }));

      setAiNote(friendlyPlanNote(noteFromApi, language));
      setSavedSnapshot(null);
      setSavedMealPlanId(null);
      setSavedMealPlanName(null);
      savedMealPlanIdRef.current = null;
      setSentToClient(false);
      setAiDiscoverPreviews({});
      setAiDiscoverHistory({});
      setActiveMealIndex(0);

      // Meals arrive one by one, in order.
      setGeneratedMealPlan({ ...menuData, meals: arrived.map((m) => emptyMeal(m.meal)), totals: { ...zero }, note: noteFromApi, origin: 'generated' });
      setDraftPhase('stream');
      arrived.forEach((meal, i) => {
        const timer = setTimeout(() => {
          setGeneratedMealPlan((prev) => {
            if (!prev?.meals || prev.origin !== 'generated') return prev;
            const nextMeals = prev.meals.map((slot, idx) => (
              idx === i ? { ...meal, aiUntouched: true, arriving: false } : slot
            ));
            const nextTotals = nextMeals.reduce((acc, item) => {
              const n = item.main?.nutrition || {};
              return {
                calories: acc.calories + (n.calories || 0),
                protein: acc.protein + (n.protein || 0),
                carbs: acc.carbs + (n.carbs || 0),
                fat: acc.fat + (n.fat || 0),
              };
            }, { ...zero });
            return { ...prev, meals: nextMeals, totals: nextTotals };
          });
          if (i === arrived.length - 1) {
            setDraftPhase(null);
            setIsGenerating(false);
            setRevealPlan('generated');
          }
        }, 320 * i);
        streamTimers.current.push(timer);
      });
    } catch (error) {
      console.error('Generation failed', error);
      streamTimers.current.forEach(clearTimeout);
      streamTimers.current = [];
      setGeneratedMealPlan(previousPlan);
      setDraftPhase(null);
      setIsGenerating(false);
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      if (offline) toast.error(t('page.offlineDraft'));
      // fetch rejects with a TypeError when the service is unreachable or blocks this origin (CORS).
      else if (error instanceof TypeError) toast.error(t('page.serviceUnreachable'));
      else if (error.code === 'MISSING_MEAL_STRUCTURE') toast.error(t('page.noStructureFailed'));
      else toast.error(t('page.generateFailed'), { description: error.message });
    }
  };

  // slotsNow: slots saved a moment ago (a click handler passes an event instead, so only arrays count).
  const requestDraft = (slotsNow) => {
    const hasFood = !!generatedMealPlan?.meals?.some((meal) => meal.main?.ingredients?.length);
    if (hasFood) {
      setRedraftConfirm(true);
      return;
    }
    handleGenerateMealPlan(Array.isArray(slotsNow) ? slotsNow : slots);
  };

  const persistMealPlanToDb = async (planData, { mealPlanName } = {}) => {
    if (!selectedClient) {
      throw new Error(t('page.selectClientFirst'));
    }

    const userCode = selectedClient.user_code || selectedClient.id;
    if (!userCode) {
      throw new Error('Client identifier is required for saving meal plan');
    }

    const payload = buildMealPlanPayload(planData, mealPlanNote, mealTranslations);
    const mainTotals = computeMainTotalsFromPlan(planData);
    const macrosTarget = {
      protein: `${dayTargets.protein}g`,
      carbs: `${dayTargets.carbs}g`,
      fat: `${dayTargets.fats}g`,
    };

    if (savedMealPlanIdRef.current) {
      await updateMealPlan(savedMealPlanIdRef.current, {
        meal_plan: payload,
        daily_total_calories: Math.round(mainTotals.calories),
        macros_target: macrosTarget,
        ...(mealPlanName ? { meal_plan_name: mealPlanName } : {}),
      });
      if (mealPlanName) {
        setSavedMealPlanName(mealPlanName);
      }
      return savedMealPlanIdRef.current;
    }

    const name = mealPlanName || `Meal Plan - ${new Date().toLocaleDateString()}`;
    const result = await saveClientMealPlan(
      userCode,
      name,
      payload,
      Math.round(mainTotals.calories),
      macrosTarget
    );

    if (result?.id) {
      savedMealPlanIdRef.current = result.id;
      setSavedMealPlanId(result.id);
      setSavedMealPlanName(name);
    }
    return result?.id;
  };

  const clientLabel = () => {
    const raw = selectedClient?.full_name || selectedClient?.name || selectedClient?.user_code || '';
    return String(raw).trim().split(/\s+/)[0] || t('page.forClient');
  };

  const defaultPlanName = () => {
    const date = new Date().toLocaleDateString(language === 'hebrew' ? 'he-IL' : undefined);
    return t('page.defaultPlanName').replace('{date}', date);
  };

  const handleSaveMealPlan = async (nameOverride, { activate = false } = {}) => {
    if (!selectedClient || !generatedMealPlan) {
      toast.error(t('page.selectClientFirst'));
      return;
    }
    if (activate && (!sendFrom || !sendUntil || !sendDays.length)) {
      toast.error(t('page.needDates'));
      setPublishOpen(true);
      return;
    }
    if (activate && sendUntil < sendFrom) {
      toast.error(t('page.endBeforeStart'));
      setPublishOpen(true);
      return;
    }

    // Block saves the client app can't use (empty meals, unnamed ingredients, unknown slots) — and say why.
    const validation = validateMealPlanForSave(generatedMealPlan, mealTranslations);
    if (!validation.valid) {
      const issueText = (msg) => {
        if (msg.includes('at least one ingredient')) return t('page.issueNoIngredients');
        if (msg.includes('ingredient name is required')) return t('page.issueIngredientName');
        if (msg.includes('unknown meal slot')) return t('page.issueUnknownSlot');
        if (msg.includes('main dish is required')) return t('page.issueNoMain');
        return msg;
      };
      const issues = validation.errors.map((err) => {
        const m = err.match(/^meals\[(\d+)\][^:]*:\s*(.*)$/);
        if (!m) return { index: null, text: err };
        const meal = generatedMealPlan.meals[Number(m[1])];
        return { index: Number(m[1]), text: `${translateMealNameForDisplay(meal?.meal) || ''}: ${issueText(m[2])}` };
      });
      const unique = [...new Set(issues.map((i) => i.text))];
      toast.error(t('page.cannotSaveYet'), {
        description: unique.slice(0, 3).join(' · ') + (unique.length > 3 ? ` (+${unique.length - 3})` : ''),
      });
      const first = issues.find((i) => i.index != null);
      if (first) { setActiveMealIndex(first.index); }
      return;
    }

    const mealPlanName = (typeof nameOverride === 'string' && nameOverride.trim())
      || planNameDraft.trim()
      || savedMealPlanName
      || defaultPlanName();

    if (activate) setIsSending(true);
    else setIsSavingMealPlan(true);
    try {
      const id = await persistMealPlanToDb(generatedMealPlan, { mealPlanName });
      setSavedSnapshot(snapshotOf(generatedMealPlan, mealPlanNote));
      if (activate) {
        const activeFromISO = new Date(`${sendFrom}T00:00:00`).toISOString();
        const activeUntilISO = new Date(`${sendUntil}T23:59:59`).toISOString();
        await activateMealPlan(id, activeFromISO, activeUntilISO, sendDays);
        toast.success(t('page.sentTo').replace('{name}', clientLabel()));
        setSentToClient(true);
        setPublishOpen(false);
      } else {
        setSentToClient(false);
        toast.success(t('page.mealPlanSaved'));
      }
    } catch (error) {
      console.error('Error saving meal plan:', error);
      toast.error(`${activate ? t('page.sendFailed') : t('page.mealPlanSaveError')}: ${error.message}`);
    } finally {
      setIsSavingMealPlan(false);
      setIsSending(false);
    }
  };

  const writeMainIngredients = (mealIndex, ingredients) => {
    setGeneratedMealPlan((prev) => {
      if (!prev?.meals?.[mealIndex]) return prev;
      const meals = prev.meals.map((meal, i) => {
        if (i !== mealIndex) return meal;
        return {
          ...meal,
          aiUntouched: false,
          main: {
            ...(meal.main || {}),
            ingredients,
            nutrition: sumIngredientNutrition(ingredients),
          },
        };
      });
      const totals = meals.reduce((acc, meal) => {
        const n = meal.main?.nutrition || {};
        return {
          calories: acc.calories + (n.calories || 0),
          protein: acc.protein + (n.protein || 0),
          carbs: acc.carbs + (n.carbs || 0),
          fat: acc.fat + (n.fat || 0),
        };
      }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
      return { ...prev, meals, totals };
    });
  };

  // Move one food from a meal's main dish to another meal's, with undo.
  const moveIngredient = (fromIndex, ingredientIndex, toIndex) => {
    const from = generatedMealPlan?.meals?.[fromIndex]?.main?.ingredients || [];
    const to = generatedMealPlan?.meals?.[toIndex]?.main?.ingredients || [];
    const food = from[ingredientIndex];
    if (!food || fromIndex === toIndex) return;
    writeMainIngredients(fromIndex, from.filter((_, i) => i !== ingredientIndex));
    writeMainIngredients(toIndex, [...to, food]);
    toast(t('page.foodMoved').replace('{food}', food.item || food.name || '').replace('{meal}', translateMealNameForDisplay(generatedMealPlan.meals[toIndex].meal)), {
      action: {
        label: t('page.undo'),
        onClick: () => {
          const now = generatedMealPlanRef.current;
          const target = now?.meals?.[toIndex]?.main?.ingredients || [];
          const source = now?.meals?.[fromIndex]?.main?.ingredients || [];
          if (!target.includes(food)) return;
          writeMainIngredients(toIndex, target.filter((x) => x !== food));
          writeMainIngredients(fromIndex, [...source.slice(0, ingredientIndex), food, ...source.slice(ingredientIndex)]);
        },
      },
    });
  };

  // A copy of a meal right after it, for this plan only (e.g. a second snack with the same foods).
  const duplicateMeal = (index) => {
    const meal = generatedMealPlan?.meals?.[index];
    if (!meal) return;
    const copy = JSON.parse(JSON.stringify({ ...meal, aiUntouched: false, arriving: false, planOnly: true }));
    setGeneratedMealPlan((prev) => (prev?.meals ? { ...prev, meals: [...prev.meals.slice(0, index + 1), copy, ...prev.meals.slice(index + 1)] } : prev));
    setActiveMealIndex(index + 1);
    toast(t('page.mealDuplicated').replace('{meal}', translateMealNameForDisplay(meal.meal)), {
      action: { label: t('page.undo'), onClick: () => setGeneratedMealPlan((p) => (p?.meals ? { ...p, meals: p.meals.filter((m) => m !== copy) } : p)) },
    });
  };

  generatedMealPlanRef.current = generatedMealPlan;

  const handleIngredientGrams = (mealIndex, ingredientIndex, grams) => {
    const ingredients = generatedMealPlan?.meals?.[mealIndex]?.main?.ingredients;
    if (!ingredients?.[ingredientIndex]) return;
    const next = ingredients.map((ing, i) => (
      i === ingredientIndex ? scaleIngredientToGrams(ing, grams) : ing
    ));
    writeMainIngredients(mealIndex, next);
  };

  const handleRebalanceMeal = (mealIndex) => {
    const meal = generatedMealPlan?.meals?.[mealIndex];
    const ingredients = meal?.main?.ingredients;
    if (!ingredients?.length) return;
    const slot = slotTargetFor(meal, mealIndex);
    if (!slot?.calories) return;
    const current = ingredients.reduce((sum, ing) => sum + (Number(ing.calories) || 0), 0);
    if (Math.abs(current - Number(slot.calories)) < 8) {
      toast(t('page.alreadyBalanced'));
      return;
    }
    const next = rebalanceIngredientList(ingredients, slot.calories);
    const gramsOf = (ing) => Number(ing?.['portionSI(gram)']) || 0;
    if (next.every((ing, i) => gramsOf(ing) === gramsOf(ingredients[i]))) {
      toast(t('page.rebalanceNoChange'));
      return;
    }
    writeMainIngredients(mealIndex, next);
  };

  const handleAlternativeGrams = (mealIndex, ingredientIndex, grams, type, altIndex) => {
    setGeneratedMealPlan((prev) => {
      if (!prev?.meals?.[mealIndex]) return prev;
      const meals = prev.meals.map((meal, i) => {
        if (i !== mealIndex) return meal;
        const dish = { ...getAltDishFromMeal(meal, type, altIndex) };
        const ingredients = (dish.ingredients || []).map((ing, idx) => (
          idx === ingredientIndex ? scaleIngredientToGrams(ing, grams) : ing
        ));
        return writeAltDishToMeal(meal, type, altIndex, {
          ...dish,
          ingredients,
          nutrition: sumIngredientNutrition(ingredients),
        });
      });
      return { ...prev, meals };
    });
  };

  const handleDiscoverAlternative = async (mealIndex, meal) => {
    const userCode = selectedClient?.user_code || selectedClient?.id;
    if (!userCode) return;

    const nutrition = meal.main?.nutrition || meal.main || {};
    const mealCalories = nutrition.calories || 0;
    const mealProtein = nutrition.protein || 0;
    const mealCarbs = nutrition.carbs || 0;
    const mealFat = nutrition.fat || 0;

    const getMealTitle = (item) =>
      item?.meal_title || item?.meal_name || item?.title || item?.meal || '';

    const mealToExcludeDetail = (item) => {
      if (!item) return null;
      const title = getMealTitle(item);
      const mainProtein = item.main_protein_source || '';
      const ingredients = (item.ingredients || [])
        .map((i) => i?.item || i?.name || '')
        .map((s) => String(s).trim())
        .filter(Boolean);
      if (!title && !mainProtein && ingredients.length === 0) return null;
      return { title, main_protein_source: mainProtein, ingredients };
    };

    const buildExcludeMealDetails = () => {
      const details = [];
      const push = (detail) => { if (detail) details.push(detail); };
      push(mealToExcludeDetail(meal.main));
      if (meal.alternative) push(mealToExcludeDetail(meal.alternative));
      (meal.alternatives || []).forEach((alt) => push(mealToExcludeDetail(alt)));
      (aiDiscoverHistory[mealIndex] || []).forEach((detail) => push(detail));
      push(mealToExcludeDetail(aiDiscoverPreviews[mealIndex]));
      const seen = new Set();
      return details.filter((d) => {
        const key = `${d.title}|${d.main_protein_source}|${d.ingredients.join(',')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    setIsDiscoverLoading((prev) => ({ ...prev, [mealIndex]: true }));
    try {
      const excludeMealDetails = buildExcludeMealDetails();
      const excludeMeals = excludeMealDetails.map((d) => d.title).filter(Boolean);

      const response = await fetch(`${API_BASE_URL}/meal-plans/generate-alternative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userCode,
          mealType: meal.meal,
          calories: mealCalories,
          protein: mealProtein,
          carbs: mealCarbs,
          fat: mealFat,
          excludeMeals,
          excludeMealDetails,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || `Request failed: ${response.status}`);
      }

      if (result?.data) {
        const normalized = normalizeAiDish(result.data, meal.meal);
        const detail = {
          title: normalized.meal_title || normalized.meal_name || '',
          main_protein_source: normalized.main_protein_source || '',
          ingredients: (normalized.ingredients || [])
            .map((i) => i?.item || i?.name || '')
            .map((s) => String(s).trim())
            .filter(Boolean),
        };
        setAiDiscoverPreviews((prev) => ({ ...prev, [mealIndex]: normalized }));
        if (detail.title || detail.main_protein_source || detail.ingredients.length) {
          setAiDiscoverHistory((prev) => ({
            ...prev,
            [mealIndex]: [...(prev[mealIndex] || []), detail],
          }));
        }
      }
    } catch (error) {
      console.error('Error generating AI alternative:', error);
      toast.error(
        language === 'hebrew'
          ? 'לא ניתן ליצור הצעה כרגע. נסה שוב.'
          : 'Could not generate a suggestion right now. Please try again.'
      );
    } finally {
      setIsDiscoverLoading((prev) => ({ ...prev, [mealIndex]: false }));
    }
  };

  const updateAiDraft = (mealIndex, updater) => {
    setAiDiscoverPreviews((prev) => {
      const current = prev[mealIndex];
      if (!current) return prev;
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [mealIndex]: next };
    });
  };

  const getMacroDiffChip = (draftNutrition, targetNutrition) => {
    if (!draftNutrition || !targetNutrition) return null;
    const isHe = language === 'hebrew';

    const metrics = [
      { key: 'calories', label: isHe ? 'קלוריות' : 'Calories', unit: 'kcal', round: Math.round },
      { key: 'protein', label: isHe ? 'חלבון' : 'Protein', unit: 'g', round: roundMacro },
      { key: 'carbs', label: isHe ? 'פחמימות' : 'Carbs', unit: 'g', round: roundMacro },
      { key: 'fat', label: isHe ? 'שומן' : 'Fat', unit: 'g', round: roundMacro },
    ];

    const outOfRange = [];
    const detailLines = [];
    let withinTarget = true;

    metrics.forEach((m) => {
      const actual = Number(draftNutrition[m.key]) || 0;
      const target = Number(targetNutrition[m.key]) || 0;
      const range = getAiMealMacroRange(target);
      const displayActual = m.round(actual);
      const displayTarget = m.round(target);
      const inRange = isWithinAiMealMacroMargin(actual, target);
      if (!inRange) withinTarget = false;

      const suffix = m.unit === 'kcal' ? ' kcal' : 'g';
      detailLines.push(
        isHe
          ? `${m.label}: ${displayActual}${suffix} (יעד ${displayTarget}, טווח ${range.min}–${range.max})`
          : `${m.label}: ${displayActual}${suffix} (target ${displayTarget}, range ${range.min}–${range.max})`
      );

      if (!inRange) {
        const diff = actual - target;
        const over = diff > 0;
        outOfRange.push({
          key: m.key,
          line: isHe
            ? `${m.label}: ${displayActual}${suffix} — ${over ? 'גבוה ב' : 'נמוך ב'} ${m.round(Math.abs(diff))}${suffix} (יעד ${displayTarget}, טווח ${range.min}–${range.max})`
            : `${m.label}: ${displayActual}${suffix} — ${over ? 'over' : 'under'} by ${m.round(Math.abs(diff))}${suffix} (target ${displayTarget}, range ${range.min}–${range.max})`,
        });
      }
    });

    return {
      withinTarget,
      label: withinTarget ? t('page.macroDiffOk') : t('page.macroDiffWarn'),
      detail: detailLines.join('\n'),
      outOfRange,
    };
  };

  const handleDiscardAiDraft = (mealIndex) => {
    setAiDiscoverPreviews((prev) => {
      const next = { ...prev };
      delete next[mealIndex];
      return next;
    });
    setEditingMealTitle((prev) =>
      prev?.mealIndex === mealIndex && prev?.type === 'aiDraft' ? null : prev
    );
  };

  // A dish becomes the meal's main dish. The old main dish isn't lost: it takes the dish's place among the
  // alternatives (or is added as one), and Undo restores the meal exactly as it was.
  //   placeOldMain(meal, oldMainDish) → the meal with the old main dish put where it belongs.
  const promoteToMain = (mealIndex, dish, placeOldMain) => {
    const meal = generatedMealPlan?.meals?.[mealIndex];
    if (!meal || !dish?.ingredients?.length) return;
    const englishMealName = normalizeMealNameForSave(meal.meal, mealTranslations.english, mealTranslations.hebrew);
    const oldMain = meal.main?.ingredients?.length ? formatDishForMealPlanDb(meal.main, englishMealName) : null;
    const withOld = placeOldMain(meal, oldMain);
    const newMain = { ...formatDishForMealPlanDb(dish, englishMealName), nutrition: sumIngredientNutrition(dish.ingredients) };
    const nextMeal = normalizeMealAlternativeSlots({ ...withOld, main: newMain, aiUntouched: false });
    const withTotals = (meals) => meals.reduce((acc, m) => {
      const n = m.main?.nutrition || {};
      return { calories: acc.calories + (n.calories || 0), protein: acc.protein + (n.protein || 0), carbs: acc.carbs + (n.carbs || 0), fat: acc.fat + (n.fat || 0) };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
    const setMealAt = (value) => setGeneratedMealPlan((prev) => {
      if (!prev?.meals?.[mealIndex]) return prev;
      const meals = prev.meals.map((m, i) => (i === mealIndex ? value : m));
      return { ...prev, meals, totals: withTotals(meals) };
    });
    setMealAt(nextMeal);
    setActiveMealIndex(mealIndex);
    toast(t(oldMain ? 'page.mainReplaced' : 'page.mainSet').replace('{dish}', newMain.meal_title), {
      action: { label: t('page.undo'), onClick: () => setMealAt(meal) },
    });
  };

  // The AI suggestion replaces the main dish; the old main dish is kept as an alternative.
  const handleUseAiDraftAsMain = (mealIndex) => {
    const draft = aiDiscoverPreviews[mealIndex];
    if (!draft) return;
    const title = (draft.meal_title || draft.meal_name || '').trim();
    if (!title) { toast.error(t('page.draftNeedsTitle')); return; }
    if (!draft.ingredients?.length) { toast.error(t('page.draftNeedsIngredients')); return; }
    promoteToMain(mealIndex, { ...draft, meal_title: title }, (meal, oldMain) => {
      if (!oldMain) return { ...meal };
      return meal.alternative ? { ...meal, alternatives: [...(meal.alternatives || []), oldMain] } : { ...meal, alternative: oldMain };
    });
    handleDiscardAiDraft(mealIndex);
  };

  // An alternative and the main dish swap places.
  const handleMakeAlternativeMain = (mealIndex, dishType, altIndex) => {
    const meal = generatedMealPlan?.meals?.[mealIndex];
    const dish = meal && getAltDishFromMeal(meal, dishType, altIndex);
    if (!dish) return;
    promoteToMain(mealIndex, dish, (m, oldMain) => (oldMain ? writeAltDishToMeal(m, dishType, altIndex, oldMain) : removeAltDishFromMeal(m, dishType, altIndex)));
  };

  const handleApplyAiDraftToPlan = async (mealIndex) => {
    const draft = aiDiscoverPreviews[mealIndex];
    if (!draft || !generatedMealPlan) return;

    const title = (draft.meal_title || draft.meal_name || '').trim();
    if (!title) {
      toast.error(t('page.draftNeedsTitle'));
      return;
    }
    if (!draft.ingredients?.length) {
      toast.error(t('page.draftNeedsIngredients'));
      return;
    }

    const updatedPlanData = { ...generatedMealPlan };
    const updatedMeals = [...updatedPlanData.meals];
    const mealToUpdate = { ...updatedMeals[mealIndex] };
    const englishMealName = normalizeMealNameForSave(
      mealToUpdate.meal,
      mealTranslations.english,
      mealTranslations.hebrew
    );
    const dishToAdd = formatDishForMealPlanDb(
      {
        ...draft,
        meal_title: title,
        nutrition: sumIngredientNutrition(draft.ingredients),
      },
      englishMealName
    );

    if (mealToUpdate.alternative) {
      mealToUpdate.alternatives = [...(mealToUpdate.alternatives || []), dishToAdd];
    } else {
      mealToUpdate.alternative = dishToAdd;
    }
    updatedMeals[mealIndex] = normalizeMealAlternativeSlots(mealToUpdate);
    updatedPlanData.meals = updatedMeals;

    setGeneratedMealPlan(updatedPlanData);
    handleDiscardAiDraft(mealIndex);
    setActiveMealIndex(mealIndex);
  };

  const handleAiDraftTitleChange = (mealIndex, newTitle) => {
    updateAiDraft(mealIndex, (draft) => ({
      ...draft,
      meal_title: newTitle || draft.meal_title || draft.meal_name || '',
    }));
    setEditingMealTitle(null);
  };

  const handleOpenAddAiDraftIngredient = (mealIndex) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAiDraftIngredientModalVisible(true);
  };

  const handleReplaceAiDraftIngredient = (mealIndex, ingredientIndex) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddAiDraftIngredientModalVisible(true);
  };

  const handleAddAiDraftIngredient = (ingredient) => {
    if (selectedAiDraftMealIndex === null) return;
    const mealIndex = selectedAiDraftMealIndex;
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;
      if (replacing) {
        ingredients[replacingIngredientIndex] = ingredient;
      } else {
        ingredients.push(ingredient);
      }
      return {
        ...draft,
        ingredients,
        nutrition: sumIngredientNutrition(ingredients),
      };
    });
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAiDraftIngredientModalVisible(false);
    setSelectedAiDraftMealIndex(null);
  };

  const handleEditAiDraftIngredient = (mealIndex, ingredientIndex, ingredient) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditAiDraftPortionModalVisible(true);
  };

  const handleUpdateAiDraftIngredientPortion = ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedAiDraftMealIndex === null || editingIngredientIndex === null || !editingIngredient) return;
    const mealIndex = selectedAiDraftMealIndex;
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      const oldIngredient = ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;
      const newScale = quantityNum / 100;
      ingredients[editingIngredientIndex] = {
        ...oldIngredient,
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };
      return { ...draft, ingredients, nutrition: sumIngredientNutrition(ingredients) };
    });
    setIsEditAiDraftPortionModalVisible(false);
    setEditingIngredient(null);
    setEditingIngredientIndex(null);
    setSelectedAiDraftMealIndex(null);
  };

  const handleAiDraftGrams = (mealIndex, ingredientIndex, grams) => {
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = (draft.ingredients || []).map((ing, i) => (
        i === ingredientIndex ? scaleIngredientToGrams(ing, grams) : ing
      ));
      return { ...draft, ingredients, nutrition: sumIngredientNutrition(ingredients) };
    });
  };

  const handleDeleteAiDraftIngredient = (mealIndex, ingredientIndex) => {
    const previous = (aiDiscoverPreviews[mealIndex]?.ingredients || []).map((ing) => ({ ...ing }));
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      ingredients.splice(ingredientIndex, 1);
      return { ...draft, ingredients, nutrition: sumIngredientNutrition(ingredients) };
    });
    toast(t('page.ingredientRemoved'), {
      action: {
        label: t('page.undo'),
        onClick: () => updateAiDraft(mealIndex, (draft) => ({
          ...draft,
          ingredients: previous,
          nutrition: sumIngredientNutrition(previous),
        })),
      },
    });
  };

  const renderAiDraftEditor = (mealIndex, meal, preview) => {
    if (!preview) return null;
    const slot = slotTargetFor(meal, mealIndex);
    const targetNutrition = slot
      ? { calories: slot.calories, protein: slot.protein, carbs: slot.carbs, fat: slot.fat }
      : (meal.main?.nutrition || meal.main || {});
    const macroChip = getMacroDiffChip(preview.nutrition, targetNutrition);

    return (
      <div className="mt-4 rounded-lg border border-line dark:border-line-dark bg-moss/40 dark:bg-moss-dark/40 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            {editingMealTitle?.mealIndex === mealIndex && editingMealTitle?.type === 'aiDraft' ? (
              <input
                type="text"
                className={themeClasses.inputField + ' !text-base !font-semibold !py-1.5 !px-3'}
                defaultValue={preview.meal_title || preview.meal_name || t('page.aiSuggestion')}
                autoFocus
                onBlur={(e) => handleAiDraftTitleChange(mealIndex, e.target.value.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                  else if (e.key === 'Escape') setEditingMealTitle(null);
                }}
              />
            ) : (
              <h5 className={`${themeClasses.textPrimary} text-base font-semibold inline-flex items-center gap-2`}>
                {preview.meal_title || preview.meal_name || t('page.aiSuggestion')}
                <button
                  type="button"
                  onClick={() => setEditingMealTitle({ mealIndex, type: 'aiDraft' })}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-muted hover:bg-moss hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:hover:bg-moss-dark"
                  aria-label={language === 'hebrew' ? 'שינוי שם המנה' : 'Rename dish'}
                >
                  <Edit2 size={14} aria-hidden="true" />
                </button>
              </h5>
            )}
            {preview.main_protein_source && (
              <p className={`${themeClasses.textMuted} text-xs mt-1`}>
                {t('page.mainProtein')}: {preview.main_protein_source}
              </p>
            )}
          </div>
          <span className="inline-flex items-center gap-1 rounded border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-1.5 py-0.5 text-[11px] font-medium text-forest-ondark dark:text-forest">
            <Sparkles size={11} aria-hidden="true" />
            {t('page.aiDraftBadge')}
          </span>
        </div>

        {preview.nutrition && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 text-sm font-bold">
              <span className="text-ink dark:text-ink-dark tabular-nums">{Math.round(preview.nutrition.calories || 0)} <span className="font-medium text-ink-muted text-[11px]">kcal</span></span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-moss" />
              <span className="text-ink dark:text-ink-dark tabular-nums">P {roundMacro(preview.nutrition.protein)}g</span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-moss" />
              <span className="text-ink dark:text-ink-dark tabular-nums">C {roundMacro(preview.nutrition.carbs)}g</span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-moss" />
              <span className="text-ink dark:text-ink-dark tabular-nums">F {roundMacro(preview.nutrition.fat)}g</span>
              {macroChip?.withinTarget && (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-lg border border-forest/40 text-forest-ondark bg-forest-faint dark:border-forest/40 dark:text-forest dark:bg-forest/15"
                  title={macroChip.detail}
                >
                  {macroChip.label} · {t('page.macroDiffFromTarget')}
                </span>
              )}
            </div>
            {macroChip && !macroChip.withinTarget && (
              <div
                className="mt-2 rounded-lg border border-amber-700/20 bg-amber-700/5 px-3 py-2 text-xs dark:border-amber-400/20 dark:bg-amber-400/10"
                title={macroChip.detail}
              >
                <div className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                  {t('page.macroDiffWarn')} · {t('page.macroDiffFromTarget')}
                </div>
                <ul className="space-y-1 text-amber-700 dark:text-amber-400">
                  {macroChip.outOfRange.map((item) => (
                    <li key={item.key} className="flex items-start gap-1.5">
                      <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                      <span>{item.line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h4 className={`${themeClasses.textPrimary} text-sm font-semibold`}>{t('page.ingredients')}</h4>
              {preview.ingredients?.length > 0 && (
                <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-moss text-ink-muted' : 'bg-moss text-ink-muted'}`}>
                  {preview.ingredients.length} {t('page.items')}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleOpenAddAiDraftIngredient(mealIndex)}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 text-xs font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
            >
              <Plus size={14} aria-hidden="true" /> {t('page.addIngredient')}
            </button>
          </div>
          <IngredientTable
            ingredients={preview.ingredients}
            language={language}
            onGramsChange={(i, grams) => handleAiDraftGrams(mealIndex, i, grams)}
            onReplace={(i) => handleReplaceAiDraftIngredient(mealIndex, i)}
            onEdit={(i, ing) => handleEditAiDraftIngredient(mealIndex, i, ing)}
            onDelete={(i) => handleDeleteAiDraftIngredient(mealIndex, i)}
          />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={() => handleApplyAiDraftToPlan(mealIndex)}
            disabled={!!isApplyingAiDraft[mealIndex]}
            className="px-5 py-2.5 rounded-lg bg-forest-ondark hover:bg-forest-ondark/90 text-white dark:text-canvas-dark font-semibold text-sm  flex items-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
          >
            {isApplyingAiDraft[mealIndex] ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <CheckCircle size={16} />
            )}
            {isApplyingAiDraft[mealIndex] ? t('page.addToPlanSaving') : t('page.addToPlan')}
          </button>
          <button
            type="button"
            onClick={() => handleUseAiDraftAsMain(mealIndex)}
            disabled={!!isApplyingAiDraft[mealIndex] || !preview.ingredients?.length}
            className={themeClasses.btnSecondary + ' !py-2 !px-4 !text-sm'}
          >
            <RefreshCw size={14} aria-hidden="true" /> {t('page.useAsMain')}
          </button>
          <button
            type="button"
            onClick={() => handleDiscardAiDraft(mealIndex)}
            className={themeClasses.btnSecondary + ' !py-2 !px-4 !text-sm'}
          >
            <X size={14} /> {t('page.discardDraft')}
          </button>
        </div>
      </div>
    );
  };

  // --- INGREDIENT HANDLERS ---
  const handleOpenAddIngredient = (mealIndex) => {
    setSelectedMealIndex(mealIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddIngredientModalVisible(true);
  };

  const handleReplaceIngredient = (mealIndex, ingredientIndex) => {
    setSelectedMealIndex(mealIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddIngredientModalVisible(true);
  };

  const handleAddIngredient = async (ingredient) => {
    if (selectedMealIndex === null || !generatedMealPlan) return;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      const mealToUpdate = { ...updatedMeals[selectedMealIndex] };

      if (!mealToUpdate.main) mealToUpdate.main = {};
      if (!mealToUpdate.main.ingredients) mealToUpdate.main.ingredients = [];

      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;

      if (replacing) {
        mealToUpdate.main.ingredients[replacingIngredientIndex] = ingredient;
        mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
          (acc, ing) => ({
            calories: acc.calories + (ing.calories || 0),
            protein: acc.protein + (ing.protein || 0),
            carbs: acc.carbs + (ing.carbs || 0),
            fat: acc.fat + (ing.fat || 0),
          }),
          { calories: 0, protein: 0, carbs: 0, fat: 0 }
        );
        setIsReplacingIngredient(false);
        setReplacingIngredientIndex(null);
      } else {
        mealToUpdate.main.ingredients = [...mealToUpdate.main.ingredients, ingredient];
        const currentNutrition = mealToUpdate.main.nutrition || {};
        mealToUpdate.main.nutrition = {
          calories: (currentNutrition.calories || 0) + ingredient.calories,
          protein: (currentNutrition.protein || 0) + ingredient.protein,
          carbs: (currentNutrition.carbs || 0) + ingredient.carbs,
          fat: (currentNutrition.fat || 0) + ingredient.fat,
        };
      }

      updatedMeals[selectedMealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.totals = newTotals;
      setGeneratedMealPlan(updatedPlanData);
    } catch (error) {
      console.error('Error adding/replacing ingredient:', error);
    }
  };

  const handleEditIngredient = (mealIndex, ingredientIndex, ingredient) => {
    setSelectedMealIndex(mealIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditPortionModalVisible(true);
  };

  const handleUpdateIngredientPortion = async ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedMealIndex === null || editingIngredientIndex === null || !generatedMealPlan || !editingIngredient) return;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      const mealToUpdate = { ...updatedMeals[selectedMealIndex] };

      const oldIngredient = mealToUpdate.main.ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;

      const newScale = quantityNum / 100;
      const updatedIngredient = {
        ...oldIngredient,
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };

      mealToUpdate.main.ingredients[editingIngredientIndex] = updatedIngredient;
      mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
        (acc, ing) => ({
          calories: acc.calories + (ing.calories || 0),
          protein: acc.protein + (ing.protein || 0),
          carbs: acc.carbs + (ing.carbs || 0),
          fat: acc.fat + (ing.fat || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedMeals[selectedMealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.totals = newTotals;
      setGeneratedMealPlan(updatedPlanData);
      setIsEditPortionModalVisible(false);
      setEditingIngredient(null);
      setEditingIngredientIndex(null);
    } catch (error) {
      console.error('Error updating ingredient:', error);
    }
  };

  const handleDeleteIngredient = async (mealIndex, ingredientIndex) => {
    if (!generatedMealPlan) return;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      const mealToUpdate = { ...updatedMeals[mealIndex] };
      const previousIngredients = mealToUpdate.main.ingredients.map((ing) => ({ ...ing }));

      mealToUpdate.main.ingredients.splice(ingredientIndex, 1);
      mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
        (acc, ing) => ({
          calories: acc.calories + (ing.calories || 0),
          protein: acc.protein + (ing.protein || 0),
          carbs: acc.carbs + (ing.carbs || 0),
          fat: acc.fat + (ing.fat || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedMeals[mealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.totals = newTotals;
      setGeneratedMealPlan(updatedPlanData);
      toast(t('page.ingredientRemoved'), {
        action: { label: t('page.undo'), onClick: () => writeMainIngredients(mealIndex, previousIngredients) },
      });
    } catch (error) {
      console.error('Error deleting ingredient:', error);
    }
  };

  // --- ALTERNATIVE MEAL INGREDIENT HANDLERS ---
  const handleDeleteAlternativeDish = async (mealIndex, type = 'alternative', altIndex = null) => {
    if (!generatedMealPlan) return;

    const meal = generatedMealPlan.meals[mealIndex];
    if (countAlternativeDishes(meal) <= 1) {
      toast.error(t('page.cannotDeleteLastAlternative'));
      return;
    }

    const previousMeal = generatedMealPlan.meals[mealIndex];
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      updatedMeals[mealIndex] = normalizeMealAlternativeSlots(
        removeAltDishFromMeal(
          { ...updatedMeals[mealIndex] },
          type,
          altIndex
        )
      );
      updatedPlanData.meals = updatedMeals;
      setGeneratedMealPlan(updatedPlanData);
      toast(t('page.alternativeRemoved'), {
        action: {
          label: t('page.undo'),
          onClick: () => setGeneratedMealPlan((prev) => {
            if (!prev?.meals) return prev;
            const meals = prev.meals.map((item, i) => (i === mealIndex ? previousMeal : item));
            return { ...prev, meals };
          }),
        },
      });
    } catch (error) {
      console.error('Error deleting alternative meal:', error);
      toast.error(`${t('page.addToPlanSaveError')}: ${error.message}`);
    }
  };

  const handleOpenAddAlternativeIngredient = (mealIndex, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAlternativeIngredientModalVisible(true);
  };

  const handleAddAlternativeIngredient = async (ingredient) => {
    if (selectedAlternativeMealIndex === null || !generatedMealPlan) return;
    const type = selectedAlternativeType || 'alternative';
    const altIndex = selectedAlternativeIndex;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      let mealToUpdate = { ...updatedMeals[selectedAlternativeMealIndex] };

      let dish = getAltDishFromMeal(mealToUpdate, type, altIndex);
      if (!dish) {
        dish = { ingredients: [], nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 } };
      } else {
        dish = { ...dish };
      }
      if (!dish.ingredients) dish.ingredients = [];

      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;
      if (replacing) {
        dish.ingredients = [...dish.ingredients];
        dish.ingredients[replacingIngredientIndex] = ingredient;
      } else {
        dish.ingredients = [...dish.ingredients, ingredient];
      }

      dish.nutrition = sumIngredientNutrition(dish.ingredients);
      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);

      setIsReplacingIngredient(false);
      setReplacingIngredientIndex(null);

      updatedMeals[selectedAlternativeMealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;
      setGeneratedMealPlan(updatedPlanData);
    } catch (error) {
      console.error('Error adding/replacing alternative ingredient:', error);
    }
  };

  const handleEditAlternativeIngredient = (mealIndex, ingredientIndex, ingredient, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditAlternativePortionModalVisible(true);
  };

  const handleUpdateAlternativeIngredientPortion = async ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedAlternativeMealIndex === null || editingIngredientIndex === null || !generatedMealPlan || !editingIngredient) return;
    const type = selectedAlternativeType || 'alternative';
    const altIndex = selectedAlternativeIndex;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      let mealToUpdate = { ...updatedMeals[selectedAlternativeMealIndex] };

      const dish = { ...getAltDishFromMeal(mealToUpdate, type, altIndex) };
      const oldIngredient = dish.ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;

      const newScale = quantityNum / 100;
      const updatedIngredient = {
        ...oldIngredient,
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };

      const ingredients = [...dish.ingredients];
      ingredients[editingIngredientIndex] = updatedIngredient;
      dish.ingredients = ingredients;
      dish.nutrition = sumIngredientNutrition(ingredients);

      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);
      updatedMeals[selectedAlternativeMealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;
      setGeneratedMealPlan(updatedPlanData);
      setIsEditAlternativePortionModalVisible(false);
      setEditingIngredient(null);
      setEditingIngredientIndex(null);
      setSelectedAlternativeMealIndex(null);
      setSelectedAlternativeType(null);
      setSelectedAlternativeIndex(null);
    } catch (error) {
      console.error('Error updating alternative ingredient:', error);
    }
  };

  const handleDeleteAlternativeIngredient = async (mealIndex, ingredientIndex, type = 'alternative', altIndex = null) => {
    if (!generatedMealPlan) return;
    try {
      const updatedPlanData = { ...generatedMealPlan };
      const updatedMeals = [...updatedPlanData.meals];
      let mealToUpdate = { ...updatedMeals[mealIndex] };
      const dish = { ...getAltDishFromMeal(mealToUpdate, type, altIndex) };
      const previousIngredients = (dish.ingredients || []).map((ing) => ({ ...ing }));
      dish.ingredients = [...dish.ingredients];
      dish.ingredients.splice(ingredientIndex, 1);
      dish.nutrition = sumIngredientNutrition(dish.ingredients);
      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);
      updatedMeals[mealIndex] = mealToUpdate;
      updatedPlanData.meals = updatedMeals;
      setGeneratedMealPlan(updatedPlanData);
      toast(t('page.ingredientRemoved'), {
        action: {
          label: t('page.undo'),
          onClick: () => setGeneratedMealPlan((prev) => {
            if (!prev?.meals?.[mealIndex]) return prev;
            const meals = prev.meals.map((meal, i) => {
              if (i !== mealIndex) return meal;
              const current = { ...getAltDishFromMeal(meal, type, altIndex) };
              return writeAltDishToMeal(meal, type, altIndex, {
                ...current,
                ingredients: previousIngredients,
                nutrition: sumIngredientNutrition(previousIngredients),
              });
            });
            return { ...prev, meals };
          }),
        },
      });
    } catch (error) {
      console.error('Error deleting alternative ingredient:', error);
    }
  };

  const handleReplaceAlternativeIngredient = (mealIndex, ingredientIndex, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddAlternativeIngredientModalVisible(true);
  };

  const handleMealTitleChange = (mealIndex, type, newTitle, altIndex = null) => {
    if (!generatedMealPlan?.meals) return;
    const updatedPlanData = { ...generatedMealPlan };
    const updatedMeals = [...updatedPlanData.meals];
    const mealToUpdate = { ...updatedMeals[mealIndex] };
    if (type === 'main' && mealToUpdate.main) {
      mealToUpdate.main = { ...mealToUpdate.main, meal_title: newTitle || mealToUpdate.main.meal_title };
    } else if (type === 'alternative' && mealToUpdate.alternative) {
      mealToUpdate.alternative = { ...mealToUpdate.alternative, meal_title: newTitle || mealToUpdate.alternative.meal_title };
    } else if (type === 'alternatives' && altIndex != null && mealToUpdate.alternatives?.[altIndex]) {
      const alternatives = [...mealToUpdate.alternatives];
      alternatives[altIndex] = { ...alternatives[altIndex], meal_title: newTitle || alternatives[altIndex].meal_title };
      mealToUpdate.alternatives = alternatives;
    }
    updatedMeals[mealIndex] = mealToUpdate;
    updatedPlanData.meals = updatedMeals;
    setGeneratedMealPlan(updatedPlanData);
    setEditingMealTitle(null);
  };

  // --- RENDER HELPERS ---
  const canDraft = targetsSaved && dayTargets.calories > 0 && slots.length > 0 && !isGenerating;

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  const PILL = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap';
  const PILL_TONE = {
    forest: 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark dark:text-forest',
    progress: 'bg-moss text-ink dark:bg-moss-dark dark:text-ink-dark',
    warning: 'bg-amber-700/10 text-amber-700 dark:bg-amber-400/15 dark:text-amber-400',
    danger: 'bg-red-700/10 text-red-700 dark:bg-red-400/15 dark:text-red-400',
    neutral: 'bg-moss text-ink dark:bg-moss-dark dark:text-ink-dark',
  };
  const renderPill = (tone, Icon, label) => (
    <span className={`${PILL} ${PILL_TONE[tone]}`}>
      {Icon && <Icon size={12} aria-hidden="true" />}
      {label}
    </span>
  );
  // Target vs actual: tolerance is ±5% for the day, ±10% per meal.
  const renderReconcilePill = (actual, target, tolerance, unit) => {
    const r = reconcile(actual, target, tolerance);
    if (r.status === 'none') return null;
    if (r.status === 'on') return renderPill('forest', CheckCircle, t('page.onTarget'));
    const n = `${Math.round(Math.abs(r.delta))}${unit}`;
    if (r.status === 'over') return renderPill('warning', AlertTriangle, t('page.overBy').replace('{n}', n));
    return renderPill('warning', ArrowDown, t('page.underBy').replace('{n}', n));
  };
  const renderAiNote = () => (aiNote ? (
    <div className="mt-2 rounded-lg border border-line dark:border-line-dark bg-moss/60 dark:bg-moss-dark/60 p-3 text-[13px]">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-forest-ondark dark:text-forest">
        <Sparkles size={12} aria-hidden="true" /> {t('page.aiNoteTitle')}
      </div>
      <p className="whitespace-pre-line text-ink dark:text-ink-dark">{aiNote}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => { setMealPlanNote((prev) => (prev && prev.trim() ? `${prev.trim()}\n\n${aiNote}` : aiNote)); setAiNote(''); }}
          className="min-h-[36px] rounded-md border border-line dark:border-line-dark px-3 text-xs font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors"
        >
          {t('page.addToNotes')}
        </button>
        <button type="button" onClick={() => setAiNote('')} className="min-h-[36px] rounded-md px-3 text-xs font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors">
          {t('page.dismiss')}
        </button>
      </div>
    </div>
  ) : null);

  const planHasFood = !!generatedMealPlan?.meals?.some((m) => m.main?.ingredients?.length);
  // Step 4 replaces the meals on screen, so it opens at the top.
  const openReview = () => {
    setPublishOpen(true);
    requestAnimationFrame(() => {
      document.getElementById('plan-main')?.scrollTo({ top: 0 });
      document.getElementById('step-title')?.focus({ preventScroll: true });
    });
  };
  const closeReview = () => {
    setPublishOpen(false);
    requestAnimationFrame(() => document.getElementById('plan-main')?.scrollTo({ top: 0 }));
  };
  const clientName = selectedClient?.full_name || selectedClient?.name || selectedClient?.user_code || '';
  // One screen at a time, in the order of the job.
  const screen = isLoadingData ? 'loading'
    : loadError ? 'error'
      : generatedMealPlan?.meals ? 'builder'
        : 'targets';
  // The four steps: 1 starting point → 2 structure → 3 meals → 4 review & send. A saved plan skips step 2.
  const step = generatedMealPlan?.meals ? (publishOpen ? 4 : 3) : (startMethod === 'ai' ? 2 : 1);
  const quietBtn = 'inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 text-sm font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:cursor-not-allowed disabled:opacity-50';
  const primaryBtn = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-forest-ondark px-5 text-sm font-bold text-white hover:bg-forest-ondark/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover dark:focus-visible:ring-offset-canvas-dark';
  // A number field that reads as plain text until hovered or focused.
  const quietNumber = 'no-spin min-h-10 rounded-md border border-line bg-transparent px-2 text-end font-semibold tabular-nums text-ink hover:border-ink-muted/60 focus:border-forest/50 focus:bg-surface focus:outline-none focus:ring-2 focus:ring-forest/20 disabled:opacity-50 dark:border-line-dark dark:text-ink-dark dark:hover:border-ink-muted-dark/60 dark:focus:bg-canvas-dark';
  const busyTargets = isSavingTargets || isGenerating;
  const canStart = !!work && targetsComplete && mealsValid && !busyTargets && !isSavingBrief;
  const canStartAi = canStart && work.slots.length > 0;
  const startReason = !work ? '' : !targetsComplete ? t('page.setTargetsToStart')
    : !mealsValid ? t('page.mealsRange')
      : startMethod === 'ai' && !work.slots.length ? t('page.draftNeedsSlots') : '';
  const profileLine = (() => {
    const p = clientProfile || {};
    return [p.weight_kg && `${p.weight_kg} kg`, p.height_cm && `${p.height_cm} cm`, p.age && `${p.age}`, p.Activity_level || p.activity_level, p.goal]
      .filter(Boolean).join(' · ');
  })();
  // What the client must not get, as the AI and the coach both read it.
  const avoidList = [...new Set([...brief.allergies, ...brief.limitations])];

  // Step 1: a starting point. AI and by hand go on to the structure; a saved plan opens its picker.
  const chooseStart = async (kind) => {
    if (kind === 'manual') {
      if (!(await saveNotes())) return;
      setStartMethod('manual');
      startManual(savedTargets);
      return;
    }
    setStartMethod(kind);
    if (kind === 'copy') openCopyPicker();
    requestAnimationFrame(() => {
      document.getElementById('plan-main')?.scrollTo({ top: 0 });
      document.getElementById('step-title')?.focus({ preventScroll: true });
    });
  };
  const backToStart = () => {
    setStartMethod(null);
    setCopyOpen(false);
    requestAnimationFrame(() => document.getElementById('step-title')?.focus());
  };

  const renderStepper = () => {
    const steps = [
      { n: 1, label: t('page.stepStart') },
      { n: 2, label: t('page.stepStructure') },
      { n: 3, label: t('page.stepMeals') },
      { n: 4, label: t('page.stepSend') },
    ];
    // Only moves that lose nothing: back to step 1 before a plan exists, and between meals and review.
    const go = {
      1: step === 2 ? backToStart : null,
      3: step === 4 ? closeReview : null,
      4: step === 3 && planHasFood && !isGenerating ? openReview : null,
    };
    return (
      <nav aria-label={t('page.stepsNav')} className="min-w-0 flex-1 overflow-x-auto no-scrollbar">
        <p className="flex min-w-0 items-center gap-2 text-[13px] sm:hidden" aria-current="step">
          <span className="shrink-0 rounded-full bg-forest-faint px-2 py-0.5 text-[11px] font-semibold tabular-nums text-forest-ondark dark:bg-forest-faint-dark dark:text-forest">
            {t('page.stepOf').replace('{n}', step).replace('{total}', steps.length)}
          </span>
          <span className="truncate font-semibold text-ink dark:text-ink-dark">{steps[step - 1]?.label}</span>
        </p>
        <ol className="mx-auto hidden w-max items-center gap-1 sm:flex">
          {steps.map(({ n, label }) => {
            const current = n === step;
            // A plan built by hand or from a saved plan never used step 2: it reads as skipped, not done.
            const skipped = n === 2 && step > 2 && generatedMealPlan?.origin !== 'ai' && startMethod !== 'ai';
            const done = n < step && !skipped;
            const onClick = go[n];
            return (
              <li key={n} className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={onClick || undefined}
                  disabled={!onClick}
                  aria-current={current ? 'step' : undefined}
                  className={`inline-flex min-h-9 items-center gap-2 rounded-md px-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest ${onClick ? 'hover:bg-moss dark:hover:bg-moss-dark' : 'cursor-default'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold tabular-nums ${done
                      ? 'bg-forest-ondark text-white dark:bg-forest dark:text-canvas-dark'
                      : skipped
                        ? 'border border-dashed border-line text-ink-muted/60 dark:border-line-dark dark:text-ink-muted-dark/60'
                        : current
                        ? 'border-2 border-forest-ondark text-forest-ondark dark:border-forest dark:text-forest'
                        : 'border border-line text-ink-muted dark:border-line-dark dark:text-ink-muted-dark'}`}
                  >
                    {done ? <CheckCircle size={14} /> : n}
                  </span>
                  <span className={`${current ? 'font-semibold text-ink dark:text-ink-dark' : `hidden font-medium md:inline ${skipped ? 'text-ink-muted/60 line-through dark:text-ink-muted-dark/60' : 'text-ink-muted dark:text-ink-muted-dark'}`}`}>{label}</span>
                </button>
                {n < steps.length && <span aria-hidden="true" className={`h-px w-4 sm:w-8 ${done ? 'bg-forest-ondark dark:bg-forest' : 'bg-line dark:bg-line-dark'}`} />}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  };

  // Every step opens with what it is for, in one sentence.
  const renderStepIntro = (title, body, extra = null) => (
    <div className="mb-5">
      <h2 id="step-title" tabIndex={-1} className="text-xl font-semibold tracking-tight text-ink focus:outline-none dark:text-ink-dark">{title}</h2>
      {body && <p className="mt-1 max-w-2xl text-sm text-ink-muted dark:text-ink-muted-dark">{body}</p>}
      {extra}
    </div>
  );

  // The structure the plan is built on: the day's calories, its macro balance, and each meal's share of the day.
  // compact = the builder's structure strip.
  const renderStructure = ({ compact = false } = {}) => {
    if (!work) return null;
    const missing = !targetsSaved;
    const pad = compact ? 'px-4' : 'px-5 sm:px-6';
    const cellPad = compact ? 'first:ps-4 last:pe-4' : 'first:ps-5 last:pe-5 sm:first:ps-6 sm:last:pe-6';
    const idp = compact ? 'strip' : 'goals';
    const onboardingDay = !missing && onboardingValues ? onboardingValues.day : null;
    const bodyKg = Number(weightTrend?.kg) || Number(clientProfile?.weight_kg) || 0;
    const dayKcal = workDay.calories || 0;
    const mealSum = work.slots.reduce((sum, s) => sum + (parseGrams(s.targetCalories) || 0), 0);
    const gap = dayKcal - mealSum;
    const splitOff = dayKcal > 0 && work.slots.length > 0 && Math.abs(gap) > dayKcal * DAY_TOLERANCE;
    const totalPct = dayKcal > 0 ? Math.round((mealSum / dayKcal) * 100) : 0;
    const showCustom = customMacros || (targetsComplete && !matchingPreset);
    const sourceText = !targetsSaved ? t('page.noTargets') : !onboardingValues ? t('page.setByCoach') : changedByCoach ? t('page.changedByCoach') : t('page.fromOnboarding');
    const macroKcal = (p, c, fat) => Math.round((Number(p) || 0) * 4 + (Number(c) || 0) * 4 + (Number(fat) || 0) * 9);
    const flags = [];
    const dayFromMacros = macroKcal(workDay.protein, workDay.carbs, workDay.fats);
    if (showCustom && dayKcal > 0 && dayFromMacros > 0 && Math.abs(dayFromMacros - dayKcal) > dayKcal * DAY_TOLERANCE) {
      flags.push(t('page.dayMacrosOff').replace('{sum}', dayFromMacros.toLocaleString()).replace('{kcal}', dayKcal.toLocaleString()));
    }
    if (advancedSplit) {
      work.slots.forEach((slot) => {
        const kcal = parseGrams(slot.targetCalories);
        const m = slot.targetMacros;
        if (!m || !(kcal > 0)) return;
        const sum = macroKcal(m.protein, m.carbs, m.fats);
        if (sum > 0 && Math.abs(sum - kcal) > kcal * MEAL_TOLERANCE) {
          flags.push(t('page.slotMacrosOff').replace('{meal}', slotLabel(slot.mealSlot)).replace('{sum}', sum.toLocaleString()).replace('{kcal}', kcal.toLocaleString()));
        }
      });
    }
    const fieldLabel = 'block text-[12px] font-medium text-ink-muted dark:text-ink-muted-dark';
    const hint = 'text-[12px] text-ink-muted dark:text-ink-muted-dark';
    const segBtn = (on) => `min-h-9 rounded-md px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-50 ${on
      ? 'bg-surface text-ink shadow-sm ring-1 ring-line dark:bg-surface-dark dark:text-ink-dark dark:ring-line-dark'
      : 'text-ink-muted hover:text-ink dark:text-ink-muted-dark dark:hover:text-ink-dark'}`;
    // Row icons stay out of sight until the row is hovered or focused (always shown on touch-size screens).
    const rowIcon = 'inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-moss hover:text-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-30 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark';
    const ghostInput = 'rounded-md border border-transparent bg-transparent hover:border-line focus:border-line focus:outline-none focus:ring-2 focus:ring-forest/20 dark:hover:border-line-dark';
    const th = `py-2 font-medium ${cellPad}`;
    const td = `py-1.5 align-middle ${cellPad}`;

    return (
      <div>
        {/* The day: calories and their balance, side by side; the grams they give in one quiet line. */}
        <div className={`${pad} ${compact ? 'py-4' : 'py-5'}`}>
          {missing && (
            <div className="mb-4 rounded-md bg-moss/60 px-3 py-2.5 text-[13px] text-ink dark:bg-moss-dark/60 dark:text-ink-dark">
              <p>{t('page.targetsMissingIntro').replace('{name}', clientName || t('page.forClient'))}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <button type="button" onClick={applyProfileEstimate} disabled={!profileEstimate || busyTargets} className={`${themeClasses.btnSecondary} !min-h-9 !px-3 !py-1 !text-[13px]`}>
                  {t('page.estimateFromProfile')}
                </button>
                <span className={hint}>{profileEstimate ? profileLine : t('page.estimateNeedsProfile')}</span>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <div>
              <label htmlFor={`${idp}-day-calories`} className={fieldLabel}>{t('page.caloriesPerDay')}</label>
              <span className="mt-1 inline-flex items-baseline gap-2">
                <input
                  id={`${idp}-day-calories`}
                  type="number" inputMode="numeric" min="0" step="10" dir="ltr"
                  value={work.day.calories === '' ? '' : workDay.calories || ''}
                  onChange={(e) => setDayCalories(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                  disabled={busyTargets}
                  className={`${quietNumber} w-28 text-xl`}
                />
                <span className="text-[13px] text-ink-muted dark:text-ink-muted-dark">{t('page.kcal')}</span>
              </span>
            </div>
            <fieldset className="min-w-0">
              <legend className={fieldLabel}>{t('page.macroStyle')}</legend>
              <div className="mt-1 inline-flex flex-wrap gap-0.5 rounded-lg bg-moss p-0.5 dark:bg-moss-dark">
                {MACRO_PRESETS.map((p) => (
                  <button key={p.id} type="button" aria-pressed={!showCustom && matchingPreset === p.id} onClick={() => applyPreset(p)} disabled={busyTargets || !(dayKcal > 0)} className={segBtn(!showCustom && matchingPreset === p.id)}>
                    {t(p.label)}
                  </button>
                ))}
                <button type="button" aria-pressed={showCustom} onClick={() => setCustomMacros(true)} disabled={busyTargets} title={t('page.customMacrosHint')} className={segBtn(showCustom)}>
                  {t('page.presetCustom')}
                </button>
              </div>
            </fieldset>
          </div>

          {showCustom ? (
            <div className="mt-3 grid max-w-md grid-cols-3 gap-3" title={t('page.customMacrosHint')}>
              {['protein', 'carbs', 'fats'].map((k) => {
                const g = workDay[k] || 0;
                const pct = dayKcal > 0 ? Math.round(((g * (k === 'fats' ? 9 : 4)) / dayKcal) * 100) : 0;
                return (
                  <label key={k} className="block min-w-0">
                    <span className="block truncate text-[12px] font-medium text-ink-muted dark:text-ink-muted-dark">{t(`page.${k}`)}</span>
                    <span className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
                    <input
                      type="number" inputMode="numeric" min="0" dir="ltr"
                      value={work.day[k] === '' ? '' : g}
                      onChange={(e) => setDayMacro(k, e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                      disabled={busyTargets}
                      className={`${quietNumber} w-full max-w-[4.5rem] text-[13px]`}
                    />
                    <span className={`tabular-nums ${hint}`}><bdi dir="ltr">g · {pct}%</bdi></span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : targetsComplete && (
            <p className="mt-3 text-[13px] text-ink-muted dark:text-ink-muted-dark">
              {['protein', 'carbs', 'fats'].map((k, i) => (
                <span key={k}>{i > 0 && ' · '}{t(`page.${k}`)} <bdi dir="ltr" className="font-semibold tabular-nums text-ink dark:text-ink-dark">{workDay[k]} g</bdi></span>
              ))}
              {bodyKg > 0 && workDay.protein > 0 && <> · <bdi dir="ltr" className="tabular-nums">{(workDay.protein / bodyKg).toFixed(1)} g/kg</bdi></>}
            </p>
          )}

          {!missing && (
            <p className={`mt-1.5 ${hint}`}>
              {sourceText}
              {onboardingDay?.calories > 0 && Math.round(workDay.calories) !== Math.round(onboardingDay.calories) && (
                <> · {t('page.onboardingWas').replace('{n}', Number(onboardingDay.calories).toLocaleString())}</>
              )}
              {estimatedFromProfile && <> · {t('page.estimatedNote')}</>}
            </p>
          )}
        </div>

        {/* The meals: one table, a total row that says when it doesn't add up. */}
        <div className="border-t border-line dark:border-line-dark">
          <div className={`flex items-center justify-between gap-2 pt-3 ${pad}`}>
            <h3 className="text-[13px] font-semibold text-ink dark:text-ink-dark">{t('page.stepMeals')}</h3>
            <OverflowMenu
              label={t('page.moreActions')}
              items={[
                { label: advancedSplit ? t('page.useShares') : t('page.advancedGrams'), icon: Scale, onClick: () => setAdvancedSplit((v) => !v) },
                { label: t('page.fitToDay'), icon: CheckCircle, onClick: fitSlotsToDay, disabled: busyTargets || !(dayKcal > 0) || !splitOff },
                canResetToOnboarding && { label: t('page.resetOnboarding'), icon: RefreshCw, onClick: resetToOnboarding, disabled: busyTargets },
              ]}
            />
          </div>

          {(missing || autoSlots) && (
            <div className={`mt-1 flex flex-wrap items-center gap-1.5 ${pad}`}>
              <span className={`me-1 ${hint}`}>{t('page.mealsPerDay')}</span>
              {[3, 4, 5, 6].map((n) => (
                <button key={n} type="button" aria-pressed={work.slots.length === n} onClick={() => setSuggestedMealCount(n)} disabled={busyTargets} className={`min-h-8 min-w-8 rounded-md border px-2 text-[13px] font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest ${work.slots.length === n ? 'border-forest-ondark bg-forest-faint text-forest-ondark dark:border-forest dark:bg-forest-faint-dark dark:text-forest' : 'border-line text-ink hover:bg-moss dark:border-line-dark dark:text-ink-dark dark:hover:bg-moss-dark'}`}>
                  {n}
                </button>
              ))}
            </div>
          )}

          <div className="overflow-x-auto">
          <table className="mt-1 w-full min-w-[19rem] text-[13px]">
            <thead className="text-[11px] text-ink-muted dark:text-ink-muted-dark">
              <tr>
                <th scope="col" className={`${th} text-start`}>{t('page.mealCol')}</th>
                <th scope="col" className={`${th} text-end`}>{advancedSplit ? <bdi dir="ltr">P · C · F (g)</bdi> : t('page.shareCol')}</th>
                <th scope="col" className={`${th} text-end`}>kcal</th>
                {!advancedSplit && <th scope="col" className={`${th} hidden text-end sm:table-cell`}><bdi dir="ltr">P · C · F</bdi></th>}
                <th scope="col" className={th}><span className="sr-only">{t('page.actions')}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line border-y border-line dark:divide-line-dark dark:border-line-dark">
              {work.slots.map((s, i) => {
                const label = slotLabel(s.mealSlot) || t('page.slot');
                const key = slotNameKey(s.mealSlot);
                const kcal = parseGrams(s.targetCalories) || 0;
                const pct = s.targetCalories === '' ? '' : dayKcal > 0 ? Math.round((kcal / dayKcal) * 100) : 0;
                const est = slotMacros({ ...s, targetMacros: null }, workDay);
                const m = slotMacros(s, workDay);
                const has = (k) => s.targetMacros?.[k] != null && s.targetMacros[k] !== '';
                const showIdea = !!s.mealName || ideaRows[i];
                return (
                  <tr key={i} className="group">
                    <td className={td}>
                      <select
                        aria-label={`${t('page.slot')} ${i + 1}`}
                        value={key ? mealTranslations.english[key] : s.mealSlot}
                        onChange={(e) => setWorkSlot(i, 'mealSlot', e.target.value)}
                        disabled={busyTargets}
                        className={`${ghostInput} -ms-2 min-h-9 w-auto max-w-full cursor-pointer px-2 font-semibold text-ink dark:text-ink-dark [&_option]:bg-surface dark:[&_option]:bg-surface-dark`}
                      >
                        {!key && <option value={s.mealSlot}>{s.mealSlot}</option>}
                        {slotKeys.map((k) => <option key={k} value={mealTranslations.english[k]}>{t(k)}</option>)}
                      </select>
                      {showIdea && (
                        <input
                          type="text"
                          autoFocus={!s.mealName}
                          aria-label={`${label}: ${t('page.dishHint')}`}
                          placeholder={t('page.dishHintShort')}
                          value={s.mealName}
                          onChange={(e) => setWorkSlot(i, 'mealName', e.target.value)}
                          onBlur={() => { if (!s.mealName) setIdeaRows((r) => ({ ...r, [i]: false })); }}
                          disabled={busyTargets}
                          title={s.mealName || undefined}
                          className={`${ghostInput} -ms-2 block min-h-7 w-full max-w-[18rem] truncate px-2 text-[12px] text-ink-muted placeholder:text-ink-muted/60 focus:text-ink dark:text-ink-muted-dark dark:focus:text-ink-dark`}
                        />
                      )}
                    </td>
                    <td className={`${td} text-end`}>
                      {advancedSplit ? (
                        <span className="inline-flex items-center gap-1" dir="ltr">
                          {['protein', 'carbs', 'fats'].map((k) => (
                            <input
                              key={k}
                              type="number" inputMode="numeric" min="0"
                              aria-label={`${label} ${t(`page.${k}`)}`}
                              value={has(k) || s.targetMacros?.[k] !== '' ? (has(k) ? parseGrams(s.targetMacros[k]) || 0 : est[k]) : ''}
                              onChange={(e) => setSlotMacro(i, k, e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                              disabled={busyTargets}
                              className={`${quietNumber} !min-h-8 w-12 text-[13px]`}
                            />
                          ))}
                        </span>
                      ) : (
                        <span className="inline-flex items-baseline gap-1">
                          <input
                            type="number" inputMode="numeric" min="0" max="100" dir="ltr"
                            aria-label={`${label} ${t('page.mealShare')}`}
                            value={pct}
                            onChange={(e) => setSlotShare(i, e.target.value)}
                            disabled={busyTargets || !(dayKcal > 0)}
                            className={`${quietNumber} !min-h-8 w-14 text-[13px]`}
                          />
                          <span className="text-ink-muted dark:text-ink-muted-dark">%</span>
                        </span>
                      )}
                    </td>
                    <td className={`${td} text-end font-semibold tabular-nums text-ink dark:text-ink-dark`}><bdi dir="ltr">{kcal.toLocaleString()}</bdi></td>
                    {!advancedSplit && (
                      <td className={`${td} hidden text-end tabular-nums text-ink-muted dark:text-ink-muted-dark sm:table-cell`}>
                        <bdi dir="ltr">{m.protein} · {m.carbs} · {m.fats}</bdi>
                      </td>
                    )}
                    <td className={`${td} w-px whitespace-nowrap text-end`}>
                      {!s.mealName && (
                        <button type="button" onClick={() => setIdeaRows((r) => ({ ...r, [i]: true }))} disabled={busyTargets} aria-label={`${t('page.addIdea')}: ${label}`} title={t('page.addIdea')} className={rowIcon}>
                          <Lightbulb size={14} aria-hidden="true" />
                        </button>
                      )}
                      <button type="button" onClick={() => removeWorkSlot(i)} disabled={busyTargets || work.slots.length <= 1} aria-label={`${t('page.removeSlot')}: ${label}`} title={t('page.removeSlot')} className={`${rowIcon} hover:!text-red-700 dark:hover:!text-red-400`}>
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className={td}>
                  <button type="button" onClick={addWorkSlot} disabled={busyTargets || work.slots.length >= 8} className="-ms-2 inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-ink-muted hover:bg-moss hover:text-ink disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark">
                    <Plus size={14} aria-hidden="true" /> {t('page.addMeal')}
                  </button>
                </td>
                <td className={`${td} text-end font-semibold tabular-nums ${splitOff ? 'text-amber-700 dark:text-amber-400' : 'text-ink dark:text-ink-dark'}`}>
                  {!advancedSplit && <bdi dir="ltr">{totalPct}%</bdi>}
                </td>
                <td className={`${td} text-end font-semibold tabular-nums ${splitOff ? 'text-amber-700 dark:text-amber-400' : 'text-ink dark:text-ink-dark'}`}>
                  <bdi dir="ltr">{mealSum.toLocaleString()}</bdi>
                </td>
                {!advancedSplit && <td className={`${td} hidden sm:table-cell`} />}
                <td className={td}>
                  {!splitOff && work.slots.length > 0 && dayKcal > 0 && <CheckCircle size={14} className="ms-auto text-forest-ondark dark:text-forest" aria-label={t('page.splitOk')} />}
                </td>
              </tr>
            </tfoot>
          </table>
          </div>

          {splitOff && (
            <div aria-live="polite" className={`flex flex-wrap items-center justify-between gap-2 border-t border-line py-2.5 text-[13px] dark:border-line-dark ${pad}`}>
              <span className="inline-flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                <AlertTriangle size={14} aria-hidden="true" />
                {(gap > 0 ? t('page.splitUnder') : t('page.splitOver')).replace('{n}', Math.abs(gap).toLocaleString())}
              </span>
              <button type="button" onClick={fitSlotsToDay} disabled={busyTargets || !(dayKcal > 0)} className="inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 font-semibold text-ink hover:bg-moss disabled:opacity-50 dark:text-ink-dark dark:hover:bg-moss-dark">
                <Scale size={14} aria-hidden="true" /> {t('page.fitToDay')}
              </button>
            </div>
          )}
        </div>

        {flags.length > 0 && (
          <div aria-live="polite" className={`space-y-1 border-t border-line py-3 text-[13px] text-amber-700 dark:border-line-dark dark:text-amber-400 ${pad}`}>
            {flags.map((line) => <p key={line}>{line}</p>)}
          </div>
        )}

        {compact && !missing && targetsDirty && (
          <div className={`flex flex-wrap items-center justify-end gap-2 border-t border-line py-3 dark:border-line-dark ${pad}`}>
            <span className="me-auto text-[13px] text-ink-muted dark:text-ink-muted-dark">{t('page.saveUpdatesClient').replace('{name}', clientLabel())}</span>
            <button type="button" onClick={undoTargetChanges} disabled={busyTargets} className={quietBtn}>{t('page.undoChanges')}</button>
            <button type="button" onClick={saveTargets} disabled={busyTargets} className={`${themeClasses.btnPrimary} !min-h-10`}>
              {isSavingTargets ? t('page.saving') : t('page.saveTargets')}
            </button>
          </div>
        )}
        {targetsError && (
          <p role="alert" className={`pb-3 text-[13px] text-red-700 dark:text-red-400 ${pad}`}>{targetsError}</p>
        )}
      </div>
    );
  };

  // The client card: beside every step before the meals, so allergies and notes are never out of sight. Not a gate.
  const renderBrief = () => {
    const isHe = language === 'hebrew';
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(isHe ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
    const activePlan = (copyPlans || []).find((p) => p.isActive);
    const goalLabel = (v) => { const g = goalOptions.find((o) => o.value === v); return g ? (isHe ? g.labelHe : g.labelEn) : v; };
    const regionLabel = (v) => { const r = regionOptions.find((o) => o.value === v); return r ? (isHe ? r.labelHe : r.labelEn) : v; };
    const none = <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.noneRecorded')}</span>;
    const chips = (items, options, tone) => {
      const list = [...new Set(items)];
      return list.length
        ? list.map((item) => <span key={item} className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-medium ${tone}`}>{optionLabel(options, item)}</span>)
        : none;
    };
    const label = 'block text-[11px] font-medium text-ink-muted dark:text-ink-muted-dark';
    const neutralChip = 'bg-moss text-ink dark:bg-moss-dark dark:text-ink-dark';
    const dangerChip = 'bg-red-700/10 text-red-700 dark:bg-red-400/15 dark:text-red-400';

    return (
      <aside aria-labelledby="brief-title" className="rounded-lg border border-line bg-surface dark:border-line-dark dark:bg-surface-dark lg:sticky lg:top-0">
        <div className="px-4 pt-4">
          <h2 id="brief-title" className="text-sm font-semibold text-ink dark:text-ink-dark">{t('page.aboutClient').replace('{name}', clientLabel())}</h2>
          {profileLine && <p className="mt-0.5 text-[12px] text-ink-muted dark:text-ink-muted-dark">{profileLine}</p>}
        </div>
        <dl className="space-y-3 px-4 py-3 text-[13px]">
          <div><dt className={label}>{t('page.mustAvoid')}</dt><dd className="mt-1 flex flex-wrap gap-1">{chips(brief.allergies, allergiesOptions, dangerChip)}</dd></div>
          <div><dt className={label}>{t('page.limitsAndDiet')}</dt><dd className="mt-1 flex flex-wrap gap-1">{chips([...brief.limitations, ...brief.dietPrefs, ...(brief.dietStyle ? [brief.dietStyle] : [])], limitationsOptions, neutralChip)}</dd></div>
          {brief.medical && <div><dt className={label}>{t('page.medicalConditions')}</dt><dd className="mt-1 whitespace-pre-line text-ink dark:text-ink-dark">{brief.medical}</dd></div>}
          <div><dt className={label}>{t('page.goalAndRegion')}</dt><dd className="mt-1 text-ink dark:text-ink-dark">{brief.goal ? goalLabel(brief.goal) : none}{brief.region ? <> · {regionLabel(brief.region)}</> : null}</dd></div>
          <div>
            <dt className={label}>{t('page.weightTrend')}</dt>
            <dd className="mt-1 text-ink dark:text-ink-dark">
              {weightLogs == null ? <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.loadingShort')}</span>
                : weightTrend ? (
                  <>
                    <bdi dir="ltr" className="font-semibold tabular-nums">{weightTrend.kg} kg</bdi>
                    {weightTrend.change != null && (
                      <span className="text-ink-muted dark:text-ink-muted-dark"> · <bdi dir="ltr" className="tabular-nums">{weightTrend.change > 0 ? '+' : ''}{weightTrend.change} kg</bdi> {t('page.since').replace('{date}', fmtDate(weightTrend.since))}</span>
                    )}
                  </>
                ) : <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.noWeighIns')}</span>}
            </dd>
          </div>
          <div>
            <dt className={label}>{t('page.currentPlan')}</dt>
            <dd className="mt-1 text-ink dark:text-ink-dark">
              {copyPlans == null ? <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.loadingShort')}</span>
                : activePlan ? (
                  <>
                    <span className="font-medium">{activePlan.meal_plan_name || t('page.newPlan')}</span>
                    {' · '}<bdi dir="ltr" className="tabular-nums">{planKcal(activePlan).toLocaleString()} kcal</bdi>
                  </>
                ) : <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.noActivePlan')}</span>}
            </dd>
          </div>
        </dl>

        {/* The coach's own input: notes the AI follows for this client. */}
        <div className="border-t border-line px-4 py-3 dark:border-line-dark">
          <label className="block">
            <span className={label}>{t('page.notesForAi')}</span>
            <textarea rows={4} value={brief.notes} placeholder={t('page.notesForAiHint')} onChange={(e) => setNotes(e.target.value)} disabled={isSavingBrief || isGenerating} className={`${themeClasses.inputField} mt-1 resize-y !px-3 !py-2 !text-[13px]`} />
          </label>
          <p className="mt-1 text-[11px] text-ink-muted dark:text-ink-muted-dark">{t('page.notesAiHelp').replace('{name}', clientLabel())}</p>
          {notesDirty && (
            <div className="mt-2 flex justify-end gap-1">
              <button type="button" onClick={cancelNoteEdits} disabled={isSavingBrief} className={`${quietBtn} !min-h-9 !text-[13px]`}>{t('page.undoChanges')}</button>
              <button type="button" onClick={saveNotes} disabled={isSavingBrief} className={`${themeClasses.btnPrimary} !min-h-9 !px-3 !py-1 !text-[13px]`}>
                {isSavingBrief ? t('page.saving') : t('page.saveNotes')}
              </button>
            </div>
          )}
          {briefError && <p role="alert" className="mt-2 text-[13px] text-red-700 dark:text-red-400">{briefError}</p>}
        </div>
      </aside>
    );
  };

  // Step 1's three ways in, each saying what happens next.
  const renderStartCards = () => {
    const options = [
      { kind: 'ai', Icon: Sparkles, title: t('page.startAiTitle'), body: t('page.startAiBody'), next: t('page.nextStructure'), accent: true },
      { kind: 'manual', Icon: Edit2, title: t('page.startManualTitle'), body: t('page.startManualBody').replace('{name}', clientLabel()), next: t('page.nextAddFoods') },
      { kind: 'copy', Icon: Copy, title: t('page.startCopyTitle'), body: t('page.startCopyBody').replace('{name}', clientLabel()), next: t('page.nextPickPlan') },
    ];
    return (
      <ul className="grid gap-3 md:grid-cols-3">
        {options.map(({ kind, Icon, title, body, next, accent }) => (
          <li key={kind} className="flex">
            <button
              type="button"
              onClick={() => chooseStart(kind)}
              disabled={busyTargets}
              className="group flex w-full flex-col rounded-lg border border-line bg-surface p-5 text-start transition-colors hover:border-forest-ondark/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-50 dark:border-line-dark dark:bg-surface-dark dark:hover:border-forest/60"
            >
              <span aria-hidden="true" className={`flex h-10 w-10 items-center justify-center rounded-md ${accent ? 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark dark:text-forest' : 'bg-moss text-ink dark:bg-moss-dark dark:text-ink-dark'}`}>
                <Icon size={18} />
              </span>
              <span className="mt-4 text-base font-semibold text-ink dark:text-ink-dark">{title}</span>
              <span className="mt-1 flex-1 text-[13px] leading-relaxed text-ink-muted dark:text-ink-muted-dark">{body}</span>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-forest-ondark dark:text-forest">
                {next} {isRTL ? <ChevronLeft size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <ClientWorkspaceLayout fillViewport>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas font-sans text-ink dark:bg-canvas-dark dark:text-ink-dark" dir={direction}>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 dark:border-line-dark dark:bg-canvas-dark md:px-5">
          <button
            type="button"
            onClick={() => navigate('/client-meal-plans')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:hover:bg-surface-dark"
            aria-label={language === 'hebrew' ? 'חזרה לתוכניות התזונה' : 'Back to nutrition plans'}
          >
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <h1 className="hidden max-w-[16rem] shrink-0 truncate text-sm font-semibold tracking-tight sm:block">
            {planNameDraft || savedMealPlanName || t('page.newPlan')}
          </h1>
          {screen === 'builder' && planStatus && (
            <span role="status" aria-live="polite" title={sentToClient && !isPlanDirty ? t('page.sentTo').replace('{name}', clientLabel()) : t('page.draftNotSentHint')} className="hidden shrink-0 sm:inline-flex">
              {planStatus === 'saved'
                ? renderPill('forest', CheckCircle, sentToClient && !isPlanDirty ? t('page.sentTo').replace('{name}', clientLabel()) : t('page.statusSaved'))
                : renderPill('neutral', null, planStatus === 'unsaved' ? t('page.statusUnsaved') : t('page.statusDraft'))}
            </span>
          )}
          {(screen === 'targets' || screen === 'builder') ? renderStepper() : <span className="flex-1" />}
          {step === 3 && !isGenerating && (
            <button type="button" onClick={openReview} disabled={!planHasFood} title={planHasFood ? undefined : t('page.nothingToSend')} className={`${primaryBtn} !min-h-9 shrink-0 !px-3`}>
              <span className="hidden sm:inline">{t('page.nextReview')}</span>
              <span className="sm:hidden">{t('page.stepSend')}</span>
              {isRTL ? <ChevronLeft size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
            </button>
          )}
        </header>

        <main id="plan-main" className="min-h-0 flex-1 overflow-y-auto px-4 py-5 custom-scrollbar sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl pb-8">

        {screen === 'loading' && (
          <div className="mx-auto max-w-2xl rounded-lg border border-line bg-surface p-5 dark:border-line-dark dark:bg-surface-dark" aria-busy="true">
            <div className="mb-3 h-4 w-40 rounded bg-moss animate-pulse motion-reduce:animate-none dark:bg-moss-dark" />
            <div className="mb-5 h-3 w-64 rounded bg-line animate-pulse motion-reduce:animate-none dark:bg-line-dark" />
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 border-t border-line py-3 dark:border-line-dark">
                <span className="h-3 w-24 rounded bg-moss animate-pulse motion-reduce:animate-none dark:bg-moss-dark" />
                <span className="h-3 flex-1 rounded bg-line animate-pulse motion-reduce:animate-none dark:bg-line-dark" />
                <span className="h-3 w-12 rounded bg-moss animate-pulse motion-reduce:animate-none dark:bg-moss-dark" />
              </div>
            ))}
          </div>
        )}

        {screen === 'error' && (
          <div role="alert" className="mx-auto max-w-2xl rounded-lg border border-red-700/30 bg-red-700/5 dark:border-red-400/30 dark:bg-red-400/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
              <span className="text-sm font-semibold">{t('page.loadErrorTitle')}</span>
            </div>
            <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="min-h-[44px] rounded-md border border-line dark:border-line-dark px-4 text-sm font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark">
              {t('page.retry')}
            </button>
          </div>
        )}

        {/* Steps 1–2: a starting point, then the structure. The client card stays beside them. */}
        {screen === 'targets' && (
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <div className="min-w-0 space-y-4">

            {/* 1. How to start. */}
            {step === 1 && !copyOpen && (
              <>
                {renderStepIntro(t('page.startTitle').replace('{name}', clientLabel()), t('page.startBody').replace(/\{name\}/g, clientLabel()))}
                {renderStartCards()}
              </>
            )}

            {/* 1b. Start from a saved plan: this client's earlier plans or a team template, with a preview of the one picked. */}
            {step === 1 && copyOpen && renderStepIntro(t('page.copyTitle'), t('page.copyHint'), (
              <button type="button" onClick={backToStart} className={`${quietBtn} -ms-3 mt-2`}>
                {isRTL ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />} {t('page.back')}
              </button>
            ))}
            {step === 1 && copyOpen && (() => {
              const onTemplates = copyTab === 'templates';
              const list = onTemplates ? templates : copyPlans;
              const listError = onTemplates ? templatesError : copyError;
              const pickId = onTemplates ? templatePickId : copyPickId;
              const setPick = onTemplates ? setTemplatePickId : setCopyPickId;
              const picked = (list || []).find((p) => p.id === pickId);
              const dateOf = (p) => (p.created_at ? new Date(p.created_at).toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
              return (
                <section aria-labelledby="step-title" className="rounded-lg border border-line bg-surface dark:border-line-dark dark:bg-surface-dark">
                  <div
                    role="tablist"
                    aria-label={t('page.copyTitle')}
                    onKeyDown={(e) => {
                      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                      const next = copyTab === 'client' ? 'templates' : 'client';
                      showCopyTab(next);
                      requestAnimationFrame(() => document.getElementById(`copy-tab-${next}`)?.focus());
                    }}
                    className="flex gap-1 border-b border-line px-5 dark:border-line-dark sm:px-6"
                  >
                    {[['client', t('page.tabClientPlans')], ['templates', t('page.tabTemplates')]].map(([id, label]) => (
                      <button
                        key={id}
                        id={`copy-tab-${id}`}
                        type="button"
                        role="tab"
                        aria-selected={copyTab === id}
                        aria-controls="copy-panel"
                        tabIndex={copyTab === id ? 0 : -1}
                        onClick={() => showCopyTab(id)}
                        className={`-mb-px min-h-11 border-b-2 px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest ${copyTab === id ? 'border-forest-ondark text-ink dark:border-forest dark:text-ink-dark' : 'border-transparent text-ink-muted hover:text-ink dark:text-ink-muted-dark dark:hover:text-ink-dark'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div role="tabpanel" id="copy-panel" aria-labelledby={`copy-tab-${copyTab}`}>
                  {listError ? (
                    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-5 py-6 sm:px-6">
                      <p className="text-[13px] text-red-700 dark:text-red-400">{listError}</p>
                      <button type="button" onClick={() => (onTemplates ? loadTemplates() : (setCopyPlans(null), openCopyPicker()))} className={themeClasses.btnSecondary}>{t('page.retry')}</button>
                    </div>
                  ) : list == null ? (
                    <div aria-busy="true" className="space-y-3 px-5 py-5 sm:px-6">
                      {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded bg-moss animate-pulse motion-reduce:animate-none dark:bg-moss-dark" />)}
                    </div>
                  ) : list.length === 0 ? (
                    <p className="px-5 py-8 text-center text-[13px] text-ink-muted dark:text-ink-muted-dark sm:px-6">{onTemplates ? t('page.templatesNone') : t('page.copyNone')}</p>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                        <fieldset className="border-line dark:border-line-dark md:border-e">
                          <legend className="sr-only">{t('page.copyTitle')}</legend>
                          <ul className="max-h-[420px] divide-y divide-line overflow-y-auto dark:divide-line-dark">
                            {list.map((p) => (
                              <li key={p.id}>
                                <label className={`flex cursor-pointer items-start gap-3 px-5 py-3 transition-colors sm:px-6 ${pickId === p.id ? 'bg-forest-faint dark:bg-forest-faint-dark' : 'hover:bg-moss dark:hover:bg-moss-dark'}`}>
                                  <input type="radio" name="copy-plan" checked={pickId === p.id} onChange={() => setPick(p.id)} className="mt-1 h-4 w-4 shrink-0 accent-[rgb(var(--forest-ondark-rgb))]" />
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate font-semibold text-ink dark:text-ink-dark">{p.meal_plan_name || t('page.newPlan')}</span>
                                    <span className="block text-[12px] text-ink-muted dark:text-ink-muted-dark">
                                      {onTemplates && <>{p.is_mine ? t('page.byYou') : (p.author_name || t('page.byTeam'))} · </>}
                                      {dateOf(p)} · <bdi dir="ltr" className="tabular-nums">{planKcal(p).toLocaleString()} kcal</bdi> · <bdi>{t('page.nMeals').replace('{n}', p.meal_plan.meals.length)}</bdi>
                                    </span>
                                  </span>
                                  {p.isActive && renderPill('forest', CheckCircle, t('page.statusActive'))}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </fieldset>
                        <div className="border-t border-line px-5 py-4 dark:border-line-dark md:border-t-0 sm:px-6" aria-live="polite">
                          {picked && (
                            <>
                              <p className="mb-2 text-[11px] font-medium text-ink-muted dark:text-ink-muted-dark">{t('page.preview')}</p>
                              <ul className="space-y-2">
                                {picked.meal_plan.meals.map((m, i) => (
                                  <li key={i} className="flex items-center gap-3">
                                    <MealStickerTile stickers={pickMealStickers(m.main?.ingredients)} size={40} />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-[13px] font-semibold text-ink dark:text-ink-dark">{translateMealNameForDisplay(m.meal)}</span>
                                      <span className="block truncate text-[12px] text-ink-muted dark:text-ink-muted-dark">{m.main?.meal_title || m.main?.meal_name || t('page.untitledDish')}</span>
                                    </span>
                                    <bdi dir="ltr" className="shrink-0 text-[12px] tabular-nums text-ink-muted dark:text-ink-muted-dark">{Math.round(Number(m.main?.nutrition?.calories ?? m.main?.calories) || 0)} kcal</bdi>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end border-t border-line px-5 py-3 dark:border-line-dark sm:px-6">
                        <button type="button" onClick={() => applyCopiedPlan(picked, { fromTemplate: onTemplates })} disabled={!picked} className={`${themeClasses.btnPrimary} !min-h-11`}>
                          <Copy size={16} aria-hidden="true" /> {t('page.useThisPlan')}
                        </button>
                      </div>
                    </>
                  )}
                  </div>
                </section>
              );
            })()}

            {/* 2. The structure the plan is built on, then the start itself. */}
            {step === 2 && (
              <>
                {renderStepIntro(t('page.structureTitle'), t('page.structureBodyAi'))}
                <section aria-label={t('page.stepStructure')} className="rounded-lg border border-line bg-surface dark:border-line-dark dark:bg-surface-dark">
                  {renderStructure()}
                </section>
                {(avoidList.length > 0 || brief.notes.trim()) && (
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-muted dark:text-ink-muted-dark">
                    <Sparkles size={14} className="text-forest-ondark dark:text-forest" aria-hidden="true" />
                    {avoidList.length > 0 && (
                      <span>{t('page.aiAvoids')}: <span className="font-medium text-ink dark:text-ink-dark">{avoidList.map((x) => optionLabel([...allergiesOptions, ...limitationsOptions], x)).join(', ')}</span></span>
                    )}
                    {avoidList.length > 0 && brief.notes.trim() && <span aria-hidden="true">·</span>}
                    {brief.notes.trim() && <span>{t('page.aiFollowsNotes')}</span>}
                  </p>
                )}

                <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-t border-line bg-canvas/95 px-4 py-3 backdrop-blur dark:border-line-dark dark:bg-canvas-dark/95 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:backdrop-blur-none sm:dark:bg-transparent">
                  <button type="button" onClick={backToStart} disabled={busyTargets} className={`${quietBtn} -ms-3`}>
                    {isRTL ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />} {t('page.back')}
                  </button>
                  <span className="flex-1" />
                  {targetsSaved && targetsDirty && (
                    <button type="button" onClick={undoTargetChanges} disabled={busyTargets} className={quietBtn}>{t('page.undoChanges')}</button>
                  )}
                  <button id="start-generate" type="button" onClick={() => startWith('ai')} disabled={!canStartAi} aria-describedby="start-note" className={`${primaryBtn} flex-1 sm:flex-none`}>
                    <Sparkles size={16} aria-hidden="true" /> {isSavingTargets || isSavingBrief ? t('page.saving') : t('page.generatePlan')}
                  </button>
                </div>
                <p id="start-note" className="text-end text-[12px] text-ink-muted dark:text-ink-muted-dark" role="status">
                  {startReason || [
                    (!targetsSaved || targetsDirty) && t('page.startSavesGoals').replace('{name}', clientLabel()),
                    t('page.aiReviewAfter').replace('{name}', clientLabel()),
                  ].filter(Boolean).join(' ')}
                </p>
              </>
            )}
            </div>

            {renderBrief()}
          </div>
        )}

        {/* 2–3. The drafted day, one meal at a time. */}
        {screen === 'builder' && (() => {
          const mainTotals = generatedMealPlan.meals.reduce(
            (acc, meal) => {
              if (meal.main && meal.main.nutrition) {
                acc.calories += meal.main.nutrition.calories || 0;
                acc.protein += meal.main.nutrition.protein || 0;
                acc.carbs += meal.main.nutrition.carbs || 0;
                acc.fat += meal.main.nutrition.fat || 0;
              }
              return acc;
            },
            { calories: 0, protein: 0, carbs: 0, fat: 0 }
          );

          return (
            <div ref={generatedPlanRef} tabIndex={-1} aria-label={t('page.stepMeals')} className="w-full space-y-6 scroll-mt-24 focus:outline-none">

              {/* 3. The meals. Guidance only while there's nothing to look at yet. */}
              {!publishOpen && (isGenerating || !planHasFood) && renderStepIntro(
                t('page.mealsTitle'),
                isGenerating ? t('page.mealsBodyGenerating').replace('{n}', generatedMealPlan.meals.length) : t('page.mealsBody'),
              )}

              {!publishOpen && (() => {
                const meals = generatedMealPlan.meals;
                const activeIdx = Math.min(Math.max(activeMealIndex, 0), meals.length - 1);
                const mealKcal = (m) => m?.main?.nutrition?.calories || m?.main?.calories || 0;
                const mealMacro = (m, k) => m?.main?.nutrition?.[k] || m?.main?.[k] || 0;
                const dishTitle = (m) => m?.main?.meal_title || m?.main?.meal_name || m?.main?.title || '';
                // The dish pictured from its ingredients; a dish with none yet falls back to its name, then the meal's icon.
                const mealStickers = (m) => {
                  if (!m || m.arriving) return [];
                  const picks = pickMealStickers(m.main?.ingredients);
                  if (picks.length) return picks;
                  const fromName = stickersFromText(dishTitle(m));
                  if (fromName.length) return fromName;
                  const icon = mealIcon(m.meal);
                  return icon ? [icon] : [];
                };
                const selectMeal = (i) => { setActiveMealIndex(i); setOpenAlt(null); };

                const meal = meals[activeIdx];
                const mealBusy = draftPhase === 'request' || !!meal?.arriving;
                const index = activeIdx;
                const slot = slotTargetFor(meal, index);
                const mealCalories = mealKcal(meal);
                const alternativeDishes = [
                  ...(meal?.alternative ? [{ dish: meal.alternative, dishType: 'alternative', altIndex: null }] : []),
                  ...(Array.isArray(meal?.alternatives) ? meal.alternatives.map((alt, altIdx) => ({ dish: alt, dishType: 'alternatives', altIndex: altIdx })) : []),
                ];
                const smallBtn = 'inline-flex min-h-9 items-center gap-1.5 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 text-[13px] font-semibold text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-50 disabled:cursor-not-allowed';
                const linkBtn = 'inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-ink-muted hover:bg-moss hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-50 dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark';
                const firstArriving = meals.findIndex((item) => item.arriving);

                // The day against its target: on (green), over (amber), still filling (neutral).
                const dayStatus = reconcile(mainTotals.calories, dayTargets.calories, DAY_TOLERANCE).status;
                const dayFill = dayTargets.calories > 0 ? Math.min(100, (mainTotals.calories / dayTargets.calories) * 100) : 0;
                const dayLeft = Math.round((dayTargets.calories || 0) - mainTotals.calories);
                // A meal's state as one dot: writing, empty, on target, off target, or no target.
                const mealDot = (m, i) => {
                  if (draftPhase === 'request' || (draftPhase === 'stream' && m.arriving)) return 'h-2 w-2 rounded-full bg-ink/40 animate-pulse motion-reduce:animate-none dark:bg-ink-dark/40';
                  if (!(m.main?.ingredients?.length)) return 'h-2 w-2 rounded-full border border-ink-muted/60 dark:border-ink-muted-dark/60';
                  const st = slotTargetFor(m, i);
                  if (!st) return 'h-2 w-2 rounded-full bg-ink/50 dark:bg-ink-dark/50';
                  return reconcile(mealKcal(m), st.calories, MEAL_TOLERANCE).status === 'on'
                    ? 'h-2 w-2 rounded-full bg-forest-ondark dark:bg-forest'
                    : 'h-2 w-2 rounded-full bg-amber-600 dark:bg-amber-400';
                };
                const openTargets = (focusMeal) => {
                  setTargetsOpen(true);
                  requestAnimationFrame(() => {
                    const field = focusMeal
                      ? (document.querySelector(`#targets-editor input[aria-label="${slotLabel(focusMeal)} ${t('page.mealShare')}"]`)
                        || document.querySelector(`#targets-editor input[aria-label="${slotLabel(focusMeal)} ${t('page.protein')}"]`))
                      : document.getElementById('strip-day-calories');
                    field?.focus();
                    field?.scrollIntoView({ block: 'center' });
                  });
                };
                const mealReconcile = slot ? reconcile(mealCalories, slot.calories, MEAL_TOLERANCE) : null;
                const hasFood = !!meal?.main?.ingredients?.length;

                return (
                  <>
                    {redraftConfirm && (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-700/30 bg-amber-700/5 p-4 dark:border-amber-400/30 dark:bg-amber-400/10" role="alertdialog" aria-labelledby="redraft-confirm-title">
                        <p id="redraft-confirm-title" className="text-sm font-medium text-ink dark:text-ink-dark">{t('page.replaceUnsavedConfirm')}</p>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => setRedraftConfirm(false)} className={smallBtn}>{t('page.cancel')}</button>
                          <button
                            type="button"
                            onClick={() => { setRedraftConfirm(false); handleGenerateMealPlan(); }}
                            className="inline-flex min-h-9 items-center rounded-md bg-red-700 px-4 text-sm font-semibold text-white hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
                          >
                            {t('page.replaceDraft')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* The day in one line: progress against the target, what to avoid, and the day's actions. */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" aria-live="polite">
                      <div className="flex min-w-[16rem] flex-1 items-center gap-3">
                        <span className="text-[13px] font-semibold text-ink dark:text-ink-dark">{t('page.dayOverview')}</span>
                        <bdi dir="ltr" className="shrink-0 text-sm font-semibold tabular-nums text-ink dark:text-ink-dark">
                          {Math.round(mainTotals.calories).toLocaleString()}
                          <span className="font-normal text-ink-muted dark:text-ink-muted-dark"> / {Number(dayTargets.calories || 0).toLocaleString()} kcal</span>
                        </bdi>
                        <div className="h-1.5 min-w-[4rem] max-w-xs flex-1 overflow-hidden rounded-full bg-moss dark:bg-moss-dark" aria-hidden="true">
                          <div className={`h-full rounded-full transition-[width] duration-200 ${dayStatus === 'on' ? 'bg-forest-ondark dark:bg-forest' : dayStatus === 'over' ? 'bg-amber-600 dark:bg-amber-400' : 'bg-ink/50 dark:bg-ink-dark/50'}`} style={{ width: `${dayFill}%` }} />
                        </div>
                        {!isGenerating && dayTargets.calories > 0 && (
                          dayStatus === 'on'
                            ? <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-forest-ondark dark:text-forest"><CheckCircle size={13} aria-hidden="true" /> {t('page.onTarget')}</span>
                            : dayStatus === 'over'
                              ? <span className="shrink-0 text-[12px] font-medium text-amber-700 dark:text-amber-400">{t('page.overBy').replace('{n}', `${Math.abs(dayLeft).toLocaleString()} kcal`)}</span>
                              : <span className="shrink-0 text-[12px] text-ink-muted dark:text-ink-muted-dark">{t('page.kcalLeft').replace('{n}', dayLeft.toLocaleString())}</span>
                        )}
                      </div>
                      <bdi dir="ltr" className="hidden text-[12px] tabular-nums text-ink-muted dark:text-ink-muted-dark md:inline">
                        P {roundMacro(mainTotals.protein)}/{dayTargets.protein} · C {roundMacro(mainTotals.carbs)}/{dayTargets.carbs} · F {roundMacro(mainTotals.fat)}/{dayTargets.fats} g
                      </bdi>
                      {brief.allergies.length > 0 && (
                        <span className="flex flex-wrap items-center gap-1" title={t('page.mustAvoid')}>
                          <AlertTriangle size={13} className="text-red-700 dark:text-red-400" aria-hidden="true" />
                          <span className="sr-only">{t('page.mustAvoid')}:</span>
                          {brief.allergies.map((x) => <span key={x} className="rounded bg-red-700/10 px-1.5 py-0.5 text-[12px] font-medium text-red-700 dark:bg-red-400/15 dark:text-red-400">{optionLabel(allergiesOptions, x)}</span>)}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => openTargets(null)} disabled={isGenerating} className={smallBtn}>
                          <Scale size={14} aria-hidden="true" /> {t('page.targetsButton')}
                          {targetsDirty && <span className="h-1.5 w-1.5 rounded-full bg-amber-600 dark:bg-amber-400" aria-label={t('page.statusUnsaved')} />}
                        </button>
                        <OverflowMenu
                          label={t('page.moreActions')}
                          items={[
                            { label: generatedMealPlan.origin === 'manual' ? t('page.generateMealPlan') : t('page.redraft'), icon: Sparkles, onClick: requestDraft, disabled: !canDraft },
                          ]}
                        />
                      </div>
                    </div>
                    {!canDraft && !isGenerating && !slots.length && generatedMealPlan.origin !== 'manual' && (
                      <p className="-mt-4 text-end text-[11px] text-ink-muted dark:text-ink-muted-dark">{t('page.draftNeedsSlots')}</p>
                    )}
                    {copyNote && (
                      <div className="flex items-start gap-2 rounded-lg bg-moss/60 px-4 py-2.5 text-[13px] text-ink dark:bg-moss-dark/60 dark:text-ink-dark" role="status">
                        <Info size={15} className="mt-0.5 shrink-0 text-ink-muted dark:text-ink-muted-dark" aria-hidden="true" />
                        <span className="flex-1">{copyNote}</span>
                        <button type="button" onClick={() => setCopyNote('')} className={linkBtn}>{t('page.dismiss')}</button>
                      </div>
                    )}

                    <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] lg:items-start">
                      {/* The meals of the day: name, state, calories. Nothing else. */}
                      <nav aria-label={t('page.dayRail')} className="min-w-0 lg:sticky lg:top-0">
                        <ul className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 lg:mx-0 lg:block lg:space-y-0.5 lg:overflow-visible lg:px-0 lg:pb-0">
                          {meals.map((m, i) => {
                            const cal = mealKcal(m);
                            const st = slotTargetFor(m, i);
                            const selected = i === activeIdx;
                            const writing = draftPhase === 'request' || (draftPhase === 'stream' && m.arriving);
                            return (
                              <li key={i} className="shrink-0 lg:shrink">
                                <button
                                  type="button"
                                  onClick={() => selectMeal(i)}
                                  aria-current={selected ? 'true' : undefined}
                                  className={`flex w-[11rem] items-center gap-2.5 rounded-md border border-line px-3 py-2 text-start lg:w-full lg:border-0 dark:border-line-dark transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest ${selected ? 'bg-surface shadow-sm ring-1 ring-line dark:bg-surface-dark dark:ring-line-dark' : 'hover:bg-moss/70 dark:hover:bg-moss-dark/70'}`}
                                >
                                  <span aria-hidden="true" className={`shrink-0 ${mealDot(m, i)}`} />
                                  <span className="min-w-0 flex-1">
                                    <span className={`block truncate text-[13px] ${selected ? 'font-semibold text-ink dark:text-ink-dark' : 'font-medium text-ink dark:text-ink-dark'}`}>{translateMealNameForDisplay(m.meal)}</span>
                                    <span className="block truncate text-[12px] text-ink-muted dark:text-ink-muted-dark">
                                      {writing ? (i === firstArriving && draftPhase === 'stream' ? t('page.writingNow') : t('page.waiting')) : (dishTitle(m) || t('page.emptyMeal'))}
                                    </span>
                                  </span>
                                  <bdi dir="ltr" className="shrink-0 text-[12px] tabular-nums text-ink dark:text-ink-dark">
                                    {writing ? '' : Math.round(cal)}
                                    {st && <span className="text-ink-muted dark:text-ink-muted-dark">{writing ? '' : '/'}{st.calories}</span>}
                                  </bdi>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        {/* Meals added here belong to this plan only; the client's saved slots don't change. */}
                        <div className="mt-1 px-1">
                          {addingMeal ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <select
                                autoFocus
                                aria-label={t('page.chooseMeal')}
                                value={newMealPick}
                                onChange={(e) => setNewMealPick(e.target.value)}
                                className={`${themeClasses.select} !min-h-9 !py-1 !text-[13px] flex-1`}
                              >
                                <option value="" disabled>{t('page.chooseMeal')}</option>
                                {slotKeys.map((k) => <option key={k} value={mealTranslations.english[k]}>{t(k)}</option>)}
                              </select>
                              <button type="button" onClick={() => { addPlanMeal(newMealPick); setNewMealPick(''); }} disabled={!newMealPick} className={smallBtn}>{t('page.addMeal')}</button>
                              <button type="button" onClick={() => { setAddingMeal(false); setNewMealPick(''); }} className={linkBtn}>{t('page.cancel')}</button>
                            </div>
                          ) : (
                            <button type="button" onClick={() => setAddingMeal(true)} disabled={isGenerating} className={linkBtn}>
                              <Plus size={14} aria-hidden="true" /> {t('page.addMeal')}
                            </button>
                          )}
                        </div>
                      </nav>

                      {/* The open meal: its dish, how it sits against its target, its foods, then its alternatives. */}
                      {meal && (
                        <section aria-labelledby="meal-detail-title" className={`${cardBase} min-w-0`}>
                          <div className="flex items-start gap-3 px-4 pt-5 sm:px-6">
                            {!mealBusy && <MealStickerTile stickers={mealStickers(meal)} size={48} />}
                            <div className="min-w-0 flex-1">
                              <p className="text-[12px] text-ink-muted dark:text-ink-muted-dark">
                                {translateMealNameForDisplay(meal.meal)}
                                {slot ? <> · {t('page.target')} <bdi dir="ltr" className="tabular-nums">{slot.calories.toLocaleString()} kcal</bdi></> : null}
                                {meal.planOnly && <> · {t('page.addedForPlan')}</>}
                              </p>
                              {!mealBusy && editingMealTitle?.mealIndex === index && editingMealTitle?.type === 'main' ? (
                                <input
                                  type="text"
                                  aria-label={t('page.renameDish')}
                                  className={themeClasses.inputField + ' !text-base !font-semibold !py-1 !px-2 max-w-md mt-0.5'}
                                  defaultValue={dishTitle(meal)}
                                  placeholder={t('page.untitledDish')}
                                  autoFocus
                                  onBlur={(e) => handleMealTitleChange(index, 'main', e.target.value.trim())}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.target.blur();
                                    else if (e.key === 'Escape') setEditingMealTitle(null);
                                  }}
                                />
                              ) : (
                                <h3 id="meal-detail-title" tabIndex={-1} className="mt-0.5 flex flex-wrap items-center gap-2 text-base font-semibold text-ink focus:outline-none dark:text-ink-dark">
                                  {mealBusy ? translateMealNameForDisplay(meal.meal) : (
                                    <button type="button" onClick={() => setEditingMealTitle({ mealIndex: index, type: 'main' })} title={t('page.renameDish')} className="group inline-flex items-center gap-1.5 rounded text-start hover:text-forest-ondark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest dark:hover:text-forest">
                                      {dishTitle(meal) || <span className="text-ink-muted dark:text-ink-muted-dark">{t('page.untitledDish')}</span>}
                                      <Edit2 size={13} aria-hidden="true" className="opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60" />
                                    </button>
                                  )}
                                  {meal.aiUntouched && renderPill('forest', Sparkles, t('page.aiDraftBadge'))}
                                </h3>
                              )}
                            </div>
                            {!mealBusy && (
                              <OverflowMenu
                                label={t('page.moreActions')}
                                items={[
                                  { label: t('page.renameDish'), icon: Edit2, onClick: () => setEditingMealTitle({ mealIndex: index, type: 'main' }) },
                                  slot && { label: t('page.editTarget'), icon: Scale, onClick: () => openTargets(meal.meal), disabled: isGenerating },
                                  { label: t('page.duplicateMeal'), icon: Copy, onClick: () => duplicateMeal(index), disabled: isGenerating || !hasFood },
                                  { label: t('page.removeMeal'), icon: Trash2, onClick: () => removePlanMeal(index), disabled: isGenerating || meals.length <= 1, danger: true },
                                ]}
                              />
                            )}
                          </div>

                          {mealBusy ? (
                            <div className="px-4 pb-5 pt-4 sm:px-6" role="status" aria-live="polite">
                              <p className="text-[13px] text-ink-muted dark:text-ink-muted-dark">
                                {draftPhase === 'stream' && meal.arriving
                                  ? t('page.writingMeal').replace('{meal}', translateMealNameForDisplay(meal.meal))
                                  : t('page.draftingDay')}
                              </p>
                              <ul className="mt-4 space-y-2">
                                {[0, 1, 2, 3].map((row) => (
                                  <li key={row} className="flex items-center gap-3 border-t border-line py-2 first:border-t-0 dark:border-line-dark">
                                    <span className="h-3 max-w-[12rem] flex-1 rounded bg-line animate-pulse motion-reduce:animate-none dark:bg-line-dark" />
                                    <span className="h-3 w-12 rounded bg-moss animate-pulse motion-reduce:animate-none dark:bg-moss-dark" />
                                    <span className="h-3 w-10 rounded bg-line animate-pulse motion-reduce:animate-none dark:bg-line-dark" />
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <>
                              {/* How the meal sits against its target, in one line; a fix is offered only when it's off. */}
                              {hasFood && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3 text-[13px] sm:px-6" aria-live="polite">
                                  <bdi dir="ltr" className="font-semibold tabular-nums text-ink dark:text-ink-dark">
                                    {Math.round(mealCalories)}{slot && <span className="font-normal text-ink-muted dark:text-ink-muted-dark"> / {slot.calories}</span>} kcal
                                  </bdi>
                                  <bdi dir="ltr" className="tabular-nums text-ink-muted dark:text-ink-muted-dark">
                                    P {roundMacro(mealMacro(meal, 'protein'))}{slot ? `/${roundMacro(slot.protein)}` : ''} · C {roundMacro(mealMacro(meal, 'carbs'))}{slot ? `/${roundMacro(slot.carbs)}` : ''} · F {roundMacro(mealMacro(meal, 'fat'))}{slot ? `/${roundMacro(slot.fat)}` : ''} g
                                  </bdi>
                                  {mealReconcile?.status === 'on' && (
                                    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-forest-ondark dark:text-forest"><CheckCircle size={13} aria-hidden="true" /> {t('page.onTarget')}</span>
                                  )}
                                  {(mealReconcile?.status === 'over' || mealReconcile?.status === 'under') && (
                                    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-700 dark:text-amber-400">
                                      {(mealReconcile.status === 'over' ? t('page.overBy') : t('page.underBy')).replace('{n}', `${Math.round(Math.abs(mealReconcile.delta))} kcal`)}
                                      <button type="button" onClick={() => handleRebalanceMeal(index)} className="min-h-8 rounded px-1.5 font-semibold underline underline-offset-4 hover:bg-amber-700/10 dark:hover:bg-amber-400/10">
                                        {t('page.fixGrams')}
                                      </button>
                                    </span>
                                  )}
                                </div>
                              )}

                              <div className="px-4 pb-5 pt-4 sm:px-6">
                                {hasFood ? (
                                  <>
                                    <IngredientTable
                                      ingredients={meal.main?.ingredients}
                                      language={language}
                                      gramsDisabled={mealBusy}
                                      onGramsChange={(i, grams) => handleIngredientGrams(index, i, grams)}
                                      onReplace={(i) => handleReplaceIngredient(index, i)}
                                      onEdit={(i, ing) => handleEditIngredient(index, i, ing)}
                                      onDelete={(i) => handleDeleteIngredient(index, i)}
                                      moveTargets={meals.map((m, i) => {
                                        const name = translateMealNameForDisplay(m.meal);
                                        const twins = meals.filter((x) => translateMealNameForDisplay(x.meal) === name).length > 1;
                                        return { index: i, label: twins ? `${name} · ${dishTitle(m) || `#${i + 1}`}` : name };
                                      }).filter((m) => m.index !== index)}
                                      onMove={(i, to) => moveIngredient(index, i, to)}
                                    />
                                    <button type="button" onClick={() => handleOpenAddIngredient(index)} className={`${smallBtn} mt-3`}>
                                      <Plus size={14} aria-hidden="true" /> {t('page.addAFood')}
                                    </button>
                                  </>
                                ) : (
                                  // An empty meal has one job: find a food for it.
                                  <div className="flex flex-col items-center rounded-lg border border-dashed border-line px-4 py-8 text-center dark:border-line-dark">
                                    <p className="text-sm text-ink dark:text-ink-dark">{t('page.noFoodYet').replace('{meal}', translateMealNameForDisplay(meal.meal))}</p>
                                    <button type="button" onClick={() => handleOpenAddIngredient(index)} className={`${primaryBtn} mt-4`}>
                                      <Plus size={16} aria-hidden="true" /> {t('page.addAFood')}
                                    </button>
                                    <button type="button" onClick={() => handleDiscoverAlternative(index, meal)} disabled={isDiscoverLoading[index]} className={`${linkBtn} mt-2`}>
                                      <Sparkles size={13} className="text-forest-ondark dark:text-forest" aria-hidden="true" />
                                      {isDiscoverLoading[index] ? t('page.generatingSuggestion') : t('page.orAskAi')}
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Alternatives: other dishes the client can swap in. Collapsed rows; open one to edit it. */}
                              <div className="border-t border-line px-4 py-4 dark:border-line-dark sm:px-6">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <h4 className="text-[13px] font-semibold text-ink dark:text-ink-dark">
                                      {t('page.tabAlternatives')}{alternativeDishes.length ? <span className="font-normal text-ink-muted dark:text-ink-muted-dark"> · {alternativeDishes.length}</span> : null}
                                    </h4>
                                    <p className="text-[12px] text-ink-muted dark:text-ink-muted-dark">{t('page.alternativesHint')}</p>
                                  </div>
                                  <button type="button" onClick={() => handleDiscoverAlternative(index, meal)} disabled={isDiscoverLoading[index]} className={smallBtn}>
                                    {isDiscoverLoading[index]
                                      ? <><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink/20 border-t-ink motion-reduce:animate-none dark:border-ink-dark/20 dark:border-t-ink-dark" aria-hidden="true" /> {t('page.generatingSuggestion')}</>
                                      : <><Sparkles size={14} className="text-forest-ondark dark:text-forest" aria-hidden="true" /> {aiDiscoverPreviews[index] ? t('page.generateAnother') : t('page.suggestWithAi')}</>}
                                  </button>
                                </div>

                                {renderAiDraftEditor(index, meal, aiDiscoverPreviews[index])}

                                {alternativeDishes.length > 0 && (
                                  <ul className="mt-3 divide-y divide-line overflow-hidden rounded-lg border border-line dark:divide-line-dark dark:border-line-dark">
                                    {alternativeDishes.map(({ dish, dishType, altIndex }, dishIdx) => {
                                      const key = `${dishType}-${altIndex ?? 'x'}`;
                                      const open = openAlt === key;
                                      const name = dish.meal_title || dish.meal_name || translateMealNameForDisplay(meal.meal);
                                      const renaming = editingMealTitle?.mealIndex === index && editingMealTitle?.type === dishType && (editingMealTitle?.altIndex ?? null) === (altIndex ?? null);
                                      return (
                                        <li key={dishIdx}>
                                          <button type="button" onClick={() => setOpenAlt(open ? null : key)} aria-expanded={open} className="flex w-full items-center gap-3 px-3 py-2.5 text-start hover:bg-moss/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest dark:hover:bg-moss-dark/60">
                                            <MealStickerTile stickers={pickMealStickers(dish.ingredients)} size={28} />
                                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink dark:text-ink-dark">{name}</span>
                                            <bdi dir="ltr" className="shrink-0 text-[12px] tabular-nums text-ink-muted dark:text-ink-muted-dark">{Math.round(dish.nutrition?.calories || 0)} kcal</bdi>
                                            <ChevronDown size={15} aria-hidden="true" className={`shrink-0 text-ink-muted transition-transform dark:text-ink-muted-dark ${open ? 'rotate-180' : ''}`} />
                                          </button>
                                          {open && (
                                            <div className="bg-canvas/50 px-3 pb-3 pt-1 dark:bg-canvas-dark/40">
                                              <div className="mb-2 flex flex-wrap items-center gap-1">
                                                {renaming ? (
                                                  <input
                                                    type="text"
                                                    aria-label={t('page.renameDish')}
                                                    className={themeClasses.inputField + ' !text-[13px] !font-semibold !py-1 !px-2 me-auto max-w-sm'}
                                                    defaultValue={name}
                                                    autoFocus
                                                    onBlur={(e) => handleMealTitleChange(index, dishType, e.target.value.trim(), altIndex)}
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') e.target.blur();
                                                      else if (e.key === 'Escape') setEditingMealTitle(null);
                                                    }}
                                                  />
                                                ) : <span className="me-auto" />}
                                                <button type="button" onClick={() => handleMakeAlternativeMain(index, dishType, altIndex)} disabled={!dish.ingredients?.length || isGenerating} className={smallBtn}>
                                                  <RefreshCw size={13} aria-hidden="true" /> {t('page.makeMain')}
                                                </button>
                                                <OverflowMenu
                                                  label={t('page.moreActions')}
                                                  items={[
                                                    { label: t('page.renameDish'), icon: Edit2, onClick: () => setEditingMealTitle({ mealIndex: index, type: dishType, altIndex }) },
                                                    { label: t('page.deleteAlternative'), icon: Trash2, onClick: () => { setOpenAlt(null); handleDeleteAlternativeDish(index, dishType, altIndex); }, disabled: countAlternativeDishes(meal) <= 1, danger: true },
                                                  ]}
                                                />
                                              </div>
                                              <IngredientTable
                                                ingredients={dish.ingredients}
                                                language={language}
                                                onGramsChange={(i, grams) => handleAlternativeGrams(index, i, grams, dishType, altIndex)}
                                                onReplace={(i) => handleReplaceAlternativeIngredient(index, i, dishType, altIndex)}
                                                onEdit={(i, ing) => handleEditAlternativeIngredient(index, i, ing, dishType, altIndex)}
                                                onDelete={(i) => handleDeleteAlternativeIngredient(index, i, dishType, altIndex)}
                                              />
                                              <button type="button" onClick={() => handleOpenAddAlternativeIngredient(index, dishType, altIndex)} className={`${linkBtn} mt-2`}>
                                                <Plus size={14} aria-hidden="true" /> {t('page.addAFood')}
                                              </button>
                                            </div>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            </>
                          )}
                        </section>
                      )}
                    </div>

                    {/* Targets live in a drawer: the builder stays put underneath. */}
                    {targetsOpen && createPortal(
                      <div className="fixed inset-0 z-[100] flex justify-end font-sans text-ink dark:text-ink-dark" dir={direction} role="dialog" aria-modal="true" aria-labelledby="targets-drawer-title"
                        onKeyDown={(e) => { if (e.key === 'Escape') setTargetsOpen(false); }}>
                        <button type="button" aria-label={t('page.close')} onClick={() => setTargetsOpen(false)} className="absolute inset-0 cursor-default bg-black/30 dark:bg-black/50" />
                        <div className="relative flex h-full w-full max-w-[560px] flex-col border-s border-line bg-surface shadow-xl dark:border-line-dark dark:bg-surface-dark">
                          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-4 py-3 dark:border-line-dark">
                            <div>
                              <h2 id="targets-drawer-title" className="text-base font-semibold text-ink dark:text-ink-dark">{t('page.targetsButton')}</h2>
                              <p className="text-[12px] text-ink-muted dark:text-ink-muted-dark">{t('page.targetsDrawerHint').replace('{name}', clientLabel())}</p>
                            </div>
                            <button type="button" onClick={() => setTargetsOpen(false)} aria-label={t('page.close')} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-moss hover:text-ink dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark">
                              <X size={18} aria-hidden="true" />
                            </button>
                          </div>
                          <div id="targets-editor" className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
                            {renderStructure({ compact: true })}
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )}
                  </>
                );
              })()}

              {(() => {
                const planHasFood = generatedMealPlan.meals.some((m) => m.main?.ingredients?.length);
                const dayNames = language === 'hebrew'
                  ? ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']
                  : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                return (
                  <>
                  {publishOpen && (
                    <div className="mx-auto max-w-3xl">
                    {renderStepIntro(t('page.sendTitle').replace('{name}', clientLabel()), t('page.sendHint'), (
                      <button type="button" onClick={closeReview} className={`${quietBtn} -ms-3 mt-2`}>
                        {isRTL ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />} {t('page.backToMeals')}
                      </button>
                    ))}
                    <div id="meal-plan-review" className={`${cardBase} p-5 sm:p-6 space-y-5`}>
                      <div className="flex flex-wrap gap-2 text-[13px]">
                        {generatedMealPlan.meals.map((m, i) => {
                          const slotRow = slotTargetFor(m, i);
                          const cal = m.main?.nutrition?.calories || 0;
                          return (
                            <span key={i} className="inline-flex items-center gap-1.5">
                              <span className="font-medium text-ink dark:text-ink-dark">{translateMealNameForDisplay(m.meal)}</span>
                              <span className="tabular-nums text-ink-muted">{Math.round(cal)}{slotRow ? `/${slotRow.calories}` : ''}</span>
                              {slotRow && renderReconcilePill(cal, slotRow.calories, MEAL_TOLERANCE, ' kcal')}
                            </span>
                          );
                        })}
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block text-[13px] font-medium text-ink dark:text-ink-dark">
                          {t('page.mealPlanName')}
                          <input
                            value={planNameDraft}
                            onChange={(e) => setPlanNameDraft(e.target.value)}
                            placeholder={defaultPlanName()}
                            className={themeClasses.inputField + ' mt-1'}
                          />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="block text-[13px] font-medium text-ink dark:text-ink-dark">
                            {t('page.startDate')}
                            <input type="date" value={sendFrom} onChange={(e) => setSendFrom(e.target.value)} className={themeClasses.inputField + ' mt-1'} />
                          </label>
                          <label className="block text-[13px] font-medium text-ink dark:text-ink-dark">
                            {t('page.endDate')}
                            <input type="date" value={sendUntil} onChange={(e) => setSendUntil(e.target.value)} className={themeClasses.inputField + ' mt-1'} />
                          </label>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="plan-notes-review" className="block text-[13px] font-medium text-ink dark:text-ink-dark">{t('page.planNotes')}</label>
                        <textarea
                          id="plan-notes-review"
                          value={mealPlanNote}
                          onChange={(e) => setMealPlanNote(e.target.value)}
                          placeholder={t('page.notesForMealPlanPlaceholder')}
                          rows={3}
                          className={notesTextareaClass}
                        />
                        <p className="text-[11px] text-ink-muted dark:text-ink-muted-dark">{t('page.notesSeedHint')}</p>
                        {renderAiNote()}
                      </div>
                      <div>
                        <p className="mb-2 text-[13px] font-medium text-ink dark:text-ink-dark">{t('page.activeDays')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {dayNames.map((label, day) => {
                            const on = sendDays.includes(day);
                            return (
                              <button
                                key={label}
                                type="button"
                                aria-pressed={on}
                                onClick={() => setSendDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()))}
                                className={`min-h-11 min-w-11 rounded-md border px-2 text-[13px] font-semibold transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest ${on ? 'border-forest-ondark bg-forest-faint text-forest-ondark dark:border-forest dark:bg-forest-faint-dark dark:text-forest' : 'border-line text-ink hover:bg-moss dark:border-line-dark dark:text-ink-dark dark:hover:bg-moss-dark'}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {!planHasFood && (
                          <p id="send-empty-reason" className="w-full text-[13px] text-ink-muted dark:text-ink-muted-dark">{t('page.nothingToSend')}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSaveMealPlan(undefined, { activate: true })}
                          disabled={isSending || isSavingMealPlan || !planHasFood}
                          aria-describedby={!planHasFood ? 'send-empty-reason' : undefined}
                          className={themeClasses.btnPrimary + ' min-h-[44px] w-full sm:w-auto'}
                        >
                          {isSending ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                          {isSending ? t('page.savingMealPlan') : t('page.sendTo').replace('{name}', clientLabel())}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveMealPlan()}
                          disabled={isSavingMealPlan || isSending || !planHasFood}
                          className={themeClasses.btnSecondary + ' min-h-[44px] w-full sm:w-auto'}
                        >
                          <Save size={16} /> {t('page.saveDraft')}
                        </button>
                        {templateName == null ? (
                          <button
                            type="button"
                            onClick={() => setTemplateName('')}
                            disabled={!planHasFood || isSending}
                            className={themeClasses.btnMutedPill + ' min-h-[44px]'}
                          >
                            <Copy size={16} aria-hidden="true" /> {t('page.saveAsTemplate')}
                          </button>
                        ) : (
                          <form
                            onSubmit={(e) => { e.preventDefault(); saveAsTemplate(); }}
                            className="flex w-full flex-wrap items-end gap-2 rounded-lg border border-line p-3 dark:border-line-dark"
                          >
                            <label className="min-w-[200px] flex-1 text-[13px] font-medium text-ink dark:text-ink-dark">
                              {t('page.templateName')}
                              <input autoFocus value={templateName} onChange={(e) => setTemplateName(e.target.value)} className={themeClasses.inputField + ' mt-1'} />
                            </label>
                            <button type="submit" disabled={!templateName.trim() || isSavingTemplate} className={themeClasses.btnPrimary + ' min-h-[44px]'}>
                              {isSavingTemplate ? t('page.saving') : t('page.saveTemplate')}
                            </button>
                            <button type="button" onClick={() => setTemplateName(null)} className={themeClasses.btnMutedPill + ' min-h-[44px]'}>{t('page.cancel')}</button>
                            <p className="w-full text-[12px] text-ink-muted dark:text-ink-muted-dark">{t('page.templateHint')}</p>
                          </form>
                        )}
                      </div>
                    </div>
                    </div>
                  )}
                  </>
                );
              })()}
            </div>
          );
        })()}

        </div>
      </main>

      {/* Add Ingredient Modals Context Layout Hooks */}
      <AddIngredientModal
        visible={isAddIngredientModalVisible}
        onClose={() => {
          setIsAddIngredientModalVisible(false);
          setSelectedMealIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddIngredient}
        recentFoods={recentFoods}
        mealName={selectedMealIndex !== null && generatedMealPlan?.meals[selectedMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      <IngredientPortionModal
        visible={isEditPortionModalVisible}
        onClose={() => {
          setIsEditPortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
        }}
        onConfirm={handleUpdateIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />

      <AddIngredientModal
        visible={isAddAlternativeIngredientModalVisible}
        onClose={() => {
          setIsAddAlternativeIngredientModalVisible(false);
          setSelectedAlternativeMealIndex(null);
          setSelectedAlternativeType(null);
          setSelectedAlternativeIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddAlternativeIngredient}
        mealName={selectedAlternativeMealIndex !== null && generatedMealPlan?.meals[selectedAlternativeMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      <IngredientPortionModal
        visible={isEditAlternativePortionModalVisible}
        onClose={() => {
          setIsEditAlternativePortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
          setSelectedAlternativeMealIndex(null);
          setSelectedAlternativeType(null);
          setSelectedAlternativeIndex(null);
        }}
        onConfirm={handleUpdateAlternativeIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />

      <AddIngredientModal
        visible={isAddAiDraftIngredientModalVisible}
        onClose={() => {
          setIsAddAiDraftIngredientModalVisible(false);
          setSelectedAiDraftMealIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddAiDraftIngredient}
        mealName={selectedAiDraftMealIndex !== null && generatedMealPlan?.meals[selectedAiDraftMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      <IngredientPortionModal
        visible={isEditAiDraftPortionModalVisible}
        onClose={() => {
          setIsEditAiDraftPortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
          setSelectedAiDraftMealIndex(null);
        }}
        onConfirm={handleUpdateAiDraftIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />

    </div>
  </ClientWorkspaceLayout>
  );
}

export default CreateMealPlanPage;